import { createPortal } from "react-dom";
import OnboardingShopifyInstall from '@/pages/ShopifyInstall/OnboardingShopifyInstall';
import ShopifyConnectionEntry from '@/components/ShopifyConnectionEntry';
import { useAdminContext } from '@/contexts/AdminContext';
import { type Translate, useTranslation } from '@client-app/i18n';
import { Permission, useAuth } from '@/contexts/AuthContext';
import type { UseWorkspaceIntegrationsReturn } from '@/contexts/AuthContext/useWorkspaceIntegrations';
import { useDialog } from '@/contexts/DialogContext';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { useToast } from '@/contexts/ToastContext';
import { authService, type StoreTrialEligibilityState } from '@common/api/authService';
import { Checkbox, Tooltip } from '@common/index';
import IntegrationDisclosure from '@/components/IntegrationDisclosure';
import ConnectionTrustTags from '@/components/Onboarding/ConnectionTrustTags';
import { resolveIntegrationContent } from '@common/lib/integration-content-data';
import { useIntegrationContent } from '@common/hooks/useIntegrationContent';
import { IntegrationContentNotice } from '@common/components/IntegrationContentNotice';
import type {
    IntegrationResource,
    ProjectIntegrationsBootstrapResponse,
} from '@common/api/brandGroupService';
import { projectService } from '@common/api/projectService';
import { featureService, type IntegrationPermission } from '@common/api/featureService';
import {
    mcpConnectorService,
    type McpConnectorResponse,
    type McpConnectorTool,
    type McpConnectorToolUpdateRequest,
    type McpCredentialRequest,
    type NormalizedMcpConnectorInput,
} from '@common/api/mcpConnectorService';
import {
    CalendarBlank,
    CaretDown,
    CaretLeft,
    CaretRight,
    ChartBar,
    ChatCircleText,
    CircleNotch,
    CloudCheck,
    Database,
    FileText,
    HardDrive,
    Kanban,
    Lightning,
    ListChecks,
    MagnifyingGlass,
    Package,
    PencilSimple,
    Percent,
    Plus,
    SealCheck,
    ShoppingBag,
    ShoppingCart,
    SlidersHorizontal,
    SquaresFour,
    Storefront,
    Table,
    Trash,
    Users,
    X,
    XCircle,
    type IconProps,
} from '@phosphor-icons/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ComponentType,
    type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import ContactSalesModal from '../../Pricing/ContactSalesModal';
import { logAdminWrite, startAdminWrite } from '@/lib/behaviorTracking';
import type { ConnectionData } from './integrations/ConnectionCard';
import {
    getConnectRestrictionAction,
    isSoloTeamCollaborationConnectionRestricted,
    resolvePluginAdminConnectPreflight,
    resolveConnectRestrictionCopyContext,
    shouldShowPluginFreeTrialUsedPrompt,
    type SubscriptionRestrictionPrompt,
    type StoreLimitPrompt,
} from './integrations/connectRestriction';
import { getIntegrationProductCopy } from './integrations/productCopy';
import { formatConnectionMetaLine } from './integrations/connectionMeta';
import GoogleAdsAccountSelectionModal, {
    type GoogleAdsAccountSelectionSavedPayload,
} from './integrations/GoogleAdsAccountSelectionModal';
import {
    getGoogleAdsAssignedBusinessGroupIdsByCustomerId,
    getGoogleAdsAssignedBusinessGroupAccessLevelsByCustomerId,
    getGoogleAdsConnectionIdentities,
} from './integrations/googleAdsBusinessGroupCandidates';
import {
    resolveBrandConnectionSync,
    resolveGoogleAdsCustomerBrandAssignments,
} from './integrations/brandConnectionSync';
import { getBrandIcon, getBrandIconFrameClassName, getBrandIconImageClassName } from './integrations/IntegrationCard';
import {
    AVAILABLE_INTEGRATION_STATUS,
    getIntegrationConnectionStatusMeta,
    isIntegrationConnectionActive,
    resolveKlaviyoIntegrationAvailability,
} from './integrations/integrationStatus';
import {
    getDingTalkOAuthRedirectUri,
    getFeishuOAuthRedirectUri,
} from './integrations/integrationOAuthRedirect';
import {
    getIntegrationBrowseDisplayScenario,
    getIntegrationCatalogGroups,
    getIntegrationCatalogTools,
    getIntegrationProviderForTool,
    INTEGRATION_SOURCE_CATEGORY_TO_SCENARIO,
    isConnectableIntegrationTool,
    MCP_TOOL_NAME,
    normalizeIntegrationProvider,
    requiresManualCredentialInput,
} from './integrations/integrationCatalog';
import { resolveDefaultIntegrationDetailTab, type IntegrationDetailTab } from './integrations/integrationDetailTabs';
import { matchesIntegrationSearch } from './integrations/integrationSearch';
import { type ConnectionScopeCounts } from './integrations/connectionScopeFilterVisibility';
import { isPluginPlanCode, PLUGIN_PLAN_CODE, resolvePluginWorkspaceState } from '@/utils/pluginWorkspace';
import { getCurrentProductCode, isPluginProduct, withPluginProductHeaders } from '@common/api/productContext';
import IntegrationApiKeyModal from './integrations/IntegrationApiKeyModal';
import IntegrationCredentialsModal from './integrations/IntegrationCredentialsModal';
import BusinessGroupAssignmentModal from './integrations/BusinessGroupAssignmentModal';
import {
    getApplicationOwnerAssignableBusinessGroups,
    getBusinessGroupCreatorName,
    getManageableBusinessGroups,
} from './brandProjectActions';
import { getBusinessGroupDisplayName } from './brandGroupViewModel';
import {
    createDefaultBusinessGroupAssignmentDraft,
    getDefaultBusinessGroupId,
    resolveConnectionBrandAssignmentAction,
    resolveOnlyDefaultBusinessGroupId,
    type BusinessGroupAvailability,
    type ConnectionBrandAssignmentMode,
} from './integrations/connectionBrandAssignmentFlow';
import IntegrationConnectGuide, { getIntegrationConnectGuideModalWidth } from './integrations/IntegrationConnectGuide';
import IntegrationUrlModal from './integrations/IntegrationUrlModal';
import {
    buildIntegrationCredentialConnection,
    canSubmitIntegrationCredentialForm,
    createIntegrationCredentialValues,
    isIntegrationCredentialTool,
    type IntegrationCredentialTool,
} from './integrations/integrationCredentialForm';
import { resolveIntegrationConnectEntryPoint } from './integrations/bustlyHostedConnectProviders';
import McpConnectorModal, { type McpBatchConnectResult, type McpConnectorModalMode } from './integrations/McpConnectorModal';
import { createWorkspaceRequestGuard, type McpConnectorFlowState, useMcpConnectorFlow } from './integrations/useMcpConnectorFlow';
import {
    isSupabaseMcpServerUrl,
    isValidSupabaseProjectRef,
    normalizeSupabaseProjectRef,
    saveAndRefreshSupabaseResourceContext,
} from './integrations/mcpResourceContext';
import {
    normalizeShopifyStoreDomainInput,
    resolveShopifyConnectionDomain,
} from '../../Onboarding/storeUrlNormalization';
import OnboardingResourceSelectionModal from '../../Onboarding/OnboardingResourceSelectionModal';
import {
    loadOnboardingResourceSelectionData,
    normalizeOnboardingResourceKey,
    resolveOnboardingConnectionId,
    resolveOnboardingWorkspaceIntegrationId,
} from '../../Onboarding/onboardingResourceSelection';
import { getGoogleOAuthAccountDisplayName } from '@/contexts/AuthContext/integrationDisplayHydration';
import {
    getOnboardingConnectionKind,
    resolveOnboardingConnectionInstances,
} from '../../Onboarding/onboardingConnectionInstances';
import ConnectionAccessSettings, { type ConnectionAccessFilter } from './ConnectionAccessSettings';
import type { BusinessGroupMemberAccessTarget } from './businessGroupApplicationAccess';
import { isSoloInviteUpgradeRequired, resolveInvitePlanCode } from './memberInviteAccess';
import Modal from './Modal';
import { useBrandPrototype } from '@/contexts/BrandPrototypeContext';
import { useBrandGroup } from '@/contexts/BrandGroupContext';
import { resolveProjectAssignmentPlanWithRetry } from './integrations/connectionProjectAssignment';
import { isTransientProjectAssignmentError } from './integrations/projectAssignmentError';

type CapabilityMode = 'read' | 'write';
type GoogleSecondaryResourceProvider = 'google-analytics' | 'google-search-console';

const GOOGLE_SECONDARY_RESOURCE_PROVIDER_BY_TOOL: Record<string, GoogleSecondaryResourceProvider> = {
    'Google Analytics': 'google-analytics',
    'Google Search Console': 'google-search-console',
};

const CONNECT_PROVIDER_TO_TOOL: Record<string, string> = {
    mcp: MCP_TOOL_NAME,
    'custom-mcp': MCP_TOOL_NAME,
    'custom-mcp-connector': MCP_TOOL_NAME,
    shopify: 'Shopify',
    bigcommerce: 'BigCommerce',
    woocommerce: 'WooCommerce',
    magento: 'Magento',
    'adobe-commerce': 'Magento',
    prestashop: 'PrestaShop',
    'presta-shop': 'PrestaShop',
    wix: 'Wix',
    'google-ads': 'Google Ads',
    googleads: 'Google Ads',
    'google-analytics': 'Google Analytics',
    'tiktok-ads': 'TikTok Ads',
    'google-search-console': 'Google Search Console',
    gsc: 'Google Search Console',
    semrush: 'Semrush',
    klaviyo: 'Klaviyo',
    'klaviyo-oauth': 'Klaviyo',
    mailchimp: 'Mailchimp',
    'mailchimp-oauth': 'Mailchimp',
    freshdesk: 'Freshdesk',
    activecampaign: 'ActiveCampaign',
    helpscout: 'Help Scout',
    'help-scout': 'Help Scout',
    shipstation: 'ShipStation',
    mixpanel: 'Mixpanel',
    feishu: 'Feishu',
    dingtalk: 'DingTalk',
    figma: 'Figma',
    'figma-oauth': 'Figma',
    similarweb: 'Similarweb',
    'similar-web': 'Similarweb',
    ahrefs: 'Ahrefs',
    omnisend: 'Omnisend',
    okendo: 'Okendo',
    goaffpro: 'GoAffPro',
    'go-aff-pro': 'GoAffPro',
    uppromote: 'UpPromote',
    'up-promote': 'UpPromote',
    loox: 'Loox',
    'loox-reviews': 'Loox',
    trustwill: 'TrustWILL',
    'trustwill-loyalty': 'TrustWILL',
    loloyal: 'TrustWILL',
    'lo-loyal': 'TrustWILL',
    noxinfluencer: 'NoxInfluencer',
    'nox-influencer': 'NoxInfluencer',
    nox: 'NoxInfluencer',
    'insider-one': 'Insider One',
    'insider-one-oauth': 'Insider One',
    insider: 'Insider One',
    useinsider: 'Insider One',
    emarsys: 'Emarsys',
    'emarsys-oauth': 'Emarsys',
    'sap-emarsys': 'Emarsys',
    'sap-engagement-cloud': 'Emarsys',
    judgeme: 'Judge.me',
    'judgeme-oauth': 'Judge.me',
    'judge-me': 'Judge.me',
    'judge.me': 'Judge.me',
    judge: 'Judge.me',
    aliexpress: 'AliExpress',
    slack: 'Slack',
    'meta-ads': 'Meta Ads',
    metaads: 'Meta Ads',
    'facebook-page': 'Facebook Page',
    facebook: 'Facebook Page',
    instagram: 'Instagram',
    'linkedin-ads': 'LinkedIn Ads',
    linkedinads: 'LinkedIn Ads',
    linkedin: 'LinkedIn Ads',
    'pinterest-ads': 'Pinterest Ads',
    pinterestads: 'Pinterest Ads',
    pinterest: 'Pinterest Ads',
    'x-ads': 'X Ads',
    xads: 'X Ads',
    'twitter-ads': 'X Ads',
    twitterads: 'X Ads',
    'twitter-x': 'X Ads',
    twitterx: 'X Ads',
    twitter: 'X Ads',
    x: 'X Ads',
    'youtube-ads': 'YouTube',
    youtubeads: 'YouTube',
    youtube: 'YouTube',
    reddit: 'Reddit',
    'reddit-social': 'Reddit',
    attribuly: 'Attribuly',
    'attribuly-attribution': 'Attribuly',
    'snapchat-ads': 'Snapchat Ads',
    snapchatads: 'Snapchat Ads',
    snapchat: 'Snapchat Ads',
};

const normalizeConnectProvider = (value?: string | null) =>
    (value || '')
        .trim()
        .toLowerCase()
        .replace(/[\s_]+/g, '-');

const getToolFromConnectProvider = (value?: string | null) => {
    const normalized = normalizeConnectProvider(value);
    return CONNECT_PROVIDER_TO_TOOL[normalized] || null;
};

const isGoogleSecondaryResourceTool = (tool: string) =>
    Boolean(GOOGLE_SECONDARY_RESOURCE_PROVIDER_BY_TOOL[tool]);

const getGoogleSecondaryResourceProvider = (tool: string): GoogleSecondaryResourceProvider | null =>
    GOOGLE_SECONDARY_RESOURCE_PROVIDER_BY_TOOL[tool] || null;

type SettingsWorkspaceIntegration = UseWorkspaceIntegrationsReturn['allIntegrations'][number];

const resolveSettingsWorkspaceIntegrationForKeys = (
    integrations: SettingsWorkspaceIntegration[],
    provider: GoogleSecondaryResourceProvider,
    connectionKeys: readonly string[],
): SettingsWorkspaceIntegration | null => {
    const normalizedConnectionKeys = new Set(
        connectionKeys.map((value) => String(value ?? '').trim().toLowerCase()).filter(Boolean),
    );

    return integrations.find((integration) => {
        if (normalizeIntegrationProvider(integration.provider) !== provider) {
            return false;
        }

        const integrationRecord = integration as typeof integration & {
            id?: string | number | null;
            connectionId?: string | number | null;
            integrationId?: string | number | null;
            integration_id?: string | number | null;
            slugId?: string | null;
            nangoConnectionId?: string | null;
            nango_connection_id?: string | null;
            externalAccountId?: string | number | null;
            external_account_id?: string | number | null;
        };

        const identityCandidates = [
            integrationRecord.id,
            integrationRecord.connectionId,
            integrationRecord.integrationId,
            integrationRecord.integration_id,
            integrationRecord.slugId,
            integrationRecord.nangoConnectionId,
            integrationRecord.nango_connection_id,
            integrationRecord.externalAccountId,
            integrationRecord.external_account_id,
            ...(integration.mappings || []).flatMap((mapping) => [
                mapping.id,
                mapping.connection_id,
                mapping.connectionId,
                mapping.nango_connection_id,
                mapping.nangoConnectionId,
                mapping.slugId,
                mapping.slug_id,
                mapping.external_account_id,
                mapping.externalAccountId,
            ]),
        ];

        return identityCandidates.some((candidate) =>
            normalizedConnectionKeys.has(String(candidate ?? '').trim().toLowerCase()),
        );
    }) || null;
};

interface CapabilityItem {
    id: string;
    label: string;
}

interface CapabilitySection {
    id: string;
    title: string;
    icon: ComponentType<IconProps>;
    items: CapabilityItem[];
}

interface CapabilityCatalog {
    read: CapabilitySection[];
    write: CapabilitySection[];
}

const buildCapabilityItem = (id: string, label: string): CapabilityItem => ({
    id,
    label,
});

const countCapabilityItems = (sections: CapabilitySection[]) => sections.reduce((total, section) => total + section.items.length, 0);

const flattenCapabilityItemIds = (sections: CapabilitySection[]) => sections.flatMap((section) => section.items.map((item) => item.id));

const getCapabilityAccessLabel = (t: Translate, readCount: number, writeCount: number) => {
    if (readCount > 0 && writeCount > 0) {
        return t('website.integrationsSettings.readAndWrite');
    }
    if (readCount > 0) {
        return t('website.integrationsSettings.readOnly');
    }
    if (writeCount > 0) {
        return t('website.integrationsSettings.writeOnly');
    }
    return t('website.integrationsSettings.noAccess');
};

const getActiveCapabilityItemIds = (tool: string, connection: ConnectionData | undefined, mode: CapabilityMode, catalog: CapabilityCatalog) => {
    const sections = catalog[mode];
    const summary = connection?.permissions?.[mode]?.trim();

    // Binary Toggle Logic: If we have any summary for this mode (Read/Write),
    // and it's not 'None', then ALL items in this catalog mode are considered active.
    if (!summary || summary === 'None' || sections.length === 0) {
        return new Set<string>();
    }

    return new Set(flattenCapabilityItemIds(sections));
};

const isMcpOAuthFlowInProgress = (state: McpConnectorFlowState) =>
    state.kind === 'creating-draft' || state.kind === 'awaiting-oauth' || state.kind === 'checking-status' || state.kind === 'refreshing';

const TOOL_TO_PERMISSION_PLATFORM: Record<string, string> = {
    shopify: 'SHOPIFY',
    'judge.me': 'JUDGEME',
    google_ads: 'GOOGLE_ADS',
    meta_ads: 'META_ADS',
    facebook_page: 'FACEBOOK_PAGE',
    instagram: 'INSTAGRAM',
    linkedin_ads: 'LINKEDIN_ADS',
    pinterest_ads: 'PINTEREST_ADS',
    x_ads: 'X_ADS',
    tiktok_ads: 'TIKTOK_ADS',
    youtube_ads: 'YOUTUBE',
    snapchat_ads: 'SNAPCHAT',
    google_analytics: 'GOOGLE_ANALYTICS',
    google_search_console: 'GOOGLE_SEARCH_CONSOLE',
    bigcommerce: 'BIGCOMMERCE',
    woocommerce: 'WOOCOMMERCE',
    magento: 'MAGENTO',
    prestashop: 'PRESTASHOP',
    aliexpress: 'ALIEXPRESS',
    wix: 'WIX',
    klaviyo: 'KLAVIYO',
    semrush: 'SEMRUSH',
    similarweb: 'SIMILARWEB',
    ahrefs: 'AHREFS',
    omnisend: 'OMNISEND',
    okendo: 'OKENDO',
    goaffpro: 'GOAFFPRO',
    uppromote: 'UPPROMOTE',
    loox: 'LOOX',
    trustwill: 'TRUSTWILL',
    noxinfluencer: 'NOXINFLUENCER',
    insider_one: 'INSIDER_ONE',
    emarsys: 'EMARSYS',
    freshdesk: 'FRESHDESK',
    activecampaign: 'ACTIVECAMPAIGN',
    helpscout: 'HELPSCOUT',
    help_scout: 'HELPSCOUT',
    shipstation: 'SHIPSTATION',
    mixpanel: 'MIXPANEL',
    mailchimp: 'MAILCHIMP',
    feishu: 'FEISHU',
    dingtalk: 'DINGTALK',
    figma: 'FIGMA',
    'figma-oauth': 'FIGMA',
    figma_oauth: 'FIGMA',
    'figma-api-key': 'FIGMA',
    figma_api_key: 'FIGMA',
};

const WORKSPACE_CONNECTED_APP_ASSIGNMENT_ACTION_TARGET_ID =
    'workspace-connected-app-assignment-action';

const PERMISSION_GROUP_ICONS: Record<string, ComponentType<IconProps>> = {
    account: Database,
    store: Storefront,
    products: ShoppingBag,
    orders: ShoppingCart,
    customers: Users,
    inventory: Package,
    discounts: Percent,
    analytics: ChartBar,
    campaigns: Lightning,
    accounts: Database,
    messages: ChatCircleText,
    chats: Users,
    comments: ChatCircleText,
    contacts: Users,
    creators: Users,
    content: FileText,
    calendar: CalendarBlank,
    docs: FileText,
    files: FileText,
    sheets: Table,
    bitable: Kanban,
    tasks: ListChecks,
    libraries: Kanban,
    variables: Database,
    dev_resources: FileText,
    webhooks: Lightning,
};

const buildCatalogFromIntegrationPermissions = (tool: string, integrationPermissions: IntegrationPermission[] = [], _t?: Translate): CapabilityCatalog | null => {
    const normalizedTool = tool.toLowerCase().replace(/\s+/g, '_');
    const platform = TOOL_TO_PERMISSION_PLATFORM[normalizedTool];
    if (!platform) {
        return null;
    }

    const catalog: CapabilityCatalog = { read: [], write: [] };
    const sectionMaps: Record<CapabilityMode, Map<string, CapabilitySection>> = {
        read: new Map(),
        write: new Map(),
    };

    integrationPermissions
        .filter((permission) => permission.platform === platform && String(permission.status || '').toUpperCase() === 'ACTIVE')
        .sort((left, right) => (left.groupSortOrder ?? 100) - (right.groupSortOrder ?? 100) || (left.sortOrder ?? 100) - (right.sortOrder ?? 100))
        .forEach((permission) => {
            const action = String(permission.action || '').toUpperCase() === 'WRITE' ? 'write' : 'read';
            const sectionKey = permission.groupKey || 'general';
            const sectionMap = sectionMaps[action];
            let section = sectionMap.get(sectionKey);

            if (!section) {
                section = {
                    id: sectionKey,
                    title: permission.groupTitle || permission.groupKey || sectionKey,
                    icon: PERMISSION_GROUP_ICONS[sectionKey] || Database,
                    items: [],
                };
                sectionMap.set(sectionKey, section);
                catalog[action].push(section);
            }

            section.items.push(buildCapabilityItem(permission.permissionKey, permission.label || permission.permissionKey));
        });

    return catalog.read.length > 0 || catalog.write.length > 0 ? catalog : null;
};

const getCapabilityCatalog = (tool: string, _connection?: ConnectionData, integrationPermissions: IntegrationPermission[] = [], t?: Translate): CapabilityCatalog => {
    const backendCatalog = buildCatalogFromIntegrationPermissions(tool, integrationPermissions, t);
    if (backendCatalog) {
        return backendCatalog;
    }

    return { read: [], write: [] };
};

const buildContentCapabilitySections = (tool: string, mode: CapabilityMode, labels: string[], t: Translate, usePluginProductCopy: boolean): CapabilitySection[] => {
    if (labels.length === 0) {
        return [];
    }

    const normalizedTool = tool.toLowerCase().replace(/\s+/g, '_');
    return [
        {
            id: `${normalizedTool}:${mode}`,
            title: getIntegrationProductCopy(
                mode === 'read' ? t('website.integrationsSettings.whatBustlyCanView') : t('website.integrationsSettings.whatBustlyCanDo'),
                usePluginProductCopy,
            ),
            icon: Database,
            items: labels.map((label, index) => buildCapabilityItem(`static:${normalizedTool}:${mode}:${index}`, label)),
        },
    ];
};

const getConnectionPrimaryLabel = (t: Translate, tool: string) => {
    if (tool === 'Shopify') {
        return t('website.integrationsSettings.storeUrl');
    }

    return t('website.integrationsSettings.account');
};

const getConnectionIdentityKey = ({ workspaceIntegrationId, connectionId, id }: Pick<ConnectionData, 'workspaceIntegrationId' | 'connectionId' | 'id'>) =>
    String(workspaceIntegrationId || connectionId || id);

const mapMcpConnectorStatusToConnectionStatus = (status?: string | null): ConnectionData['status'] => {
    const normalized = String(status || '').toUpperCase();
    if (normalized === 'ACTIVE') {
        return 'connected';
    }
    if (normalized === 'FAILED' || normalized === 'ERROR') {
        return 'error';
    }
    return 'disconnected';
};

const findNearestScrollContainer = (element: HTMLElement | null) => {
    if (typeof window === 'undefined') {
        return null;
    }

    let current = element?.parentElement || null;

    while (current) {
        const { overflowY } = window.getComputedStyle(current);

        if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
            return current;
        }

        current = current.parentElement;
    }

    return document.scrollingElement instanceof HTMLElement ? document.scrollingElement : null;
};

type IntegrationsSettingsTimingKey = 'workspaceDataAccess' | 'integrationPermissions';

type IntegrationsSettingsTimingTracker = {
    record: (key: IntegrationsSettingsTimingKey, elapsedMs: number) => void;
    toLogPayload: () => Record<string, unknown>;
};

const nowForIntegrationsSettingsTiming = () => (typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now());

const createIntegrationsSettingsTimingTracker = (workspaceId?: string): IntegrationsSettingsTimingTracker => {
    const startedAt = nowForIntegrationsSettingsTiming();
    const timings: Partial<Record<IntegrationsSettingsTimingKey, number>> = {};

    return {
        record: (key, elapsedMs) => {
            timings[key] = Math.round(elapsedMs);
        },
        toLogPayload: () => ({
            workspaceId,
            totalMs: Math.round(nowForIntegrationsSettingsTiming() - startedAt),
            ...timings,
        }),
    };
};

const trackIntegrationsSettingsTiming = async <T,>(timingTracker: IntegrationsSettingsTimingTracker, key: IntegrationsSettingsTimingKey, promise: PromiseLike<T>): Promise<T> => {
    const startedAt = nowForIntegrationsSettingsTiming();
    try {
        return await promise;
    } finally {
        timingTracker.record(key, nowForIntegrationsSettingsTiming() - startedAt);
    }
};

/**
 * IntegrationsSettings 组件
 *
 * 集成状态数据源变更：
 * - 旧版本：从 profile.shopInfos (Shopify) 和 nangoIntegrations (其他平台) 混合获取
 * - 新版本：统一从 workspace_integrations 表通过 Supabase 拉取
 *
 * workspaceIntegrations 提供的接口：
 * - workspaceIntegrations.shopify - Shopify 集成数据
 * - workspaceIntegrations.bigCommerce - BigCommerce 集成数据
 * - workspaceIntegrations.googleAds - Google Ads 集成数据
 * - workspaceIntegrations.googleAnalytics - Google Analytics 集成数据
 * - workspaceIntegrations.tiktokAds - TikTok Ads 集成数据
 * - workspaceIntegrations.googleSearchConsole - Google Search Console 集成数据
 * - workspaceIntegrations.semrush - Semrush 集成数据
 * - workspaceIntegrations.slack - Slack 集成数据
 * - workspaceIntegrations.woocommerce - WooCommerce 集成数据
 * - workspaceIntegrations.magento - Magento 集成数据
 * - workspaceIntegrations.prestashop - PrestaShop 集成数据
 * - workspaceIntegrations.wix - Wix 集成数据
 *
 * 每个平台的集成数据结构：
 * - status: 'ACTIVE' | 'INACTIVE' | 'ERROR' - 连接状态
 * - connected_at: string - 连接时间
 * - nango_connection_id: string - Nango 连接 ID
 * - platform_shop_name: string | null - 平台店铺名称
 */

const MCP_UI_ENABLED = true;
const isMcpUiEnabled = () => MCP_UI_ENABLED;

const isMcpTool = (tool: string) => tool === MCP_TOOL_NAME;

const isMcpOAuthCancellationReason = (reasonCode: string | null) => {
    const normalizedReasonCode = String(reasonCode || '').trim().toUpperCase();
    return normalizedReasonCode === 'MCP_OAUTH_CANCELLED'
        || normalizedReasonCode === 'MCP_OAUTH_DENIED'
        || normalizedReasonCode === 'ACCESS_DENIED';
};

const mcpToolAccessOptions: Array<NonNullable<McpConnectorToolUpdateRequest['accessLevelRequired']>> = ['READ', 'WRITE'];
const mcpToolRiskOptions: Array<NonNullable<McpConnectorToolUpdateRequest['riskLevel']>> = ['LOW', 'MEDIUM', 'HIGH'];

const getMcpToolDisplayName = (tool: McpConnectorTool) => {
    if (tool.displayName || tool.mcpToolName) {
        return tool.displayName || tool.mcpToolName || tool.name;
    }

    const internalNameMatch = tool.name.match(/^mcp__c\d+__(.+)__[a-f0-9]{12}$/i);
    return internalNameMatch?.[1] || tool.name;
};

const getMcpToolIdentifier = (tool: McpConnectorTool) => tool.toolId ?? tool.id ?? tool.name;

const getMcpToolUpdateKey = (connectorId: number | string, tool: McpConnectorTool) => `${connectorId}:${getMcpToolIdentifier(tool)}`;

const ZERNIO_DIRECT_CONNECT_TOOLS = new Set(['Meta Ads', 'Facebook Page', 'Instagram', 'Pinterest Ads', 'X Ads']);

const ZERNIO_CONNECT_TIMEOUT_MS = 5 * 60 * 1000;

const withZernioConnectTimeout = async <T,>(tool: string, promise: Promise<T>): Promise<T> => {
    if (!ZERNIO_DIRECT_CONNECT_TOOLS.has(tool)) {
        return promise;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    try {
        return await Promise.race([
            promise,
            new Promise<T>((_, reject) => {
                timeoutId = setTimeout(() => {
                    reject(new Error(`${tool} authorization did not finish. Please refresh and try again.`));
                }, ZERNIO_CONNECT_TIMEOUT_MS);
            }),
        ]);
    } finally {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    }
};

type FeishuCustomAppRegion = 'feishu';
type DingTalkCustomAppRegion = 'dingtalk';
type DingTalkConnectionMethod = 'internal_app' | 'mcp_server';
type McpConnectSurface = 'custom' | 'dingtalk';

const FEISHU_REQUIRED_BASE_SCOPE = 'offline_access';
const DINGTALK_REQUIRED_BASE_SCOPE = 'openid corpid';

const ALL_INTEGRATIONS = getIntegrationCatalogGroups(isMcpUiEnabled());
const ALL_INTEGRATION_TOOLS = getIntegrationCatalogTools(isMcpUiEnabled());

const ALL_INTEGRATION_TOOL_ORDER = new Map(ALL_INTEGRATION_TOOLS.map((tool, index) => [tool, index]));

const SCENARIO_FILTERS = [
    { id: 'Connected', label: 'Connected', icon: 'connect' },
    { id: 'Store Ops', label: 'Store Ops', icon: 'store' },
    { id: 'Advertising', label: 'Advertising', icon: 'dashboard' },
    { id: 'Marketing', label: 'Marketing', icon: 'users' },
    { id: 'Analytics', label: 'Analytics', icon: 'pulse' },
    { id: 'SEO', label: 'SEO', icon: 'search' },
    { id: 'Social Media', label: 'Social Media', icon: 'users' },
    { id: 'Customers', label: 'Customers', icon: 'profile' },
    { id: 'Finance', label: 'Finance', icon: 'billing' },
    { id: 'Team Collaboration', label: 'Team Collaboration', icon: 'users' },
    ...(isMcpUiEnabled() ? [{ id: 'MCP', label: 'MCP', icon: 'database' }] : []),
] as const;
type ScenarioFilter = (typeof SCENARIO_FILTERS)[number]['id'];

const DEFAULT_SCENARIO_FILTER: ScenarioFilter = 'Connected';

const REGISTRATION_URLS: Record<string, string> = {
    Shopify: 'https://www.shopify.com/free-trial',
    AliExpress: 'https://seller.aliexpress.com/',
    'Google Ads': 'https://ads.google.com/',
    'Google Search Console': 'https://search.google.com/search-console',
    'LinkedIn Ads': 'https://www.linkedin.com/campaignmanager/',
    'Pinterest Ads': 'https://ads.pinterest.com/',
    'X Ads': 'https://ads.x.com/',
    'YouTube Ads': 'https://ads.google.com/',
    'Snapchat Ads': 'https://forbusiness.snapchat.com/advertising',
    Semrush: 'https://www.semrush.com/signup/',
    Similarweb: 'https://www.similarweb.com/corp/pricing/',
    Ahrefs: 'https://ahrefs.com/pricing',
    Omnisend: 'https://app.omnisend.com/registration/',
    Okendo: 'https://reviews.okendo.io/admin/',
    GoAffPro: 'https://goaffpro.com/',
    UpPromote: 'https://uppromote.com/',
    Loox: 'https://loox.io/',
    TrustWILL: 'https://www.loloyal.com/',
    NoxInfluencer: 'https://www.noxinfluencer.com/skills/dashboard',
    PrestaShop: 'https://prestashop.com/',
    'Insider One': 'https://useinsider.com/',
    Emarsys: 'https://emarsys.com/',
    Wix: 'https://www.wix.com/',
    Feishu: 'https://www.feishu.cn/',
    DingTalk: 'https://open-dev.dingtalk.com/',
    Figma: 'https://www.figma.com/',
};

const API_KEY_FALLBACK_TOOLS = new Set(['Insider One']);
const API_KEY_CONNECT_PROVIDER: Record<string, string> = {
    'Insider One': 'insider-one-api-key',
};

const normalizeConnectionMatchValue = (value?: string | null) =>
    String(value || '')
        .trim()
        .toLowerCase();
const formatIntegerForDisplay = (value: number, locale: string = 'en') =>
    new Intl.NumberFormat(locale === 'zh-CN' ? 'zh-CN' : 'en-US', {
        maximumFractionDigits: 0,
    }).format(value);
const formatConnectionCountLabel = (t: Translate, connectionCount: number) => {
    if (connectionCount <= 0) {
        return null;
    }

    return t('website.integrationsSettings.connectionCount', {
        count: connectionCount,
        plural: connectionCount === 1 ? '' : 's',
    });
};

const normalizeConnectionStatusValue = (status: unknown, fallback: ConnectionData['status'] = 'disconnected'): ConnectionData['status'] => {
    const normalized = String(status ?? '')
        .trim()
        .toLowerCase();

    if (!normalized) {
        return fallback;
    }

    if (normalized === '1' || normalized === 'true' || normalized === 'active' || normalized === 'connected') {
        return 'connected';
    }

    if (normalized === '0' || normalized === 'false' || normalized === 'inactive' || normalized === 'disconnected') {
        return 'disconnected';
    }

    if (normalized === 'error' || normalized === 'failed') {
        return 'error';
    }

    if (normalized === 'disabled' || normalized === 'suspended' || normalized === 'trial_expired') {
        return normalized as ConnectionData['status'];
    }

    return fallback;
};

const DISCONNECT_LOADING_DURATION_MS = 3000;

const isDuplicateCurrentWorkspaceConnection = (message?: string | null) => (
    /already connected to (?:this|the current) workspace/i.test(message || '')
    || /already connected in (?:this|the current) workspace/i.test(message || '')
);

const normalizeConnectionUserMessage = (message?: string | null, fallback = 'Connection failed. Please try again.') => {
    if (!message) {
        return fallback;
    }

    const cleanedMessage = message
        .replace(/Nango/gi, 'Bustly')
        .replace(/Zernio/gi, 'Bustly')
        .replace(/Zerinio/gi, 'Bustly')
        .replace(/zerinio/gi, 'Bustly')
        .replace(/zernio/gi, 'Bustly');

    const judgeMeMismatch = cleanedMessage.match(/Judge\.me authorized shop ([a-z0-9-]+(?:\.[a-z0-9-]+)+) does not match requested shop ([a-z0-9-]+(?:\.[a-z0-9-]+)+)/i);
    if (judgeMeMismatch) {
        const [, authorizedShop, requestedShop] = judgeMeMismatch;
        return `Bustly requested ${requestedShop}, but Judge.me returned authorization for ${authorizedShop}. This usually means the Judge.me popup is still signed in to ${authorizedShop}. Sign out or switch stores in Judge.me, then authorize ${requestedShop}.`;
    }

    if (/Judge\.me shop info API error .*Shop domain or Api Token is wrong/i.test(cleanedMessage)) {
        return 'Judge.me could not verify this store. Make sure Judge.me is installed on that Shopify store, then authorize with the same store account.';
    }

    return cleanedMessage;
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const getReadableDisplayValue = (value: unknown): string | null => {
    if (typeof value !== 'string') {
        return null;
    }

    const trimmedValue = value.trim();

    if (!trimmedValue || UUID_PATTERN.test(trimmedValue)) {
        return null;
    }

    return trimmedValue;
};

const resolveConnectionUserDisplayName = (mapping?: Record<string, any>, profile: Record<string, any> = {}) => {
    const profileUser = profile.user || profile.connected_by || profile.created_by || {};
    const displayName = [
        mapping?.connected_by_name,
        mapping?.user_name,
        mapping?.created_by_name,
        mapping?.connectedByName,
        mapping?.userName,
        mapping?.createdByName,
        profile.connected_by_name,
        profile.user_name,
        profile.created_by_name,
        profile.name,
        profile.full_name,
        profileUser.name,
        profileUser.full_name,
        mapping?.authorized_account_email,
        mapping?.authorizedAccountEmail,
        mapping?.external_account_email,
        mapping?.externalAccountEmail,
        mapping?.connected_by_email,
        mapping?.user_email,
        mapping?.created_by_email,
        mapping?.email,
        profile.authorized_account_email,
        profile.authorizedAccountEmail,
        profile.external_account_email,
        profile.externalAccountEmail,
        profile.connected_by_email,
        profile.user_email,
        profile.created_by_email,
        profile.email,
        profileUser.authorized_account_email,
        profileUser.authorizedAccountEmail,
        profileUser.external_account_email,
        profileUser.externalAccountEmail,
        profileUser.email,
        mapping?.connected_by,
        mapping?.created_by,
        mapping?.user_id,
    ]
        .map(getReadableDisplayValue)
        .find(Boolean);

    if (displayName) {
        return displayName;
    }

    return 'Admin';
};

const getNonEmptyDisplayValue = (value: unknown): string | null => {
    if (value == null) {
        return null;
    }

    const text = String(value).trim();
    return text || null;
};

const FEISHU_INTERNAL_ACCOUNT_PATTERN = /^feishu:[^:]+:.+$/i;
const WORKSPACE_INTEGRATION_SLUG_PATTERN = /^wi_[0-9a-z]{10}$/i;

const getFeishuReadableDisplayValue = (value: unknown): string | null => {
    const text = getNonEmptyDisplayValue(value);
    if (!text || FEISHU_INTERNAL_ACCOUNT_PATTERN.test(text) || WORKSPACE_INTEGRATION_SLUG_PATTERN.test(text)) {
        return null;
    }

    return text;
};

const resolveFeishuConnectionDisplayName = (profile: Record<string, any> = {}, integration: Record<string, any> = {}, mapping?: Record<string, any>) => {
    const integrationProfile = integration.profile_data || {};
    return (
        [
            mapping?.external_account_name,
            mapping?.externalAccountName,
            integration.external_account_name,
            integration.externalAccountName,
            mapping?.authorized_account_name,
            mapping?.authorizedAccountName,
            profile.authorized_account_name,
            profile.authorizedAccountName,
            profile.external_account_name,
            profile.externalAccountName,
            profile.accountName,
            profile.name,
            integrationProfile.authorized_account_name,
            integrationProfile.authorizedAccountName,
            integrationProfile.external_account_name,
            integrationProfile.externalAccountName,
            integrationProfile.accountName,
            integrationProfile.name,
            mapping?.account_name,
            mapping?.name,
            mapping?.label,
            integration.label,
        ]
            .map(getFeishuReadableDisplayValue)
            .find(Boolean) || 'Feishu Workspace'
    );
};

const DINGTALK_INTERNAL_ACCOUNT_PATTERN = /^dingtalk:[^:]+:.+$/i;

const getDingTalkReadableDisplayValue = (value: unknown): string | null => {
    const text = getNonEmptyDisplayValue(value);
    if (!text || DINGTALK_INTERNAL_ACCOUNT_PATTERN.test(text) || WORKSPACE_INTEGRATION_SLUG_PATTERN.test(text)) {
        return null;
    }

    return text;
};

const resolveDingTalkConnectionDisplayName = (profile: Record<string, any> = {}, integration: Record<string, any> = {}, mapping?: Record<string, any>) => {
    const integrationProfile = integration.profile_data || {};
    return (
        [
            mapping?.external_account_name,
            mapping?.externalAccountName,
            integration.external_account_name,
            integration.externalAccountName,
            profile.corpName,
            profile.corp_name,
            profile.accountName,
            profile.nick,
            profile.name,
            integrationProfile.corpName,
            integrationProfile.corp_name,
            integrationProfile.accountName,
            mapping?.account_name,
            mapping?.name,
            mapping?.label,
            integration.label,
        ]
            .map(getDingTalkReadableDisplayValue)
            .find(Boolean) || 'DingTalk Workspace'
    );
};

const resolveGoogleAdsAuthorizedAccountValue = (profile: Record<string, any> = {}, integration: Record<string, any> = {}, mapping?: Record<string, any>) => {
    const integrationProfile = integration.profile_data || {};
    const workspaceExternalAccountName =
        getNonEmptyDisplayValue(mapping?.external_account_name) ||
        getNonEmptyDisplayValue(mapping?.externalAccountName) ||
        getNonEmptyDisplayValue(integration.external_account_name) ||
        getNonEmptyDisplayValue(integration.externalAccountName);
    if (workspaceExternalAccountName) {
        return workspaceExternalAccountName;
    }

    const authorizedAccountValue = [
        mapping?.authorized_account_email,
        mapping?.authorizedAccountEmail,
        mapping?.external_account_email,
        mapping?.externalAccountEmail,
        mapping?.authorized_account_name,
        mapping?.authorizedAccountName,
        mapping?.external_account_name,
        mapping?.externalAccountName,
        profile.authorized_account_email,
        profile.authorizedAccountEmail,
        profile.external_account_email,
        profile.externalAccountEmail,
        profile.authorized_account_name,
        profile.authorizedAccountName,
        profile.external_account_name,
        profile.externalAccountName,
        profile.email,
        profile.accountEmail,
        profile.loginEmail,
        profile.accountName,
        integrationProfile.authorized_account_email,
        integrationProfile.authorizedAccountEmail,
        integrationProfile.external_account_email,
        integrationProfile.externalAccountEmail,
        integrationProfile.authorized_account_name,
        integrationProfile.authorizedAccountName,
        integrationProfile.external_account_name,
        integrationProfile.externalAccountName,
        integrationProfile.email,
        integrationProfile.accountEmail,
        integrationProfile.loginEmail,
        integrationProfile.accountName,
        integration.external_account_name,
        integration.externalAccountName,
        mapping?.authorized_account_id,
        mapping?.authorizedAccountId,
        mapping?.external_account_id,
        mapping?.externalAccountId,
        integration.external_account_id,
        integration.externalAccountId,
        mapping?.connection_id,
    ]
        .map(getNonEmptyDisplayValue)
        .find(Boolean);

    return authorizedAccountValue || 'Google Ads account';
};

interface IntegrationsSettingsProps {
    workspaceIntegrations: UseWorkspaceIntegrationsReturn;
    mode?: 'onboarding' | 'admin';
    /**
     * The full manager remains the Workspace default. Brand pages use two
     * focused surfaces backed by the same catalog and connection handlers:
     * a connected-account list and an authorization catalog.
     */
    viewMode?: 'full' | 'connected' | 'catalog';
    hideHeader?: boolean;
    /**
     * Keeps the Brand authorization modal stable while its application list
     * scrolls. This is intentionally opt-in so the embedded Brand page keeps
     * its natural document flow.
     */
    scrollableCatalog?: boolean;
    onCloseModal?: () => void;
    onBrowseIntegrations?: () => void;
    onConnectionSuccess?: () => void;
    /** Lets the Brand page place the connection filter in its shared toolbar. */
    connectionFilter?: ConnectionAccessFilter;
    onConnectionFilterChange?: (filter: ConnectionAccessFilter) => void;
    hideConnectionScopeFilter?: boolean;
    /**
     * Makes the existing integration manager a Brand-owned workspace: only
     * that Brand's accounts and members are visible, while connection actions
     * keep using the production OAuth and credential flows.
     */
    brandId?: string | null;
    canManageBrand?: boolean;
    canRemoveConnectionsFromBrand?: boolean;
    brandConnectionIds?: string[];
    brandResources?: Array<IntegrationResource & { brandIds?: string[] }>;
    projectBootstrap?: ProjectIntegrationsBootstrapResponse | null;
    /** Opens member access for one exact Business-group application account. */
    onSetMemberAccess?: (target: BusinessGroupMemberAccessTarget) => void;
    /** Reports whether the selected application account is ready to edit. */
    canSetMemberAccess?: (target: BusinessGroupMemberAccessTarget) => boolean;
    /** Explains why a visible Business-group member access action is disabled. */
    getSetMemberAccessDisabledReason?: (target: BusinessGroupMemberAccessTarget) => string | undefined;
    connectProvider?: string | null;
    connectRequestId?: string | null;
    detailProvider?: string | null;
    detailRequestId?: string | null;
}

const IntegrationsSettings: React.FC<IntegrationsSettingsProps> = ({
    workspaceIntegrations: {
        integrations: workspaceIntegrations,
        allIntegrations,
        isLoading,
        refresh: refreshWorkspaceIntegrations,
        connect: connectWorkspacePlatform,
        disconnect: disconnectPlatform,
    },
    mode = 'admin',
    brandId = null,
    canManageBrand = false,
    canRemoveConnectionsFromBrand = false,
    brandConnectionIds,
    brandResources,
    projectBootstrap,
    onSetMemberAccess,
    canSetMemberAccess,
    getSetMemberAccessDisabledReason,
    viewMode = 'full',
    hideHeader = false,
    scrollableCatalog = false,
    onCloseModal,
    onBrowseIntegrations,
    onConnectionSuccess,
    connectionFilter: controlledConnectionFilter,
    onConnectionFilterChange,
    hideConnectionScopeFilter = false,
    connectProvider,
    connectRequestId,
    detailProvider,
    detailRequestId,
}) => {
    const { t, locale } = useTranslation();
    const content = useIntegrationContent();
    const getToolDescription = (_t: Translate, tool: string, usePluginProductCopy: boolean) => {
        const row = resolveIntegrationContent(content.rows, tool, locale);
        const description = row?.description || (content.status === 'loading'
            ? (locale === 'zh-CN' ? '正在加载应用说明…' : 'Loading app details…')
            : (locale === 'zh-CN' ? '此应用的详细说明暂不可用。' : 'Detailed information for this app is not available yet.'));
        return getIntegrationProductCopy(description, usePluginProductCopy);
    };

    const {
        data: brandPrototypeData,
        assignExternalConnectionToBrand,
        assignExternalGoogleAdsCustomerToBrand,
    } = useBrandPrototype();
    const {
        data: projectData,
        isLoading: isLoadingBusinessGroups,
        refresh: refreshProjects,
    } = useBrandGroup();
    const { permissions, userBilling, currentWorkspace, user } = useAuth();
    const connectPlatform = useCallback<UseWorkspaceIntegrationsReturn['connect']>(
        (platform, connectionConfig, options) => {
            const scopedConnectionConfig = brandId
                ? { ...(connectionConfig || {}), brand_id: brandId }
                : connectionConfig;
            if (mode !== 'admin') {
                return connectWorkspacePlatform(
                    platform,
                    scopedConnectionConfig,
                    options,
                );
            }

            return (async () => {
            const tracking = startAdminWrite({
                area: 'integration',
                action: 'connect',
                workspace_id: currentWorkspace?.id,
                entity_id: platform,
            });
            let mutationSucceeded = false;
            try {
                const trackedOptions = {
                    ...options,
                    onMutationSucceeded: () => {
                        mutationSucceeded = true;
                        options?.onMutationSucceeded?.();
                    },
                    onPostWriteFailed: (error: unknown) => {
                        mutationSucceeded = true;
                        tracking.postWriteFailed(error, {
                            failure_stage: 'post_write_follow_up',
                        });
                        options?.onPostWriteFailed?.(error);
                    },
                };
                const result = await connectWorkspacePlatform(
                    platform,
                    scopedConnectionConfig,
                    trackedOptions,
                );
                if (result?.blockConnect || result?.success === false) {
                    const reasonCode = result?.reasonCode
                        || result?.restrictionState
                        || result?.eligibilityState
                        || 'connection_restricted';
                    if (mutationSucceeded) {
                        tracking?.postWriteFailed({ code: reasonCode }, {
                            failure_stage: 'post_write_follow_up',
                        });
                    } else {
                        tracking?.blocked(reasonCode);
                    }
                } else if (result?.action === 'REDIRECT' || result?.uiOpened) {
                    tracking?.handedOff({
                        handoff_type: result?.action === 'REDIRECT'
                            ? 'redirect'
                            : 'external_ui',
                        connection_backend: result?.connectionBackend,
                    });
                } else {
                    tracking?.succeeded({
                        connection_backend: result?.connectionBackend,
                        requires_account_selection: Boolean(result?.requiresAccountSelection),
                    });
                }
                return result;
            } catch (error) {
                if (mutationSucceeded) {
                    tracking?.postWriteFailed(error, {
                        failure_stage: 'post_write_follow_up',
                    });
                } else {
                    tracking?.failed(error);
                }
                throw error;
            }
            })();
        },
        [brandId, connectWorkspacePlatform, currentWorkspace?.id, mode],
    );
    const localizedScenarioLabels: Record<ScenarioFilter, string> = useMemo(
        () => ({
            Connected: t('website.integrationsSettings.connectedFilter'),
            'Store Ops': t('website.integrationsSettings.storeOps'),
            Advertising: t('website.integrationsSettings.advertising'),
            Marketing: t('website.integrationsSettings.marketing'),
            Analytics: t('website.integrationsSettings.analytics'),
            SEO: t('website.integrationsSettings.seo'),
            'Social Media': t('website.integrationsSettings.socialMedia'),
            Customers: t('website.integrationsSettings.customers'),
            Finance: t('website.integrationsSettings.finance'),
            'Team Collaboration': t('website.integrationsSettings.teamCollaboration'),
            MCP: 'MCP',
        }),
        [t],
    );
    const { modal } = useAdminContext();
    const canConnect = mode == 'onboarding' || canManageBrand || permissions.checkPermissions([Permission.INTEGRATION_CONNECT]);
    const canDisconnect = mode == 'onboarding' || canManageBrand || permissions.checkPermissions([Permission.INTEGRATION_DISCONNECT]);
    const workspacePlanCode = resolveInvitePlanCode({
        currentWorkspacePlanCode: currentWorkspace?.subscription?.plan_code,
        billingSummaryPlanCode: userBilling.summary?.planCode,
        userPlanCode: userBilling.userPlan?.planCode,
    });
    const pluginWorkspace = resolvePluginWorkspaceState({
        currentWorkspacePlanCode: currentWorkspace?.subscription?.plan_code,
        currentWorkspacePlanName: currentWorkspace?.subscription?.plan_name,
        currentWorkspacePlanProvider: currentWorkspace?.subscription?.provider,
        billingSummaryPlanCode: userBilling.summary?.planCode,
        billingSummaryPlanName: userBilling.summary?.planName,
        billingSummaryProvider: userBilling.summary?.provider || userBilling.summary?.billingProvider,
        userPlanCode: userBilling.userPlan?.planCode,
        userPlanName: userBilling.userPlan?.planName,
        userPlanProvider: userBilling.userPlan?.provider,
    });
    const shouldUsePluginWorkspaceUi = isPluginProduct(getCurrentProductCode());
    const shouldUsePluginWorkspaceContext = shouldUsePluginWorkspaceUi || pluginWorkspace.isPluginWorkspace;
    const isPluginAdmin = mode === 'admin' && shouldUsePluginWorkspaceContext;
    const shouldSkipBusinessGroupAssignment = shouldUsePluginWorkspaceContext;
    const isPluginTrialWorkspace = isPluginAdmin && userBilling.planText.status === 'trial';
    const connectRestrictionCopyContext = useMemo(
        () => {
            const copyContext = resolveConnectRestrictionCopyContext(
                isPluginAdmin ? (isPluginPlanCode(workspacePlanCode) ? workspacePlanCode : PLUGIN_PLAN_CODE) : workspacePlanCode === PLUGIN_PLAN_CODE ? null : workspacePlanCode,
            );

            return mode === 'admin' && !isPluginAdmin
                ? { ...copyContext, usesAdminPooledConnectionCopy: true }
                : copyContext;
        },
        [isPluginAdmin, mode, workspacePlanCode],
    );
    const brandCatalogConnectionUsage = useMemo(() => {
        if (!brandId || viewMode !== 'catalog') {
            return null;
        }

        const activeConnections = resolveOnboardingConnectionInstances(allIntegrations)
            .filter((connection) => connection.isValidConnection);

        return {
            storeConnections: activeConnections.filter(
                (connection) => getOnboardingConnectionKind(connection.provider) === 'store',
            ).length,
            totalConnections: activeConnections.length,
        };
    }, [allIntegrations, brandId, viewMode]);
    const showBrandCatalogConnectionUsage = Boolean(
        brandCatalogConnectionUsage
        && (
            connectRestrictionCopyContext.storeLimit !== null
            || connectRestrictionCopyContext.totalIntegrationLimit !== null
        ),
    );
    const isSoloWorkspace = mode === 'admin' && isSoloInviteUpgradeRequired(workspacePlanCode);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<ScenarioFilter>(viewMode === 'catalog' ? 'Store Ops' : DEFAULT_SCENARIO_FILTER);
    const catalogLandingWorkspaceIdRef = useRef<string | null>(null);
    const [uncontrolledConnectionFilter, setUncontrolledConnectionFilter] = useState<ConnectionAccessFilter>(brandId ? 'all' : 'owned_by_me');
    const [connectionScopeCounts, setConnectionScopeCounts] = useState<ConnectionScopeCounts | null>(null);
    const connectionFilter = controlledConnectionFilter ?? uncontrolledConnectionFilter;
    const setConnectionFilter = onConnectionFilterChange ?? setUncontrolledConnectionFilter;
    const { onboardingData, updateOnboardingData } = useOnboarding();
    const { showToast } = useToast();
    const { showDialog } = useDialog();

    useEffect(() => {
        setConnectionScopeCounts(null);
    }, [currentWorkspace?.id]);

    const showConnectionFailureToast = (
        errorMessage: string | null | undefined,
        tool: string,
        fallback: string,
        errorPrefix?: string,
    ) => {
        const message = normalizeConnectionUserMessage(errorMessage, fallback);
        const isDuplicate = isDuplicateCurrentWorkspaceConnection(message);
        const localizedMessage = isDuplicate
            ? t('website.integrationsSettings.duplicateConnectionInCurrentWorkspace', { tool })
            : message;

        // A repeated submission changes nothing. Keep it distinct from a
        // connection failure and let the active UI language provide the copy.
        showToast(
            isDuplicate || !errorPrefix ? localizedMessage : `${errorPrefix}: ${localizedMessage}`,
            isDuplicate ? 'warning' : 'error',
        );

        return { message: localizedMessage, isDuplicate };
    };

    // 获取各个应用的连接状态
    // 现在统一从 workspace_integrations 表查询
    const {
        shopify: shopifyIntegration,
        bigCommerce: bigCommerceIntegration,
        googleAds: googleAdsIntegration,
        googleAnalytics: googleAnalyticsIntegration,
        tiktokAds: tiktokAdsIntegration,
        googleSearchConsole: googleSearchConsoleIntegration,
        semrush: semrushIntegration,
        slack: slackIntegration,
        woocommerce: woocommerceIntegration,
        magento: magentoIntegration,
        prestashop: prestashopIntegration,
        aliexpress: aliexpressIntegration,
        klaviyo: klaviyoIntegration,
        freshdesk: freshdeskIntegration,
        activecampaign: activeCampaignIntegration,
        helpScout: helpScoutIntegration,
        mailchimp: mailchimpIntegration,
        shipstation: shipStationIntegration,
        mixpanel: mixpanelIntegration,
        similarweb: similarwebIntegration,
        ahrefs: ahrefsIntegration,
        omnisend: omnisendIntegration,
        insiderOne: insiderOneIntegration,
        emarsys: emarsysIntegration,
        judgeme: judgemeIntegration,
        wix: wixIntegration,
        metaAds: metaAdsIntegration,
        facebookPage: facebookPageIntegration,
        instagram: instagramIntegration,
        linkedinAds: linkedinAdsIntegration,
        pinterestAds: pinterestAdsIntegration,
        xAds: xAdsIntegration,
        youtube: youtubeIntegration,
        snapchat: snapchatIntegration,
        feishu: feishuIntegration,
        dingtalk: dingtalkIntegration,
        figma: figmaIntegration,
        noxinfluencer: noxInfluencerIntegration,
    } = workspaceIntegrations;

    const [shopifyStatusContainer, setShopifyStatusContainer] = useState<HTMLDivElement | null>(null);
    const renderShopifyStatus = (status: React.ReactNode) => shopifyStatusContainer ? createPortal(status, shopifyStatusContainer) : null;
    const [shopifyInstallState, setShopifyInstallState] = useState<"checking" | "error" | "idle">("idle");
    const [shopifyEntryState, setShopifyEntryState] = useState<"checking" | "error" | "idle">("idle");
    const [syncingTools, setSyncingTools] = useState<Set<string>>(new Set());
    const [connectionSyncRevision, setConnectionSyncRevision] = useState(0);
    const [disconnectingTools, setDisconnectingTools] = useState<Set<string>>(new Set());
    const [disconnectingConnectionIds, setDisconnectingConnectionIds] = useState<Set<string>>(new Set());
    const urlConnectionInFlightRef = useRef(false);
    const connectOperationKeysRef = useRef<Set<string>>(new Set());
    const connectAttemptedRef = useRef<string | null>(null);
    const disconnectOperationKeysRef = useRef<Set<string>>(new Set());
    const completedConnectToolsRef = useRef<Set<string>>(new Set());
    const finalizedConnectToolsRef = useRef<Set<string>>(new Set());
    const connectionSuccessToastToolsRef = useRef<Set<string>>(new Set());
    const connectStartConnectionCountsRef = useRef<Map<string, number>>(new Map());
    const connectStartConnectionKeysRef = useRef<Map<string, Set<string>>>(new Map());
    const pendingGoogleAdsCustomerIdsRef = useRef<Map<string, string[]>>(new Map());
    const pendingGoogleAdsCustomerBrandIdsRef = useRef<Map<string, Record<string, string[]>>>(new Map());
    const pendingGoogleAdsCustomerBrandAccessLevelsRef = useRef<Map<string, Record<string, Record<string, 'READ' | 'WRITE'>>>>(new Map());
    const googleAdsBusinessGroupAssignmentOperationKeysRef = useRef<Set<string>>(new Set());
    const pendingGoogleSecondaryResourceKeysRef = useRef<Map<string, string[]>>(new Map());
    const googleSecondaryResourceSelectionOperationKeysRef = useRef<Set<string>>(new Set());
    const pendingConnectionBrandAssignmentModeRef = useRef<Map<string, ConnectionBrandAssignmentMode>>(new Map());
    const pendingConnectionBrandIdsRef = useRef<Map<string, string[]>>(new Map());
    const pendingConnectionBrandAccessLevelsRef = useRef<Map<string, Record<string, 'READ' | 'WRITE'>>>(new Map());
    const [connectingTool, setConnectingTool] = useState<string | null>(null);
    const [storeUrl, setStoreUrl] = useState('');
    const [urlConnectionError, setUrlConnectionError] = useState<string | null>(null);
    const [bigCommerceStoreHash, setBigCommerceStoreHash] = useState('');
    const [bigCommerceAccountUuid, setBigCommerceAccountUuid] = useState('');
    const [googleAdsAccountSelectionConnectionId, setGoogleAdsAccountSelectionConnectionId] = useState<string | null>(null);
    const [pendingBusinessGroupAssignmentResourceKeys, setPendingBusinessGroupAssignmentResourceKeys] = useState<Set<string>>(new Set());
    type GoogleSecondaryResourceSelectionState =
        | { kind: 'idle' }
        | {
              kind: 'choosing';
              tool: string;
              provider: GoogleSecondaryResourceProvider;
              connectionId: string;
              connectionKeys: string[];
              resources: IntegrationResource[];
          };
    const [googleSecondaryResourceSelection, setGoogleSecondaryResourceSelection] = useState<GoogleSecondaryResourceSelectionState>({ kind: 'idle' });
    const googleSecondaryResourceSelectionRef = useRef<GoogleSecondaryResourceSelectionState>({ kind: 'idle' });
    const [isSavingGoogleSecondaryResources, setIsSavingGoogleSecondaryResources] = useState(false);
    useEffect(() => {
        googleSecondaryResourceSelectionRef.current = googleSecondaryResourceSelection;
    }, [googleSecondaryResourceSelection]);
    const connectionBrandOptions = useMemo(() => {
        // A post-authorization assignment is performed by the actor who just
        // connected the application. Application ownership permits attaching
        // the connection to any active public Workspace group; private groups
        // remain restricted to their own Business-group owner.
        const manageableGroups = getManageableBusinessGroups(
            projectData.assignmentBrands,
            [user?.id, user?.email].filter((value): value is string => Boolean(value)),
            permissions.isOwner,
        );
        return getApplicationOwnerAssignableBusinessGroups(
            projectData.assignmentBrands,
            manageableGroups,
            true,
        ).map((project) => ({
                id: project.id,
                name: getBusinessGroupDisplayName(project),
                logoUrl: project.logoUrl,
                accent: project.accent,
                isDefault: project.isDefault,
                creatorName: getBusinessGroupCreatorName(project, projectData.users),
            }));
    }, [permissions.isOwner, projectData.assignmentBrands, projectData.brands, projectData.users, t, user?.email, user?.id]);
    const googleAdsBusinessGroupOptions = useMemo(
        // Google Ads account assignment is an integration-assignment action,
        // not navigation. Keep the same public/private boundary as the other
        // post-authorization assignment paths; the backend still decides the
        // effective ceiling and this list does not grant access by itself.
        () => getApplicationOwnerAssignableBusinessGroups(
          projectData.assignmentBrands,
          getManageableBusinessGroups(
            projectData.assignmentBrands,
            [user?.id, user?.email].filter((value): value is string => Boolean(value)),
            permissions.isOwner,
          ),
          true,
        ).map((businessGroup) => ({
            id: businessGroup.id,
            name: getBusinessGroupDisplayName(businessGroup),
            logoUrl: businessGroup.logoUrl,
            accent: businessGroup.accent,
            isDefault: businessGroup.isDefault,
            creatorName: getBusinessGroupCreatorName(businessGroup, projectData.users),
        })),
        [permissions.isOwner, projectData.assignmentBrands, projectData.brands, projectData.users, t, user?.email, user?.id],
      );
    const googleAdsConnectionIdentities = useMemo(
        () =>
            getGoogleAdsConnectionIdentities(
                allIntegrations,
                googleAdsAccountSelectionConnectionId,
            ),
        [allIntegrations, googleAdsAccountSelectionConnectionId],
    );
    const googleAdsAssignedBrandIdsByCustomerId = useMemo(
        () =>
            getGoogleAdsAssignedBusinessGroupIdsByCustomerId(
                projectData.connections,
                googleAdsConnectionIdentities,
            ),
        [
            googleAdsConnectionIdentities,
            projectData.connections,
        ],
    );
    const googleAdsAssignedBrandAccessLevelsByCustomerId = useMemo(
        () =>
            getGoogleAdsAssignedBusinessGroupAccessLevelsByCustomerId(
                projectData.connections,
                googleAdsConnectionIdentities,
            ),
        [
            googleAdsConnectionIdentities,
            projectData.connections,
        ],
    );
    const onlyDefaultBusinessGroupId = useMemo(
        () => resolveOnlyDefaultBusinessGroupId(connectionBrandOptions),
        [connectionBrandOptions],
    );
    const defaultBusinessGroupId = useMemo(
        () => getDefaultBusinessGroupId(connectionBrandOptions),
        [connectionBrandOptions],
    );
    const onlyDefaultGoogleAdsBusinessGroupId = useMemo(
        () => resolveOnlyDefaultBusinessGroupId(googleAdsBusinessGroupOptions),
        [googleAdsBusinessGroupOptions],
    );
    const businessGroupAvailability: BusinessGroupAvailability = isLoadingBusinessGroups
        ? 'loading'
        : onlyDefaultBusinessGroupId
            ? 'only_default'
        : connectionBrandOptions.length > 0
            ? 'available'
            : 'empty';
    type ConnectionBrandAssignmentPromptState =
        | { kind: 'idle' }
        | {
              kind: 'choosing';
              tool: string;
              connectionKeys: string[];
              selectedBrandIds: string[];
              accessLevels: Record<string, 'READ' | 'WRITE'>;
          };
    const [connectionBrandAssignmentPrompt, setConnectionBrandAssignmentPrompt] = useState<ConnectionBrandAssignmentPromptState>({ kind: 'idle' });
    const connectionBrandAssignmentPromptRef = useRef<ConnectionBrandAssignmentPromptState>({ kind: 'idle' });
    const [isSavingPostAuthorizationBrandAssignment, setIsSavingPostAuthorizationBrandAssignment] = useState(false);
    const postAuthorizationBrandAssignmentSavingRef = useRef(false);
    useEffect(() => {
        connectionBrandAssignmentPromptRef.current = connectionBrandAssignmentPrompt;
    }, [connectionBrandAssignmentPrompt]);
    const [apiKeyTool, setApiKeyTool] = useState<string | null>(null);
    const [apiKeyValue, setApiKeyValue] = useState('');
    const [credentialTool, setCredentialTool] = useState<IntegrationCredentialTool | null>(null);
    const [credentialValues, setCredentialValues] = useState(() => createIntegrationCredentialValues());
    const [feishuCustomAppTool, setFeishuCustomAppTool] = useState<string | null>(null);
    const [feishuCustomAppRegion, setFeishuCustomAppRegion] = useState<FeishuCustomAppRegion>('feishu');
    const [feishuCustomAppId, setFeishuCustomAppId] = useState('');
    const [feishuCustomAppSecret, setFeishuCustomAppSecret] = useState('');
    const [dingtalkCustomAppTool, setDingTalkCustomAppTool] = useState<string | null>(null);
    const [dingtalkCustomAppRegion, setDingTalkCustomAppRegion] = useState<DingTalkCustomAppRegion>('dingtalk');
    const [dingtalkCustomAppId, setDingTalkCustomAppId] = useState('');
    const [dingtalkCustomAppSecret, setDingTalkCustomAppSecret] = useState('');
    const [dingtalkConnectionMethod, setDingTalkConnectionMethod] = useState<DingTalkConnectionMethod>('internal_app');
    const [prestaShopStoreUrl, setPrestaShopStoreUrl] = useState('');
    const [selectedTool, setSelectedTool] = useState<string | null>(null);
    const [selectedDetailTab, setSelectedDetailTab] = useState<IntegrationDetailTab>('permissions');
    const handledDetailRequestRef = useRef<string | null>(null);
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const catalogScrollAreaRef = useRef<HTMLDivElement | null>(null);
    const scrollContainerRef = useRef<HTMLElement | null>(null);
    const listScrollTopRef = useRef(0);
    const pendingScrollTargetRef = useRef<'detail' | 'list' | null>(null);
    const [isAddConnectionUpgradeModalOpen, setIsAddConnectionUpgradeModalOpen] = useState(false);
    const [blockedToolName, setBlockedToolName] = useState<string | null>(null);
    const [storeLimitPrompt, setStoreLimitPrompt] = useState<StoreLimitPrompt | null>(null);
    const [subscriptionRestrictionPrompt, setSubscriptionRestrictionPrompt] = useState<SubscriptionRestrictionPrompt | null>(null);
    const [isStoreLimitContactSalesOpen, setIsStoreLimitContactSalesOpen] = useState(false);
    const [integrationPermissionCatalog, setIntegrationPermissionCatalog] = useState<IntegrationPermission[]>(() => featureService.getCachedIntegrationPermissions() || []);
    const lastStableConnectionsByToolRef = useRef<Record<string, ConnectionData[]>>({});
    const [viewingDetailsConnection, setViewingDetailsConnection] = useState<ConnectionData | null>(null);
    const [connectionAccessRefreshKey, setConnectionAccessRefreshKey] = useState(0);
    const [mcpConnectors, setMcpConnectors] = useState<McpConnectorResponse[]>([]);
    const customMcpConnectors = useMemo(() => mcpConnectors.filter((connector) => connector.platform !== 'DINGTALK'), [mcpConnectors]);
    const [isLoadingMcpConnectors, setIsLoadingMcpConnectors] = useState(false);
    const [mcpConnectorError, setMcpConnectorError] = useState<string | null>(null);
    const [mcpModalMode, setMcpModalMode] = useState<McpConnectorModalMode | null>(null);
    const [mcpConnectSurface, setMcpConnectSurface] = useState<McpConnectSurface | null>(null);
    const mcpConnectSurfaceRef = useRef<McpConnectSurface | null>(null);
    mcpConnectSurfaceRef.current = mcpConnectSurface;
    const [isSavingMcpConnector, setIsSavingMcpConnector] = useState(false);
    const [testingMcpConnectorIds, setTestingMcpConnectorIds] = useState<Set<number>>(() => new Set());
    const [syncingMcpConnectorIds, setSyncingMcpConnectorIds] = useState<Set<number>>(() => new Set());
    const [mcpResourceContextDrafts, setMcpResourceContextDrafts] = useState<Record<number, string>>({});
    const [savingMcpResourceContextConnectorIds, setSavingMcpResourceContextConnectorIds] = useState<Set<string>>(() => new Set());
    const [updatingMcpToolKeys, setUpdatingMcpToolKeys] = useState<Set<string>>(() => new Set());
    const [columnUpdatingMcpConnectorIds, setColumnUpdatingMcpConnectorIds] = useState<Set<number>>(() => new Set());
    const [mcpToolManagerConnectorId, setMcpToolManagerConnectorId] = useState<number | null>(null);
    const [disconnectingMcpConnectorIds, setDisconnectingMcpConnectorIds] = useState<Set<number>>(() => new Set());
    const [recoveringMcpConnectorIds, setRecoveringMcpConnectorIds] = useState<Set<number>>(() => new Set());
    const currentMcpWorkspaceIdRef = useRef<string | null>(currentWorkspace?.id || null);
    currentMcpWorkspaceIdRef.current = currentWorkspace?.id || null;
    const mcpListRequestGuardRef = useRef(createWorkspaceRequestGuard(() => currentMcpWorkspaceIdRef.current));
    const mcpOAuthTrackingRef = useRef<ReturnType<typeof startAdminWrite> | null>(null);
    const mcpOAuthTrackingWorkspaceIdRef = useRef<string | null>(currentWorkspace?.id || null);
    const mcpOAuthMutationSucceededRef = useRef(false);
    const cancelMcpOAuthTracking = useCallback((cancelReason?: string) => {
        const tracking = mcpOAuthTrackingRef.current;
        tracking?.cancelled({
            ...(cancelReason ? { cancel_reason: cancelReason } : {}),
            ...(mcpOAuthMutationSucceededRef.current ? { mutation_succeeded: true } : {}),
        });
        if (mcpOAuthTrackingRef.current === tracking) {
            mcpOAuthTrackingRef.current = null;
        }
        mcpOAuthMutationSucceededRef.current = false;
    }, []);

    const applyPluginAdminConnectPreflight = useCallback(
        (tool: string): boolean => {
            const preflight = resolvePluginAdminConnectPreflight({
                enabled: isPluginAdmin,
                tool,
                integrations: allIntegrations,
                planCode: workspacePlanCode,
            });

            if (preflight.kind === 'allowed') {
                return false;
            }

            if (mode === 'admin') {
                logAdminWrite('blocked', {
                    area: 'integration',
                    action: 'connect',
                    workspace_id: currentWorkspace?.id,
                    entity_id: tool,
                    block_reason: preflight.kind === 'tool_not_included'
                        ? 'tool_not_included'
                        : preflight.reason,
                });
            }

            if (preflight.kind === 'tool_not_included') {
                setBlockedToolName(preflight.tool);
                return true;
            }

            const action = getConnectRestrictionAction(
                {
                    blockConnect: true,
                    restrictionState: preflight.reason,
                    upgradeRequired: false,
                    currentStoreCount: preflight.reason === 'store_limit_reached' ? preflight.current : null,
                    storeLimit: preflight.reason === 'store_limit_reached' ? preflight.limit : null,
                },
                t,
                connectRestrictionCopyContext,
            );

            if (action.storeLimitPrompt) {
                setStoreLimitPrompt(action.storeLimitPrompt);
            }

            return true;
        },
        [allIntegrations, connectRestrictionCopyContext, currentWorkspace?.id, isPluginAdmin, mode, t],
    );

    const applyIntegrationConnectPreflight = useCallback(
        (tool: string): boolean => {
            if (!canConnect) {
                if (mode === 'admin') {
                    logAdminWrite('blocked', {
                        area: 'integration',
                        action: 'connect',
                        workspace_id: currentWorkspace?.id,
                        entity_id: tool,
                        block_reason: 'permission',
                    });
                }
                showToast(t('website.integrationsSettings.connectPermissionDenied'), 'error');
                return true;
            }

            if (applyPluginAdminConnectPreflight(tool)) {
                return true;
            }

            if (isSoloTeamCollaborationConnectionRestricted({ isSoloWorkspace, tool })) {
                if (mode === 'admin') {
                    logAdminWrite('blocked', {
                        area: 'integration',
                        action: 'connect',
                        workspace_id: currentWorkspace?.id,
                        entity_id: tool,
                        block_reason: 'solo_team_collaboration',
                    });
                }
                setBlockedToolName(tool);
                return true;
            }

            return false;
        },
        [applyPluginAdminConnectPreflight, canConnect, currentWorkspace?.id, isSoloWorkspace, mode, showToast, t],
    );

    const openGoogleAdsAccountSelection = useCallback((connectionId?: string | null) => {
        if (!connectionId) {
            return;
        }
        setGoogleAdsAccountSelectionConnectionId(connectionId);
    }, []);

    const closeGoogleAdsAccountSelection = useCallback(() => {
        setGoogleAdsAccountSelectionConnectionId(null);
    }, []);

    const refreshWorkspaceIntegrationsAndAccess = useCallback(
        async (showLoading = false, options?: Parameters<typeof refreshWorkspaceIntegrations>[1]) => {
            await refreshWorkspaceIntegrations(showLoading, options);
            if (options?.forceRefresh) {
                setConnectionAccessRefreshKey((value) => value + 1);
            }
        },
        [refreshWorkspaceIntegrations],
    );

    const handleGoogleAdsAccountSelectionSaved = useCallback(async ({
        selectedCustomerIds,
        selectedCustomerBrandIds,
        selectedCustomerBrandAccessLevels,
    }: GoogleAdsAccountSelectionSavedPayload) => {
        const tool = 'Google Ads';
        pendingGoogleAdsCustomerIdsRef.current.set(tool, selectedCustomerIds);
        pendingGoogleAdsCustomerBrandIdsRef.current.set(tool, selectedCustomerBrandIds);
        pendingGoogleAdsCustomerBrandAccessLevelsRef.current.set(tool, selectedCustomerBrandAccessLevels);
        completedConnectToolsRef.current.add(tool);
        setSyncingTools((previous) => new Set(previous).add(tool));

        await refreshWorkspaceIntegrationsAndAccess(false, { forceRefresh: true });
    }, [refreshWorkspaceIntegrationsAndAccess]);

    const finishGoogleSecondaryResourceSelection = useCallback(async (resources: IntegrationResource[]) => {
        const prompt = googleSecondaryResourceSelectionRef.current;
        if (prompt.kind !== 'choosing') {
            return;
        }

        setIsSavingGoogleSecondaryResources(true);
        try {
            const selectedResourceKeys = resources.map((resource) =>
                normalizeOnboardingResourceKey(resource.resourceType, resource.resourceKey),
            );
            await authService.saveNangoGoogleResourceSelection({
                provider: prompt.provider,
                connectionId: prompt.connectionId,
                selectedResourceKeys,
            });
            pendingGoogleSecondaryResourceKeysRef.current.set(prompt.tool, selectedResourceKeys);
            setGoogleSecondaryResourceSelection({ kind: 'idle' });
            completedConnectToolsRef.current.add(prompt.tool);
            setSyncingTools((previous) => new Set(previous).add(prompt.tool));
            setConnectionSyncRevision((revision) => revision + 1);
        } catch (error: any) {
            showConnectionFailureToast(
                error?.message,
                prompt.tool,
                t('website.integrationsSettings.failedToConnectToTool', { tool: prompt.tool }),
                t('website.integrationsSettings.connectionFailed'),
            );
        } finally {
            setIsSavingGoogleSecondaryResources(false);
        }
    }, [t]);

    const dismissGoogleSecondaryResourceSelection = useCallback(async () => {
        const prompt = googleSecondaryResourceSelectionRef.current;
        if (prompt.kind !== 'choosing') {
            return;
        }

        setIsSavingGoogleSecondaryResources(true);
        try {
            await authService.saveNangoGoogleResourceSelection({
                provider: prompt.provider,
                connectionId: prompt.connectionId,
                selectedResourceKeys: [],
            });
            pendingGoogleSecondaryResourceKeysRef.current.set(prompt.tool, []);
            setGoogleSecondaryResourceSelection({ kind: 'idle' });
            completedConnectToolsRef.current.add(prompt.tool);
            setSyncingTools((previous) => new Set(previous).add(prompt.tool));
            setConnectionSyncRevision((revision) => revision + 1);
        } catch (error: any) {
            showConnectionFailureToast(
                error?.message,
                prompt.tool,
                t('website.integrationsSettings.failedToConnectToTool', { tool: prompt.tool }),
                t('website.integrationsSettings.connectionFailed'),
            );
        } finally {
            setIsSavingGoogleSecondaryResources(false);
        }
    }, [t]);

    const loadMcpConnectors = useCallback(
        async (expectedWorkspaceId?: string, externalSignal?: AbortSignal): Promise<boolean> => {
            const workspaceId = expectedWorkspaceId || currentMcpWorkspaceIdRef.current;
            // DingTalk MCP mode reuses the MCP runtime even while the standalone
            // Custom MCP catalog entry remains feature-gated.
            if (viewMode === 'connected') {
                mcpListRequestGuardRef.current.dispose();
                setMcpConnectors([]);
                setIsLoadingMcpConnectors(false);
                return false;
            }
            if (!workspaceId || mode !== 'admin') {
                mcpListRequestGuardRef.current.dispose();
                setMcpConnectors([]);
                setIsLoadingMcpConnectors(false);
                return false;
            }

            const request = mcpListRequestGuardRef.current.begin(workspaceId);
            const abortFromExternalSignal = () => request.abort();
            if (externalSignal?.aborted) {
                request.abort();
            } else {
                externalSignal?.addEventListener('abort', abortFromExternalSignal, {
                    once: true,
                });
            }

            setIsLoadingMcpConnectors(true);
            try {
                const connectors = await mcpConnectorService.list(workspaceId, request.signal);
                if (!request.isCurrent()) {
                    const staleRequestError = new Error('Stale MCP connector list response');
                    staleRequestError.name = 'AbortError';
                    throw staleRequestError;
                }
                setMcpConnectors(connectors || []);
                return true;
            } catch (error) {
                if (!request.isCurrent() || request.signal.aborted) {
                    return false;
                }
                console.warn('[IntegrationsSettings] Failed to load MCP connectors', error);
                setMcpConnectorError(t('website.integrationsSettings.mcpLoadFailed'));
                throw error;
            } finally {
                externalSignal?.removeEventListener('abort', abortFromExternalSignal);
                if (request.isLatest()) {
                    setIsLoadingMcpConnectors(false);
                }
            }
        },
        [mode, t, viewMode],
    );

    const refreshMcpConnectorsAfterDisconnect = useCallback(async (): Promise<void> => {
        await loadMcpConnectors();
    }, [loadMcpConnectors]);

    const mcpFlow = useMcpConnectorFlow({
        workspaceId: currentWorkspace?.id || null,
        onMutationSucceeded: () => {
            mcpOAuthMutationSucceededRef.current = true;
        },
        refreshCurrentWorkspaceConnectors: async ({ originWorkspaceId, signal }) => {
            const refreshedCurrentWorkspace = await loadMcpConnectors(originWorkspaceId, signal);
            if (!refreshedCurrentWorkspace || signal.aborted || currentMcpWorkspaceIdRef.current !== originWorkspaceId) {
                const staleWorkspaceError = new Error('Workspace changed during MCP refresh');
                staleWorkspaceError.name = 'AbortError';
                throw staleWorkspaceError;
            }
            try {
                await refreshWorkspaceIntegrationsAndAccess(false, {
                    forceRefresh: true,
                    throwOnError: true,
                });
            } catch (error) {
                if (signal.aborted || currentMcpWorkspaceIdRef.current !== originWorkspaceId) {
                    const staleWorkspaceError = new Error('Workspace changed during integration refresh');
                    staleWorkspaceError.name = 'AbortError';
                    throw staleWorkspaceError;
                }
                mcpOAuthTrackingRef.current?.postWriteFailed(error, {
                    failure_stage: 'integration_refresh',
                });
                console.warn('[IntegrationsSettings] MCP connector saved, but integration refresh failed', error);
            }
            if (signal.aborted || currentMcpWorkspaceIdRef.current !== originWorkspaceId) {
                const staleWorkspaceError = new Error('Workspace changed during integration refresh');
                staleWorkspaceError.name = 'AbortError';
                throw staleWorkspaceError;
            }
        },
        onFlowError: (reasonCode, details = {}) => {
            const tracking = mcpOAuthTrackingRef.current;
            if (isMcpOAuthCancellationReason(reasonCode)) {
                tracking?.cancelled({
                    cancel_reason: reasonCode || 'oauth_cancelled',
                    ...(details.mutationSucceeded ? { mutation_succeeded: true } : {}),
                });
            } else if (reasonCode === 'MCP_OAUTH_REFRESH_FAILED' || details.mutationSucceeded) {
                tracking?.postWriteFailed({ code: reasonCode }, {
                    failure_stage: details.failureStage || 'refresh',
                });
            } else {
                tracking?.failed({ code: reasonCode || 'MCP_OAUTH_FAILED' });
            }
            if (mcpOAuthTrackingRef.current === tracking) {
                mcpOAuthTrackingRef.current = null;
            }
            mcpOAuthMutationSucceededRef.current = false;
            setMcpConnectorError(reasonCode === 'MCP_OAUTH_REFRESH_FAILED' ? t('website.integrationsSettings.mcpRefreshFailed') : t('website.integrationsSettings.mcpOAuthFailed'));
        },
        onFlowCompleted: () => {
            const tracking = mcpOAuthTrackingRef.current;
            tracking?.succeeded({ connection_backend: 'MCP_SERVER' });
            if (mcpOAuthTrackingRef.current === tracking) {
                mcpOAuthTrackingRef.current = null;
            }
            mcpOAuthMutationSucceededRef.current = false;
        },
    });
    const isMcpOAuthLoading = isMcpOAuthFlowInProgress(mcpFlow.state);

    useEffect(() => {
        const workspaceId = currentWorkspace?.id || null;
        if (mcpOAuthTrackingWorkspaceIdRef.current === workspaceId) {
            return;
        }
        cancelMcpOAuthTracking('workspace_changed');
        mcpOAuthTrackingWorkspaceIdRef.current = workspaceId;
    }, [cancelMcpOAuthTracking, currentWorkspace?.id]);

    useEffect(() => {
        void loadMcpConnectors().catch((error) => {
            if (error instanceof Error && error.name !== 'AbortError') {
                console.warn('[IntegrationsSettings] MCP connector list refresh failed', error);
            }
        });
    }, [currentWorkspace?.id, loadMcpConnectors, viewMode]);

    useEffect(
        () => () => {
            mcpListRequestGuardRef.current.dispose();
            cancelMcpOAuthTracking('navigation');
        },
        [cancelMcpOAuthTracking],
    );

    const openMcpConnectorModal = useCallback(
        (nextMode: McpConnectorModalMode = 'url') => {
            if (!canConnect) {
                showToast(t('website.integrationsSettings.connectPermissionDenied'), 'error');
                return;
            }
            if (applyPluginAdminConnectPreflight(MCP_TOOL_NAME)) {
                return;
            }

            setMcpConnectorError(null);
            setMcpConnectSurface('custom');
            setMcpModalMode(nextMode);
        },
        [applyPluginAdminConnectPreflight, canConnect, showToast, t],
    );

    const closeMcpConnectorModal = useCallback(() => {
        if (!isSavingMcpConnector && !isMcpOAuthFlowInProgress(mcpFlow.state)) {
            cancelMcpOAuthTracking();
            setMcpModalMode(null);
            setMcpConnectSurface(null);
            setMcpConnectorError(null);
            mcpFlow.close();
        }
    }, [cancelMcpOAuthTracking, isSavingMcpConnector, mcpFlow]);

    const handleMcpOAuthStart = useCallback(
        async (input: NormalizedMcpConnectorInput) => {
            if (!currentWorkspace?.id || isSavingMcpConnector || isMcpOAuthLoading) {
                return;
            }
            const owningTool = input.createRequest.platform === 'DINGTALK' ? 'DingTalk' : MCP_TOOL_NAME;
            if (applyIntegrationConnectPreflight(owningTool)) {
                setMcpModalMode(null);
                return;
            }

            setMcpConnectorError(null);
            setMcpConnectSurface(input.createRequest.platform === 'DINGTALK' ? 'dingtalk' : 'custom');
            cancelMcpOAuthTracking('replaced_by_new_flow');
            const tracking = mode === 'admin'
                ? startAdminWrite({ area: 'integration', action: 'connect', workspace_id: currentWorkspace.id, entity_id: owningTool })
                : null;
            mcpOAuthTrackingRef.current = tracking;
            const connector = await mcpFlow.startOAuth(input);
            if (!connector && mcpOAuthTrackingRef.current === tracking) {
                tracking?.failed({ code: 'MCP_OAUTH_START_FAILED' });
                mcpOAuthTrackingRef.current = null;
            }
        },
        [applyIntegrationConnectPreflight, cancelMcpOAuthTracking, currentWorkspace?.id, isMcpOAuthLoading, isSavingMcpConnector, mcpFlow, mode],
    );

    const handleMcpDirectConnect = useCallback(
        async (input: NormalizedMcpConnectorInput) => {
            const workspaceId = currentWorkspace?.id;
            if (!workspaceId || isSavingMcpConnector || isMcpOAuthLoading) {
                return;
            }
            const owningTool = input.createRequest.platform === 'DINGTALK' ? 'DingTalk' : MCP_TOOL_NAME;
            if (applyIntegrationConnectPreflight(owningTool)) {
                setMcpModalMode(null);
                return;
            }

            setIsSavingMcpConnector(true);
            setMcpConnectorError(null);
            const tracking = mode === 'admin'
                ? startAdminWrite({ area: 'integration', action: 'connect', workspace_id: workspaceId, entity_id: owningTool })
                : null;
            let mutationSucceeded = false;
            try {
                await mcpConnectorService.connect(workspaceId, input, {
                    testConnection: true,
                    syncTools: true,
                    onConnectorCreated: () => {
                        mutationSucceeded = true;
                    },
                });
                await loadMcpConnectors();
                try {
                    await refreshWorkspaceIntegrationsAndAccess(false, {
                        forceRefresh: true,
                        throwOnError: true,
                    });
                } catch (refreshError) {
                    tracking?.postWriteFailed(refreshError, { failure_stage: 'refresh' });
                }
                tracking?.succeeded({ connection_backend: 'MCP_SERVER' });
                setMcpModalMode(null);
                setMcpConnectSurface(null);
                if (input.createRequest.platform === 'DINGTALK') {
                    setDingTalkCustomAppTool(null);
                    setDingTalkConnectionMethod('internal_app');
                }
                mcpFlow.close();
                showToast(
                    t(
                        input.createRequest.platform === 'DINGTALK'
                            ? 'website.integrationsSettings.dingtalkMcpConnectedSuccess'
                            : 'website.integrationsSettings.mcpConnectedSuccess',
                    ),
                    'success',
                );
            } catch (error: any) {
                if (mutationSucceeded) {
                    tracking?.postWriteFailed(error, { failure_stage: 'post_write_follow_up' });
                } else {
                    tracking?.failed(error);
                }
                setMcpConnectorError(normalizeConnectionUserMessage(error.message, t('website.integrationsSettings.mcpSaveFailed')));
            } finally {
                setIsSavingMcpConnector(false);
            }
        },
        [
            applyIntegrationConnectPreflight,
            currentWorkspace?.id,
            isMcpOAuthLoading,
            isSavingMcpConnector,
            loadMcpConnectors,
            mcpFlow,
            mode,
            refreshWorkspaceIntegrationsAndAccess,
            showToast,
            t,
        ],
    );

    const handleDingTalkMcpBatchConnect = useCallback(
        async (inputs: NormalizedMcpConnectorInput[]): Promise<McpBatchConnectResult | void> => {
            const workspaceId = currentWorkspace?.id;
            if (!workspaceId || isSavingMcpConnector || isMcpOAuthLoading) {
                return;
            }
            if (inputs.length === 0 || inputs.some((input) => input.createRequest.platform !== 'DINGTALK')) {
                throw new Error(t('website.integrationsSettings.dingtalkMcpImportInvalid'));
            }
            if (applyIntegrationConnectPreflight('DingTalk')) {
                setMcpModalMode(null);
                return;
            }

            setIsSavingMcpConnector(true);
            setMcpConnectorError(null);
            let connectedCount = 0;
            let createdCount = 0;
            let refreshError: unknown = null;
            let refreshFailed = false;
            const failedInputs: NormalizedMcpConnectorInput[] = [];
            const tracking = mode === 'admin'
                ? startAdminWrite({ area: 'integration', action: 'connect', workspace_id: workspaceId, entity_id: 'DingTalk', entity_count: inputs.length })
                : null;
            try {
                for (const input of inputs) {
                    try {
                        await mcpConnectorService.connect(workspaceId, input, {
                            testConnection: true,
                            syncTools: true,
                            onConnectorCreated: () => {
                                createdCount += 1;
                            },
                        });
                        connectedCount += 1;
                    } catch {
                        failedInputs.push(input);
                        console.warn('[IntegrationsSettings] DingTalk MCP batch item failed', {
                            displayName: input.createRequest.displayName,
                        });
                    }
                }

                if (connectedCount > 0) {
                    try {
                        await loadMcpConnectors();
                        await refreshWorkspaceIntegrations(false, {
                            forceRefresh: true,
                            throwOnError: true,
                        });
                    } catch (error) {
                        refreshFailed = true;
                        refreshError = error;
                        console.warn('[IntegrationsSettings] DingTalk MCP batch connected, but integration refresh failed', error);
                    }
                }

                const failedCount = failedInputs.length;
                if (failedCount > 0) {
                    if (createdCount > 0) {
                        tracking?.postWriteFailed(
                            { code: 'MCP_BATCH_PARTIAL_FAILURE' },
                            {
                                failure_stage: 'partial_batch',
                                connected_count: connectedCount,
                                created_count: createdCount,
                                failed_count: failedCount,
                                refresh_failed: refreshFailed,
                            },
                        );
                    } else {
                        tracking?.failed(
                            { code: 'MCP_BATCH_PARTIAL_FAILURE' },
                            { connected_count: connectedCount, failed_count: failedCount },
                        );
                    }
                    const message = t('website.integrationsSettings.dingtalkMcpBatchPartial', {
                        connectedCount,
                        failedCount,
                    });
                    setMcpConnectorError(message);
                    showToast(message, 'error');
                    return { failedInputs, errorMessage: message };
                }

                setMcpModalMode(null);
                setMcpConnectSurface(null);
                setDingTalkCustomAppTool(null);
                setDingTalkConnectionMethod('internal_app');
                mcpFlow.close();
                showToast(
                    t('website.integrationsSettings.dingtalkMcpBatchSuccess', {
                        count: connectedCount,
                    }),
                    'success',
                );
                if (refreshFailed) {
                    tracking?.postWriteFailed(refreshError || { code: 'MCP_REFRESH_FAILED' }, {
                        failure_stage: 'refresh',
                        connected_count: connectedCount,
                    });
                } else {
                    tracking?.succeeded({ connected_count: connectedCount });
                }
                return { failedInputs: [] };
            } catch (error) {
                tracking?.failed(error);
                throw error;
            } finally {
                setIsSavingMcpConnector(false);
            }
        },
        [applyIntegrationConnectPreflight, currentWorkspace?.id, isMcpOAuthLoading, isSavingMcpConnector, loadMcpConnectors, mcpFlow, mode, refreshWorkspaceIntegrations, showToast, t],
    );

    const handleMcpManualSubmit = useCallback(
        async (credential: McpCredentialRequest | null) => {
            const workspaceId = currentWorkspace?.id;
            const flowState = mcpFlow.state;
            if (
                !workspaceId
                || flowState.kind !== 'manual-setup'
                || flowState.originWorkspaceId !== workspaceId
                || isSavingMcpConnector
            ) {
                return;
            }

            setIsSavingMcpConnector(true);
            setMcpConnectorError(null);
            const tracking = mcpOAuthTrackingRef.current
                || (mode === 'admin'
                    ? startAdminWrite({ area: 'integration', action: 'connect', workspace_id: workspaceId, entity_id: MCP_TOOL_NAME })
                    : null);
            let mutationSucceeded = true;
            try {
                if (credential) {
                    await mcpConnectorService.setCredential(workspaceId, flowState.connectorId, credential);
                }
                await mcpConnectorService.testConnection(workspaceId, flowState.connectorId);
                await mcpConnectorService.syncTools(workspaceId, flowState.connectorId);
                await loadMcpConnectors();
                try {
                    await refreshWorkspaceIntegrationsAndAccess(false, {
                        forceRefresh: true,
                        throwOnError: true,
                    });
                } catch (refreshError) {
                    tracking?.postWriteFailed(refreshError, { failure_stage: 'refresh' });
                }
                tracking?.succeeded({ connection_backend: 'MCP_SERVER' });
                if (mcpOAuthTrackingRef.current === tracking) {
                    mcpOAuthTrackingRef.current = null;
                }
                setMcpModalMode(null);
                const completedSurface = mcpConnectSurfaceRef.current;
                setMcpConnectSurface(null);
                if (completedSurface === 'dingtalk') {
                    setDingTalkCustomAppTool(null);
                    setDingTalkConnectionMethod('internal_app');
                }
                mcpFlow.close();
                showToast(
                    t(completedSurface === 'dingtalk' ? 'website.integrationsSettings.dingtalkMcpConnectedSuccess' : 'website.integrationsSettings.mcpConnectedSuccess'),
                    'success',
                );
            } catch (error: any) {
                if (mutationSucceeded) {
                    tracking?.postWriteFailed(error, { failure_stage: 'post_write_follow_up' });
                } else {
                    tracking?.failed(error);
                }
                if (mcpOAuthTrackingRef.current === tracking) {
                    mcpOAuthTrackingRef.current = null;
                }
                setMcpConnectorError(normalizeConnectionUserMessage(error.message, t('website.integrationsSettings.mcpSaveFailed')));
            } finally {
                setIsSavingMcpConnector(false);
            }
        },
        [currentWorkspace?.id, isSavingMcpConnector, loadMcpConnectors, mcpFlow, mode, refreshWorkspaceIntegrationsAndAccess, showToast, t],
    );

    useEffect(() => {
        if (mcpFlow.state.kind === 'manual-setup') {
            setMcpModalMode('manual');
        }
        if (mcpFlow.state.kind === 'completed') {
            setMcpConnectorError(null);
            setMcpModalMode(null);
            const completedSurface = mcpConnectSurfaceRef.current;
            setMcpConnectSurface(null);
            if (completedSurface === 'dingtalk') {
                setDingTalkCustomAppTool(null);
                setDingTalkConnectionMethod('internal_app');
            }
            showToast(
                t(completedSurface === 'dingtalk' ? 'website.integrationsSettings.dingtalkMcpConnectedSuccess' : 'website.integrationsSettings.mcpConnectedSuccess'),
                'success',
            );
        }
    }, [mcpFlow.state, showToast, t]);

    const handleMcpConnectorTest = useCallback(
        async (connector: McpConnectorResponse) => {
            const workspaceId = currentWorkspace?.id;
            if (!workspaceId || !canConnect || connector.canManage === false || testingMcpConnectorIds.has(connector.connectorId)) {
                return;
            }

            setTestingMcpConnectorIds((previous) => new Set(previous).add(connector.connectorId));
            setMcpConnectorError(null);
            try {
                await mcpConnectorService.testConnection(workspaceId, connector.connectorId);
                await loadMcpConnectors();
                showToast(t('website.integrationsSettings.mcpTestSuccess'), 'success');
            } catch (error: any) {
                const message = normalizeConnectionUserMessage(error.message, t('website.integrationsSettings.mcpTestFailed'));
                setMcpConnectorError(message);
                showToast(message, 'error');
            } finally {
                setTestingMcpConnectorIds((previous) => {
                    const next = new Set(previous);
                    next.delete(connector.connectorId);
                    return next;
                });
            }
        },
        [canConnect, currentWorkspace?.id, loadMcpConnectors, showToast, t, testingMcpConnectorIds],
    );

    const handleMcpConnectorSync = useCallback(
        async (connector: McpConnectorResponse) => {
            const workspaceId = currentWorkspace?.id;
            if (!workspaceId || !canConnect || connector.canManage === false || syncingMcpConnectorIds.has(connector.connectorId)) {
                return;
            }

            setSyncingMcpConnectorIds((previous) => new Set(previous).add(connector.connectorId));
            setMcpConnectorError(null);
            try {
                await mcpConnectorService.syncTools(workspaceId, connector.connectorId);
                await loadMcpConnectors();
                showToast(t('website.integrationsSettings.mcpSyncSuccess'), 'success');
            } catch (error: any) {
                const message = normalizeConnectionUserMessage(error.message, t('website.integrationsSettings.mcpSyncFailed'));
                setMcpConnectorError(message);
                showToast(message, 'error');
            } finally {
                setSyncingMcpConnectorIds((previous) => {
                    const next = new Set(previous);
                    next.delete(connector.connectorId);
                    return next;
                });
            }
        },
        [canConnect, currentWorkspace?.id, loadMcpConnectors, showToast, syncingMcpConnectorIds, t],
    );

    const handleMcpConnectorCredentialRecovery = useCallback(
        async (connector: McpConnectorResponse) => {
            const workspaceId = currentWorkspace?.id;
            if (!workspaceId || !canConnect || connector.canManage === false || recoveringMcpConnectorIds.has(connector.connectorId)) {
                return;
            }

            const requiresOAuth = connector.credentialStatus === 'AUTH_REQUIRED' || connector.reasonCode === 'MCP_AUTH_REQUIRED';
            const requiresLegacyQueryRepair = connector.reasonCode === 'MCP_LEGACY_QUERY_CREDENTIAL_REPAIR_REQUIRED';
            if (!requiresOAuth && !requiresLegacyQueryRepair) {
                return;
            }

            setRecoveringMcpConnectorIds((previous) => new Set(previous).add(connector.connectorId));
            setMcpConnectorError(null);
            let tracking: ReturnType<typeof startAdminWrite> | null = null;
            let mutationSucceeded = false;
            try {
                if (requiresOAuth) {
                    cancelMcpOAuthTracking('replaced_by_new_flow');
                    tracking = mode === 'admin'
                        ? startAdminWrite({ area: 'integration', action: 'connect', workspace_id: workspaceId, entity_id: String(connector.connectorId) })
                        : null;
                    mcpOAuthTrackingRef.current = tracking;
                    await mcpFlow.startExistingOAuth(connector.connectorId);
                    return;
                }
                tracking = mode === 'admin'
                    ? startAdminWrite({ area: 'integration', action: 'connect', workspace_id: workspaceId, entity_id: String(connector.connectorId) })
                    : null;
                await mcpConnectorService.repairLegacyQueryCredential(workspaceId, connector.connectorId);
                mutationSucceeded = true;
                await mcpConnectorService.testConnection(workspaceId, connector.connectorId);
                await mcpConnectorService.syncTools(workspaceId, connector.connectorId);
                await loadMcpConnectors();
                try {
                    await refreshWorkspaceIntegrationsAndAccess(false, {
                        forceRefresh: true,
                        throwOnError: true,
                    });
                } catch (refreshError) {
                    tracking?.postWriteFailed(refreshError, { failure_stage: 'refresh' });
                }
                tracking?.succeeded({ connection_backend: 'MCP_SERVER' });
                showToast(t('website.integrationsSettings.mcpConnectedSuccess'), 'success');
            } catch (error: any) {
                if (mutationSucceeded) {
                    tracking?.postWriteFailed(error, { failure_stage: 'post_write_follow_up' });
                } else {
                    tracking?.failed(error);
                }
                if (mcpOAuthTrackingRef.current === tracking) {
                    mcpOAuthTrackingRef.current = null;
                }
                const message = normalizeConnectionUserMessage(error.message, t('website.integrationsSettings.mcpCredentialRecoveryFailed'));
                setMcpConnectorError(message);
                showToast(message, 'error');
            } finally {
                setRecoveringMcpConnectorIds((previous) => {
                    const next = new Set(previous);
                    next.delete(connector.connectorId);
                    return next;
                });
            }
        },
        [canConnect, cancelMcpOAuthTracking, currentWorkspace?.id, loadMcpConnectors, mcpFlow, mode, recoveringMcpConnectorIds, refreshWorkspaceIntegrationsAndAccess, showToast, t],
    );

    const handleMcpResourceContextUpdate = useCallback(
        async (connector: McpConnectorResponse, rawProjectRef: string) => {
            const workspaceId = currentWorkspace?.id;
            const resourceContextUpdateKey = workspaceId ? `${workspaceId}:${connector.connectorId}` : null;
            if (!workspaceId || !resourceContextUpdateKey || !canConnect || connector.canManage === false || savingMcpResourceContextConnectorIds.has(resourceContextUpdateKey)) {
                return;
            }

            const projectRef = normalizeSupabaseProjectRef(rawProjectRef);
            if (!isValidSupabaseProjectRef(projectRef)) {
                const message = t('website.integrationsSettings.mcpSupabaseProjectRefInvalid');
                setMcpConnectorError(message);
                showToast(message, 'error');
                return;
            }

            setSavingMcpResourceContextConnectorIds((previous) => new Set(previous).add(resourceContextUpdateKey));
            setMcpConnectorError(null);
            try {
                const result = await saveAndRefreshSupabaseResourceContext<McpConnectorResponse>({
                    service: mcpConnectorService,
                    workspaceId,
                    connectorId: connector.connectorId,
                    projectRef,
                    isCurrentWorkspace: () => currentMcpWorkspaceIdRef.current === workspaceId,
                    refresh: loadMcpConnectors,
                });
                if (result.kind === 'stale') {
                    return;
                }
                setMcpConnectors((previous) =>
                    previous.map((item) => (item.connectorId === connector.connectorId ? { ...item, ...result.connector } : item)),
                );
                setMcpResourceContextDrafts((previous) => {
                    const next = { ...previous };
                    delete next[connector.connectorId];
                    return next;
                });
                if (result.refreshError) {
                    console.warn('[IntegrationsSettings] MCP resource context saved, but connector refresh failed', result.refreshError);
                }
                showToast(t('website.integrationsSettings.mcpResourceContextSaved'), 'success');
            } catch (error: any) {
                if (currentMcpWorkspaceIdRef.current !== workspaceId) {
                    return;
                }
                const message = normalizeConnectionUserMessage(error.message, t('website.integrationsSettings.mcpResourceContextSaveFailed'));
                setMcpConnectorError(message);
                showToast(message, 'error');
            } finally {
                setSavingMcpResourceContextConnectorIds((previous) => {
                    const next = new Set(previous);
                    next.delete(resourceContextUpdateKey);
                    return next;
                });
            }
        },
        [canConnect, currentWorkspace?.id, loadMcpConnectors, savingMcpResourceContextConnectorIds, showToast, t],
    );

    const handleMcpToolUpdate = useCallback(
        async (connector: McpConnectorResponse, tool: McpConnectorTool, update: McpConnectorToolUpdateRequest) => {
            const workspaceId = currentWorkspace?.id;
            const toolId = getMcpToolIdentifier(tool);
            if (!workspaceId || toolId == null || !canConnect || connector.canManage === false) {
                showToast(t('website.integrationsSettings.mcpToolUpdateFailed'), 'error');
                return;
            }

            const updateKey = getMcpToolUpdateKey(connector.connectorId, tool);
            if (updatingMcpToolKeys.has(updateKey)) {
                return;
            }

            setUpdatingMcpToolKeys((previous) => {
                const next = new Set(previous);
                next.add(updateKey);
                return next;
            });
            setMcpConnectorError(null);
            try {
                await mcpConnectorService.updateTool(workspaceId, connector.connectorId, toolId, update);
                await loadMcpConnectors();
                showToast(t('website.integrationsSettings.mcpToolUpdateSuccess'), 'success');
            } catch (error: any) {
                const message = normalizeConnectionUserMessage(error.message, t('website.integrationsSettings.mcpToolUpdateFailed'));
                setMcpConnectorError(message);
                showToast(message, 'error');
            } finally {
                setUpdatingMcpToolKeys((previous) => {
                    const next = new Set(previous);
                    next.delete(updateKey);
                    return next;
                });
            }
        },
        [canConnect, currentWorkspace?.id, loadMcpConnectors, showToast, t, updatingMcpToolKeys],
    );

    const handleMcpToolBooleanColumnToggle = useCallback(
        async (connector: McpConnectorResponse, field: 'enabled' | 'requireConfirmation') => {
            const workspaceId = currentWorkspace?.id;
            const tools = connector.tools || [];
            if (!workspaceId || !canConnect || connector.canManage === false || tools.length === 0 || columnUpdatingMcpConnectorIds.has(connector.connectorId)) {
                return;
            }

            const nextValue = !tools.every((tool) => Boolean(tool[field]));
            const toolsToUpdate = tools
                .filter((tool) => Boolean(tool[field]) !== nextValue)
                .map((tool) => ({
                    toolId: getMcpToolIdentifier(tool),
                    updateKey: getMcpToolUpdateKey(connector.connectorId, tool),
                }))
                .filter((item): item is { toolId: string | number; updateKey: string } => item.toolId != null);

            if (toolsToUpdate.length === 0) {
                return;
            }

            setColumnUpdatingMcpConnectorIds((previous) => new Set(previous).add(connector.connectorId));
            setUpdatingMcpToolKeys((previous) => {
                const next = new Set(previous);
                toolsToUpdate.forEach((item) => next.add(item.updateKey));
                return next;
            });
            setMcpConnectorError(null);
            try {
                await Promise.all(toolsToUpdate.map(({ toolId }) => mcpConnectorService.updateTool(workspaceId, connector.connectorId, toolId, { [field]: nextValue })));
                await loadMcpConnectors();
                showToast(t('website.integrationsSettings.mcpToolUpdateSuccess'), 'success');
            } catch (error: any) {
                const message = normalizeConnectionUserMessage(error.message, t('website.integrationsSettings.mcpToolUpdateFailed'));
                setMcpConnectorError(message);
                showToast(message, 'error');
            } finally {
                setColumnUpdatingMcpConnectorIds((previous) => {
                    const next = new Set(previous);
                    next.delete(connector.connectorId);
                    return next;
                });
                setUpdatingMcpToolKeys((previous) => {
                    const next = new Set(previous);
                    toolsToUpdate.forEach((item) => next.delete(item.updateKey));
                    return next;
                });
            }
        },
        [canConnect, columnUpdatingMcpConnectorIds, currentWorkspace?.id, loadMcpConnectors, showToast, t],
    );

    const handleMcpConnectorDisconnect = useCallback(
        async (connector: McpConnectorResponse) => {
            const workspaceId = currentWorkspace?.id;
            if (!workspaceId) {
                return;
            }
            if (!canDisconnect || connector.canManage === false) {
                if (mode === 'admin') {
                    logAdminWrite('blocked', {
                        area: 'integration',
                        action: 'disconnect',
                        workspace_id: workspaceId,
                        entity_id: connector.connectorId,
                        block_reason: 'permission',
                    });
                }
                showToast(t('website.integrationsSettings.disconnectPermissionDenied'), 'error');
                return;
            }

            const disconnectOperationKey = `${MCP_TOOL_NAME}:${connector.connectorId}`;
            if (disconnectOperationKeysRef.current.has(disconnectOperationKey)) {
                return;
            }
            disconnectOperationKeysRef.current.add(disconnectOperationKey);
            const confirmed = await showDialog({
                title: t('website.integrationsSettings.disconnectTitle', {
                    target: connector.displayName,
                }),
                subtitle: t('website.integrationsSettings.disconnectSubtitle'),
                confirmText: t('website.integrationsSettings.disconnect'),
                cancelText: t('common.cancel'),
                destructive: true,
            });

            if (!confirmed) {
                if (mode === 'admin') {
                    logAdminWrite('cancelled', {
                        area: 'integration',
                        action: 'disconnect',
                        workspace_id: workspaceId,
                        entity_id: connector.connectorId,
                    });
                }
                disconnectOperationKeysRef.current.delete(disconnectOperationKey);
                return;
            }

            setDisconnectingMcpConnectorIds((previous) => {
                const next = new Set(previous);
                next.add(connector.connectorId);
                return next;
            });
            setMcpConnectorError(null);
            const tracking = mode === 'admin'
                ? startAdminWrite({ area: 'integration', action: 'disconnect', workspace_id: workspaceId, entity_id: connector.connectorId })
                : null;
            let mutationSucceeded = false;
            try {
                await mcpConnectorService.disconnect(workspaceId, connector.connectorId);
                mutationSucceeded = true;
                setMcpToolManagerConnectorId((previous) => (previous === connector.connectorId ? null : previous));
                await loadMcpConnectors();
                void refreshWorkspaceIntegrationsAndAccess(false, {
                    forceRefresh: true,
                    throwOnError: true,
                }).then(
                    () => tracking?.succeeded(),
                    (error) => {
                        tracking?.postWriteFailed(error, { failure_stage: 'refresh' });
                        console.warn('[IntegrationsSettings] Failed to refresh integrations after MCP disconnect', error);
                    },
                );
                showToast(
                    t('website.integrationsSettings.disconnectedSuccess', {
                        tool: connector.displayName,
                    }),
                    'success',
                );
            } catch (error: any) {
                if (mutationSucceeded) {
                    tracking?.postWriteFailed(error, { failure_stage: 'refresh' });
                } else {
                    tracking?.failed(error);
                }
                const message = `${t('website.integrationsSettings.disconnectFailed')}: ${normalizeConnectionUserMessage(
                    error.message,
                    t('website.integrationsSettings.failedToDisconnectFromTool', {
                        tool: connector.displayName,
                    }),
                )}`;
                setMcpConnectorError(message);
                showToast(message, 'error');
            } finally {
                disconnectOperationKeysRef.current.delete(disconnectOperationKey);
                setDisconnectingMcpConnectorIds((previous) => {
                    const next = new Set(previous);
                    next.delete(connector.connectorId);
                    return next;
                });
            }
        },
        [canDisconnect, currentWorkspace?.id, loadMcpConnectors, mode, refreshWorkspaceIntegrationsAndAccess, showDialog, showToast, t],
    );

    useEffect(() => {
        if (mode !== 'admin') {
            return undefined;
        }

        let isMounted = true;
        const timingTracker = createIntegrationsSettingsTimingTracker(currentWorkspace?.id);

        trackIntegrationsSettingsTiming(timingTracker, 'integrationPermissions', featureService.getIntegrationPermissions())
            .then((permissions) => {
                if (!isMounted) {
                    return;
                }
                setIntegrationPermissionCatalog(permissions || []);
            })
            .catch((error) => {
                console.warn('[IntegrationsSettings] Failed to load integration permission catalog', error);
            })
            .finally(() => {
                console.info('[IntegrationsSettings] integration permission load timings', timingTracker.toLogPayload());
            });

        return () => {
            isMounted = false;
        };
    }, [mode]);

    const getScrollContainer = useCallback(() => {
        if (scrollContainerRef.current?.isConnected) {
            return scrollContainerRef.current;
        }

        const nextScrollContainer = findNearestScrollContainer(rootRef.current);
        scrollContainerRef.current = nextScrollContainer;
        return nextScrollContainer;
    }, []);

    const closeIntegrationDetail = useCallback(() => {
        pendingScrollTargetRef.current = 'list';
        setSelectedTool(null);
    }, []);

    const clearSearch = useCallback(() => {
        setSearchQuery('');
        searchInputRef.current?.focus();
    }, []);

    const handleOpenAddConnectionUpgrade = () => {
        setIsAddConnectionUpgradeModalOpen(true);
    };

    const handleOpenAddConnectionUpgradePricing = () => {
        if (isPluginAdmin) {
            setIsAddConnectionUpgradeModalOpen(false);
            return;
        }
        setIsAddConnectionUpgradeModalOpen(false);
        modal.openModal('pricing');
    };

    const handleStoreLimitPrimaryAction = () => {
        const primaryActionTarget = storeLimitPrompt?.primaryActionTarget;
        setStoreLimitPrompt(null);

        if (primaryActionTarget === 'none') {
            return;
        }

        if (primaryActionTarget === 'sales') {
            setIsStoreLimitContactSalesOpen(true);
            return;
        }

        modal.openModal('pricing');
    };

    const handleSubscriptionRestrictionPrimaryAction = () => {
        const primaryActionTarget = subscriptionRestrictionPrompt?.primaryActionTarget;
        setSubscriptionRestrictionPrompt(null);

        if (primaryActionTarget === 'none') {
            return;
        }

        modal.openModal('pricing');
    };

    // 需要输入 URL 的应用配置
    const URL_INPUT_CONFIG: Record<
        string,
        {
            prefix: string;
            suffix: string;
            placeholder: string;
            fieldLabel?: string;
            helperText?: string;
        }
    > = {
        Shopify: {
            prefix: 'https://',
            suffix: '.myshopify.com',
            placeholder: 'your-store',
        },
        BigCommerce: {
            prefix: 'https://store-',
            suffix: '.mybigcommerce.com',
            placeholder: 'your-store',
        },
    };

    const resetConnectionForm = useCallback(() => {
        setConnectingTool(null);
        setStoreUrl('');
        setUrlConnectionError(null);
        setBigCommerceStoreHash('');
        setBigCommerceAccountUuid('');
        setApiKeyTool(null);
        setApiKeyValue('');
        setCredentialTool(null);
        setCredentialValues(createIntegrationCredentialValues());
        setFeishuCustomAppTool(null);
        setFeishuCustomAppRegion('feishu');
        setFeishuCustomAppId('');
        setFeishuCustomAppSecret('');
        setDingTalkCustomAppTool(null);
        setDingTalkCustomAppRegion('dingtalk');
        setDingTalkCustomAppId('');
        setDingTalkCustomAppSecret('');
        setDingTalkConnectionMethod('internal_app');
        setMcpConnectSurface(null);
        setMcpModalMode(null);
        setMcpConnectorError(null);
        setPrestaShopStoreUrl('');
    }, []);

    // 检查应用是否支持连接
    const isSupported = (name: string) => isConnectableIntegrationTool(name);

    const getWorkspaceConnectionCount = useCallback(
        (tool: string) => {
            const targetKey = normalizeIntegrationProvider(getIntegrationProviderForTool(tool));

            if (!targetKey) {
                return 0;
            }

            return allIntegrations.reduce((count, integration) => {
                if (!integration.connected || normalizeIntegrationProvider(integration.provider) !== targetKey) {
                    return count;
                }

                const mappings = integration.mappings || [];
                if (mappings.length === 0) {
                    return count + 1;
                }

                return (
                    count +
                    mappings.filter((mapping) => {
                        const status = String(mapping?.status || 'connected').toLowerCase();
                        return status === 'active' || status === 'connected';
                    }).length
                );
            }, 0);
        },
        [allIntegrations],
    );

    const getWorkspaceConnectionIdentityKeys = useCallback(
        (tool: string) => {
            const providerKey = normalizeIntegrationProvider(getIntegrationProviderForTool(tool) || tool);

            if (!providerKey) {
                return new Set<string>();
            }

            return allIntegrations.reduce((keys, integration) => {
                if (normalizeIntegrationProvider(integration.provider) !== providerKey) {
                    return keys;
                }

                // The Resource Contract identifies a connection by the
                // numeric workspace-integration id (and, depending on the
                // provider, by slug/external identity).  A freshly connected
                // mapping can briefly expose only its mapping/Nango ids, so
                // retaining the integration-level identities prevents the
                // post-OAuth assignment from losing the canonical Project
                // connection id during that transition.
                const integrationRecord = integration as typeof integration & {
                    id?: string | number | null;
                    connectionId?: string | number | null;
                    slugId?: string | null;
                    nangoConnectionId?: string | null;
                    nango_connection_id?: string | null;
                };
                [
                    integrationRecord.id,
                    integrationRecord.connectionId,
                    integrationRecord.slugId,
                    integrationRecord.nangoConnectionId,
                    integrationRecord.nango_connection_id,
                    integration.externalAccountId,
                ]
                    .filter((value): value is string | number => value != null && String(value).trim() !== '')
                    .forEach((value) => keys.add(String(value)));

                const mappings = integration.mappings?.length ? integration.mappings : [undefined];
                mappings.forEach((mapping, idx) => {
                    const mappingKeys = [
                        mapping?.id,
                        mapping?.connection_id,
                        mapping?.connectionId,
                        mapping?.nango_connection_id,
                        mapping?.nangoConnectionId,
                        mapping?.slugId,
                        mapping?.slug_id,
                        mapping?.external_account_id,
                        mapping?.externalAccountId,
                    ].filter((value): value is string | number => value != null && String(value).trim() !== '');

                    (mappingKeys.length > 0 ? mappingKeys : [`${normalizeIntegrationProvider(integration.provider)}-${idx}`])
                        .forEach((value) => keys.add(String(value)));
                });

                return keys;
            }, new Set<string>());
        },
        [allIntegrations],
    );

    const clearConnectionSuccessTracking = useCallback((tool: string) => {
        connectStartConnectionCountsRef.current.delete(tool);
        connectStartConnectionKeysRef.current.delete(tool);
        pendingConnectionBrandAssignmentModeRef.current.delete(tool);
        pendingConnectionBrandIdsRef.current.delete(tool);
        pendingConnectionBrandAccessLevelsRef.current.delete(tool);
        pendingGoogleSecondaryResourceKeysRef.current.delete(tool);
    }, []);

    const startSyncingTool = useCallback(
        (tool: string) => {
            completedConnectToolsRef.current.delete(tool);
            finalizedConnectToolsRef.current.delete(tool);
            connectionSuccessToastToolsRef.current.delete(tool);
            pendingGoogleAdsCustomerIdsRef.current.delete(tool);
            pendingGoogleAdsCustomerBrandIdsRef.current.delete(tool);
            pendingGoogleAdsCustomerBrandAccessLevelsRef.current.delete(tool);
            pendingGoogleSecondaryResourceKeysRef.current.delete(tool);
            // Bustly Core can offer optional Business-group assignment after
            // authorization. Plugin connections belong to the workspace only,
            // so they finish as soon as the connection succeeds.
            pendingConnectionBrandAssignmentModeRef.current.set(
                tool,
                shouldSkipBusinessGroupAssignment && tool !== 'NoxInfluencer'
                    ? 'inline'
                    : 'after_authorization',
            );
            pendingConnectionBrandIdsRef.current.set(tool, brandId ? [brandId] : []);
            pendingConnectionBrandAccessLevelsRef.current.set(tool, brandId ? { [brandId]: 'READ' } : {});
            connectStartConnectionCountsRef.current.set(tool, getWorkspaceConnectionCount(tool));
            connectStartConnectionKeysRef.current.set(tool, getWorkspaceConnectionIdentityKeys(tool));
            setSyncingTools((prev) => new Set(prev).add(tool));
        },
        [
            brandId,
            getWorkspaceConnectionCount,
            getWorkspaceConnectionIdentityKeys,
            shouldSkipBusinessGroupAssignment,
        ],
    );

    const clearSyncingTool = useCallback((tool: string) => {
        setSyncingTools((prev) => {
            if (!prev.has(tool)) {
                return prev;
            }

            const next = new Set(prev);
            next.delete(tool);
            return next;
        });
    }, []);

    const persistBusinessGroupAssignments = useCallback(
        async ({
            connectionKeys,
            brandIds,
            selectedResourceKeys,
            requireResources,
            accessLevels,
            attempts,
            waitMs,
            writeAttempts = 1,
            writeWaitMs,
        }: {
            connectionKeys: string[];
            brandIds: string[];
            selectedResourceKeys?: string[];
            requireResources?: boolean;
            accessLevels: Record<string, 'READ' | 'WRITE'>;
            attempts?: number;
            waitMs?: number;
            writeAttempts?: number;
            writeWaitMs?: number;
        }) => {
            if (!currentWorkspace?.id || brandIds.length === 0) {
                return;
            }

            const totalWriteAttempts = Math.max(1, writeAttempts);
            for (let writeAttempt = 0; writeAttempt < totalWriteAttempts; writeAttempt += 1) {
                try {
                    const { connectionIds, resources: assignedResources } = await resolveProjectAssignmentPlanWithRetry(
                        () => projectService.getIntegrationContract(currentWorkspace.id!),
                        connectionKeys,
                        selectedResourceKeys,
                        {
                            attempts,
                            requireResources,
                            wait: () => new Promise((resolve) => window.setTimeout(resolve, waitMs ?? 500)),
                        },
                    );

                    // A Project owns the Integration even when that Integration has no
                    // discoverable resources. Keep the complete cross-Project update in
                    // one Account request so a large group selection cannot exhaust the
                    // server's JDBC pool with concurrent requests. The Account batch
                    // service still applies parent CONNECTION assignments before
                    // secondary resources.
                    await projectService.applyProjectAssignmentBatch(currentWorkspace.id, {
                        integrationAssignments: brandIds.flatMap((brandId) =>
                            connectionIds.map((connectionId) => ({
                                projectId: brandId,
                                connectionId,
                                maxAccessLevel: accessLevels[brandId] || 'WRITE',
                            })),
                        ),
                        resourceSyncs: assignedResources.length === 0 ? [] : brandIds.map((brandId) => ({
                            projectId: brandId,
                            resources: assignedResources.map((resource) => ({
                                provider: resource.provider,
                                resourceType: resource.resourceType,
                                resourceKey: resource.resourceKey,
                                connectionId: resource.connectionId,
                                label: null,
                                maxAccessLevel: accessLevels[brandId] || 'WRITE',
                            })),
                            removals: [],
                        })),
                    });

                    await refreshProjects();
                    return;
                } catch (error) {
                    if (!isTransientProjectAssignmentError(error) || writeAttempt + 1 >= totalWriteAttempts) {
                        throw error;
                    }
                    await new Promise((resolve) => window.setTimeout(resolve, writeWaitMs ?? 500));
                }
            }
        },
        [currentWorkspace?.id, refreshProjects],
    );

    const completeConnectionSuccess = useCallback(
        (tool: string, options: {
            brandAssignmentSynchronized?: boolean;
            businessGroupAssignmentsUpdated?: boolean;
        } = {}) => {
            completedConnectToolsRef.current.add(tool);
            // The refreshed integration snapshot can arrive before the OAuth
            // callback resolves. Make that success transition observable so
            // the assignment flow evaluates the latest snapshot again.
            setConnectionSyncRevision((revision) => revision + 1);

            const hasGoogleAdsCustomerSelection = tool === 'Google Ads'
                && pendingGoogleAdsCustomerIdsRef.current.has(tool);
            const brandAssignmentMode = pendingConnectionBrandAssignmentModeRef.current.get(tool);
            if ((brandAssignmentMode === 'after_authorization' || hasGoogleAdsCustomerSelection) && !options.brandAssignmentSynchronized) {
                return;
            }

            if (!connectionSuccessToastToolsRef.current.has(tool)) {
                connectionSuccessToastToolsRef.current.add(tool);
                showToast(
                    t(
                        options.businessGroupAssignmentsUpdated
                            ? 'website.integrationsSettings.connectedAndBusinessGroupsAssignedSuccess'
                            : 'website.integrationsSettings.connectedSuccess',
                        { tool },
                    ),
                    'success',
                );
            }

            if (!finalizedConnectToolsRef.current.has(tool)) {
                finalizedConnectToolsRef.current.add(tool);
                setSelectedDetailTab('connections');
                clearConnectionSuccessTracking(tool);
                pendingGoogleAdsCustomerIdsRef.current.delete(tool);
                pendingGoogleAdsCustomerBrandIdsRef.current.delete(tool);
                pendingGoogleAdsCustomerBrandAccessLevelsRef.current.delete(tool);
                pendingGoogleSecondaryResourceKeysRef.current.delete(tool);

                if (updateOnboardingData) {
                    const connectedIntegrations = onboardingData.connectedIntegrations || [];
                    updateOnboardingData({
                        connectedIntegrations: connectedIntegrations.includes(tool) ? connectedIntegrations : [...connectedIntegrations, tool],
                    });
                }

                onConnectionSuccess?.();
            }

            completedConnectToolsRef.current.delete(tool);
            clearSyncingTool(tool);
        },
        [brandId, clearSyncingTool, clearConnectionSuccessTracking, onboardingData.connectedIntegrations, onConnectionSuccess, showToast, updateOnboardingData],
    );

    const refreshIntegrationsAfterConnectionSuccess = useCallback(
        (tool: string) => {
            void refreshWorkspaceIntegrationsAndAccess(false, { forceRefresh: true }).catch((error) => {
                console.warn(`[IntegrationsSettings] Failed to refresh integrations after ${tool} connection`, error);
            });
        },
        [refreshWorkspaceIntegrationsAndAccess],
    );

    /**
     * 获取应用的连接状态
     * 统一处理所有应用的连接状态判断逻辑
     * 现在从 workspace_integrations 表查询，检查对象的 connected 字段
     */
    const getWorkspaceConnectionStatus = useCallback(
        (tool: string): 'idle' | 'connected' => {
            const provider = getIntegrationProviderForTool(tool);

            switch (provider) {
                case 'shopify':
                    return shopifyIntegration?.connected ? 'connected' : 'idle';
                case 'bigcommerce':
                    return bigCommerceIntegration?.connected ? 'connected' : 'idle';
                case 'google-ads':
                    return googleAdsIntegration?.connected ? 'connected' : 'idle';
                case 'google-analytics':
                    return googleAnalyticsIntegration?.connected ? 'connected' : 'idle';
                case 'tiktok-ads':
                    return tiktokAdsIntegration?.connected ? 'connected' : 'idle';
                case 'google-search-console':
                    return googleSearchConsoleIntegration?.connected ? 'connected' : 'idle';
                case 'semrush':
                    return semrushIntegration?.connected ? 'connected' : 'idle';
                case 'slack':
                    return slackIntegration?.connected ? 'connected' : 'idle';
                case 'woocommerce':
                    return woocommerceIntegration?.connected ? 'connected' : 'idle';
                case 'magento':
                    return magentoIntegration?.connected ? 'connected' : 'idle';
                case 'prestashop':
                    return prestashopIntegration?.connected ? 'connected' : 'idle';
                case 'aliexpress':
                    return aliexpressIntegration?.connected ? 'connected' : 'idle';
                case 'klaviyo':
                    return klaviyoIntegration?.connected ? 'connected' : 'idle';
                case 'freshdesk':
                    return freshdeskIntegration?.connected ? 'connected' : 'idle';
                case 'activecampaign':
                    return activeCampaignIntegration?.connected ? 'connected' : 'idle';
                case 'helpscout':
                    return helpScoutIntegration?.connected ? 'connected' : 'idle';
                case 'shipstation':
                    return shipStationIntegration?.connected ? 'connected' : 'idle';
                case 'mixpanel':
                    return mixpanelIntegration?.connected ? 'connected' : 'idle';
                case 'mailchimp':
                    return mailchimpIntegration?.connected ? 'connected' : 'idle';
                case 'similarweb':
                    return similarwebIntegration?.connected ? 'connected' : 'idle';
                case 'ahrefs':
                    return ahrefsIntegration?.connected ? 'connected' : 'idle';
                case 'omnisend':
                    return omnisendIntegration?.connected ? 'connected' : 'idle';
                case 'insider-one':
                    return insiderOneIntegration?.connected ? 'connected' : 'idle';
                case 'emarsys':
                    return emarsysIntegration?.connected ? 'connected' : 'idle';
                case 'judgeme':
                    return judgemeIntegration?.connected ? 'connected' : 'idle';
                case 'wix':
                    return wixIntegration?.connected ? 'connected' : 'idle';
                case 'meta-ads':
                    return metaAdsIntegration?.connected ? 'connected' : 'idle';
                case 'facebook-page':
                    return facebookPageIntegration?.connected ? 'connected' : 'idle';
                case 'instagram':
                    return instagramIntegration?.connected ? 'connected' : 'idle';
                case 'linkedin-ads':
                    return linkedinAdsIntegration?.connected ? 'connected' : 'idle';
                case 'pinterest-ads':
                    return pinterestAdsIntegration?.connected ? 'connected' : 'idle';
                case 'x-ads':
                    return xAdsIntegration?.connected ? 'connected' : 'idle';
                case 'youtube-ads':
                    return youtubeIntegration?.connected ? 'connected' : 'idle';
                case 'snapchat-ads':
                    return snapchatIntegration?.connected ? 'connected' : 'idle';
                case 'feishu':
                    return feishuIntegration?.connected ? 'connected' : 'idle';
                case 'dingtalk':
                    return dingtalkIntegration?.connected ? 'connected' : 'idle';
                case 'figma':
                    return figmaIntegration?.connected ? 'connected' : 'idle';
                case 'noxinfluencer':
                    return noxInfluencerIntegration?.connected ? 'connected' : 'idle';
                default:
                    return 'idle';
            }
        },
        [
            aliexpressIntegration?.connected,
            ahrefsIntegration?.connected,
            bigCommerceIntegration?.connected,
            facebookPageIntegration?.connected,
            freshdeskIntegration?.connected,
            feishuIntegration?.connected,
            dingtalkIntegration?.connected,
            activeCampaignIntegration?.connected,
            emarsysIntegration?.connected,
            figmaIntegration?.connected,
            googleAdsIntegration?.connected,
            googleAnalyticsIntegration?.connected,
            googleSearchConsoleIntegration?.connected,
            helpScoutIntegration?.connected,
            instagramIntegration?.connected,
            insiderOneIntegration?.connected,
            judgemeIntegration?.connected,
            klaviyoIntegration?.connected,
            linkedinAdsIntegration?.connected,
            magentoIntegration?.connected,
            mailchimpIntegration?.connected,
            metaAdsIntegration?.connected,
            mixpanelIntegration?.connected,
            noxInfluencerIntegration?.connected,
            omnisendIntegration?.connected,
            pinterestAdsIntegration?.connected,
            prestashopIntegration?.connected,
            semrushIntegration?.connected,
            shipStationIntegration?.connected,
            shopifyIntegration?.connected,
            similarwebIntegration?.connected,
            slackIntegration?.connected,
            snapchatIntegration?.connected,
            tiktokAdsIntegration?.connected,
            wixIntegration?.connected,
            woocommerceIntegration?.connected,
            xAdsIntegration?.connected,
            youtubeIntegration?.connected,
        ],
    );

    const getConnectionStatus = (tool: string): 'idle' | 'syncing' | 'connected' => {
        const workspaceStatus = getWorkspaceConnectionStatus(tool);
        if (workspaceStatus === 'connected') return 'connected';
        if (syncingTools.has(tool)) return 'syncing';
        return 'idle';
    };

    const getRealConnectionsCount = (tool: string): number => {
        const providerKey = normalizeIntegrationProvider(getIntegrationProviderForTool(tool) || tool);
        return allIntegrations.filter((integration) => normalizeIntegrationProvider(integration.provider) === providerKey).length;
    };

    const isToolConnectedBackend = (tool: string): boolean => {
        const provider = getIntegrationProviderForTool(tool);

        switch (provider) {
            case 'shopify':
                return !!shopifyIntegration?.connected;
            case 'bigcommerce':
                return !!bigCommerceIntegration?.connected;
            case 'google-ads':
                return !!googleAdsIntegration?.connected;
            case 'google-analytics':
                return !!googleAnalyticsIntegration?.connected;
            case 'tiktok-ads':
                return !!tiktokAdsIntegration?.connected;
            case 'google-search-console':
                return !!googleSearchConsoleIntegration?.connected;
            case 'semrush':
                return !!semrushIntegration?.connected;
            case 'slack':
                return !!slackIntegration?.connected;
            case 'woocommerce':
                return !!woocommerceIntegration?.connected;
            case 'magento':
                return !!magentoIntegration?.connected;
            case 'prestashop':
                return !!prestashopIntegration?.connected;
            case 'aliexpress':
                return !!aliexpressIntegration?.connected;
            case 'klaviyo':
                return !!klaviyoIntegration?.connected;
            case 'freshdesk':
                return !!freshdeskIntegration?.connected;
            case 'activecampaign':
                return !!activeCampaignIntegration?.connected;
            case 'helpscout':
                return !!helpScoutIntegration?.connected;
            case 'shipstation':
                return !!shipStationIntegration?.connected;
            case 'mixpanel':
                return !!mixpanelIntegration?.connected;
            case 'mailchimp':
                return !!mailchimpIntegration?.connected;
            case 'similarweb':
                return !!similarwebIntegration?.connected;
            case 'ahrefs':
                return !!ahrefsIntegration?.connected;
            case 'omnisend':
                return !!omnisendIntegration?.connected;
            case 'insider-one':
                return !!insiderOneIntegration?.connected;
            case 'emarsys':
                return !!emarsysIntegration?.connected;
            case 'judgeme':
                return !!judgemeIntegration?.connected;
            case 'wix':
                return !!wixIntegration?.connected;
            case 'meta-ads':
                return !!metaAdsIntegration?.connected;
            case 'facebook-page':
                return !!facebookPageIntegration?.connected;
            case 'instagram':
                return !!instagramIntegration?.connected;
            case 'feishu':
                return !!feishuIntegration?.connected;
            case 'dingtalk':
                return !!dingtalkIntegration?.connected;
            case 'figma':
                return !!figmaIntegration?.connected;
            case 'noxinfluencer':
                return !!noxInfluencerIntegration?.connected;
            default:
                return false;
        }
    };

    useEffect(() => {
        const toolsAwaitingConnectionSync = new Set([
            ...syncingTools,
            ...completedConnectToolsRef.current,
        ]);

        Array.from(toolsAwaitingConnectionSync).forEach(async (tool) => {
            if (disconnectingTools.has(tool)) {
                return;
            }

            const hasCompletedSuccess = completedConnectToolsRef.current.has(tool);
            const workspaceStatus = getWorkspaceConnectionStatus(tool);

            const selectedBrandIds = pendingConnectionBrandIdsRef.current.get(tool) ?? [];
            const brandAssignmentMode = pendingConnectionBrandAssignmentModeRef.current.get(tool) ?? 'inline';
            const skipBusinessGroupAssignmentForTool = brandAssignmentMode === 'inline';
            const businessGroupAvailabilityForTool = tool === 'NoxInfluencer'
                && businessGroupAvailability === 'only_default'
                ? 'available'
                : businessGroupAvailability;
            const selectedGoogleAdsCustomerIds = pendingGoogleAdsCustomerIdsRef.current.get(tool) ?? [];
            const hasGoogleAdsCustomerSelection = tool === 'Google Ads'
                && pendingGoogleAdsCustomerIdsRef.current.has(tool);
            const connectionSyncInput = {
                authorizationCompleted: hasCompletedSuccess,
                workspaceConnected: workspaceStatus === 'connected',
                initialConnectionKeys: connectStartConnectionKeysRef.current.get(tool) ?? new Set<string>(),
                currentConnectionKeys: getWorkspaceConnectionIdentityKeys(tool),
            };
            const syncDecision = resolveBrandConnectionSync({
                ...connectionSyncInput,
                googleAdsCustomerIds: selectedGoogleAdsCustomerIds,
            });
            let pluginDefaultProjectIds: string[] = [];
            if (
                skipBusinessGroupAssignmentForTool
                && selectedBrandIds.length === 0
                && syncDecision.kind === 'ready'
            ) {
                if (!currentWorkspace?.id) {
                    return;
                }
                try {
                    pluginDefaultProjectIds = (
                        await projectService.listIntegrationAssignmentProjects(currentWorkspace.id)
                    )
                        .filter((project) => project.isDefault || project.default)
                        .map((project) => project.id);
                } catch (error) {
                    clearSyncingTool(tool);
                    clearConnectionSuccessTracking(tool);
                    completedConnectToolsRef.current.delete(tool);
                    showToast(
                        t('website.integrationsSettings.businessGroupAssignmentFailed'),
                        'error',
                    );
                    console.warn('[IntegrationsSettings] Failed to load the Plugin default Project', error);
                    return;
                }

                if (pluginDefaultProjectIds.length === 0) {
                    clearSyncingTool(tool);
                    clearConnectionSuccessTracking(tool);
                    completedConnectToolsRef.current.delete(tool);
                    showToast(
                        t('website.integrationsSettings.businessGroupAssignmentFailed'),
                        'error',
                    );
                    console.warn('[IntegrationsSettings] Plugin workspace has no default Project');
                    return;
                }
            }
            const assignmentBrandIds = selectedBrandIds.length > 0
                ? selectedBrandIds
                : pluginDefaultProjectIds;
            const assignmentAccessLevels = {
                ...(pendingConnectionBrandAccessLevelsRef.current.get(tool) || {}),
                ...Object.fromEntries(pluginDefaultProjectIds.map((projectId) => [projectId, 'WRITE'])),
            } as Record<string, 'READ' | 'WRITE'>;

            if (hasGoogleAdsCustomerSelection) {
                if (syncDecision.kind !== 'ready') {
                    return;
                }

                if (skipBusinessGroupAssignmentForTool) {
                    completeConnectionSuccess(tool, { brandAssignmentSynchronized: true });
                    return;
                }

                const brandIdsByCustomerId = pendingGoogleAdsCustomerBrandIdsRef.current.get(tool) ?? {};
                const accessLevelsByCustomerId = pendingGoogleAdsCustomerBrandAccessLevelsRef.current.get(tool) ?? {};
                const customerAssignments = resolveGoogleAdsCustomerBrandAssignments({
                    connectionKeys: syncDecision.connectionKeys,
                    customerIds: selectedGoogleAdsCustomerIds,
                    brandIdsByCustomerId,
                    fallbackBrandIds: selectedBrandIds,
                });
                const customersWithBusinessGroupAssignments = [...new Set(
                    selectedGoogleAdsCustomerIds
                        .map(String)
                        .filter((customerId) => (brandIdsByCustomerId[customerId] || selectedBrandIds).length > 0),
                )];
                const assignmentOperationKey = [
                    tool,
                    [...syncDecision.connectionKeys].sort().join(','),
                    customersWithBusinessGroupAssignments.sort().join(','),
                ].join('::');
                if (googleAdsBusinessGroupAssignmentOperationKeysRef.current.has(assignmentOperationKey)) {
                    return;
                }
                googleAdsBusinessGroupAssignmentOperationKeysRef.current.add(assignmentOperationKey);
                setPendingBusinessGroupAssignmentResourceKeys((previous) => new Set([
                    ...previous,
                    ...customersWithBusinessGroupAssignments,
                ]));
                try {
                    await Promise.all(
                        [...new Set(selectedGoogleAdsCustomerIds.map(String).filter(Boolean))].map((customerId) =>
                            (brandIdsByCustomerId[customerId] || selectedBrandIds).length > 0
                              ?
                            persistBusinessGroupAssignments({
                                connectionKeys: syncDecision.connectionKeys,
                                brandIds: brandIdsByCustomerId[customerId] || selectedBrandIds,
                                accessLevels: accessLevelsByCustomerId[customerId] || {},
                                selectedResourceKeys: [customerId],
                                requireResources: true,
                                // The selection endpoint commits the mapping
                                // before the integrations resource contract
                                // read model is necessarily visible.  Give
                                // that read boundary a few seconds to settle,
                                // and retry only transient idempotent writes.
                                // The backend still validates the exact active
                                // Customer on every attempt.
                                attempts: 20,
                                waitMs: 500,
                                writeAttempts: 3,
                                writeWaitMs: 500,
                            })
                              : Promise.resolve(),
                        ),
                    );
                    customerAssignments.forEach(({ customerKey, brandIds }) => {
                        assignExternalGoogleAdsCustomerToBrand(customerKey, brandIds);
                    });
                    completeConnectionSuccess(tool, {
                        brandAssignmentSynchronized: true,
                        businessGroupAssignmentsUpdated: customersWithBusinessGroupAssignments.length > 0,
                    });
                } catch (error) {
                    clearSyncingTool(tool);
                    clearConnectionSuccessTracking(tool);
                    completedConnectToolsRef.current.delete(tool);
                    pendingGoogleAdsCustomerIdsRef.current.delete(tool);
                    pendingGoogleAdsCustomerBrandIdsRef.current.delete(tool);
                    pendingGoogleAdsCustomerBrandAccessLevelsRef.current.delete(tool);
                    showToast(
                        t('website.integrationsSettings.businessGroupAssignmentFailed'),
                        'error',
                    );
                    console.warn('[IntegrationsSettings] Failed to save Google Ads Business group assignment', error);
                } finally {
                    googleAdsBusinessGroupAssignmentOperationKeysRef.current.delete(assignmentOperationKey);
                    setPendingBusinessGroupAssignmentResourceKeys((previous) => {
                        const next = new Set(previous);
                        customersWithBusinessGroupAssignments.forEach((customerId) => next.delete(customerId));
                        return next;
                    });
                }
                return;
            }

            if (isGoogleSecondaryResourceTool(tool)) {
                const googleSecondaryProvider = getGoogleSecondaryResourceProvider(tool);
                if (!googleSecondaryProvider) {
                    return;
                }

                if (syncDecision.kind !== 'ready') {
                    return;
                }

                if (!pendingGoogleSecondaryResourceKeysRef.current.has(tool)) {
                    const resourcePromptAlreadyOpen = googleSecondaryResourceSelectionRef.current.kind === 'choosing'
                        && googleSecondaryResourceSelectionRef.current.tool === tool;
                    if (resourcePromptAlreadyOpen) {
                        return;
                    }

                    const resourceSelectionOperationKey = [
                        tool,
                        [...syncDecision.connectionKeys].sort().join(','),
                    ].join('::');
                    if (googleSecondaryResourceSelectionOperationKeysRef.current.has(resourceSelectionOperationKey)) {
                        return;
                    }
                    googleSecondaryResourceSelectionOperationKeysRef.current.add(resourceSelectionOperationKey);

                    try {
                        if (!currentWorkspace?.id) {
                            return;
                        }

                        const integration = resolveSettingsWorkspaceIntegrationForKeys(
                            allIntegrations,
                            googleSecondaryProvider,
                            syncDecision.connectionKeys,
                        );
                        const nangoConnectionId = resolveOnboardingConnectionId(integration);
                        const workspaceIntegrationId =
                            resolveOnboardingWorkspaceIntegrationId(integration, nangoConnectionId)
                            ?? syncDecision.connectionKeys
                                .map((connectionKey) => Number(connectionKey))
                                .find((connectionId) => Number.isSafeInteger(connectionId) && connectionId > 0)
                            ?? null;

                        if (!workspaceIntegrationId) {
                            throw new Error('google_secondary_workspace_integration_not_ready');
                        }
                        const selectionConnectionId = nangoConnectionId ?? String(workspaceIntegrationId);

                        const { resources } = await loadOnboardingResourceSelectionData({
                            loadContract: () => projectService.getIntegrationContract(currentWorkspace.id!),
                            loadBrands: () => (
                                typeof projectService.listIntegrationAssignmentProjects === 'function'
                                    ? projectService.listIntegrationAssignmentProjects(currentWorkspace.id!)
                                    : projectService.listBrands(currentWorkspace.id!)
                            ).catch(() => projectService.listBrands(currentWorkspace.id!)),
                            provider: googleSecondaryProvider,
                            integrationId: workspaceIntegrationId,
                            connectionId: selectionConnectionId,
                            refreshResources: () => authService.refreshNangoGoogleResources({
                                provider: googleSecondaryProvider,
                                connectionId: selectionConnectionId,
                            }),
                            requireDefaultBusinessGroup: false,
                        });

                        if (resources.length === 0) {
                            await authService.saveNangoGoogleResourceSelection({
                                provider: googleSecondaryProvider,
                                connectionId: selectionConnectionId,
                                selectedResourceKeys: [],
                            });
                            pendingGoogleSecondaryResourceKeysRef.current.set(tool, []);
                            completedConnectToolsRef.current.add(tool);
                            setSyncingTools((previous) => new Set(previous).add(tool));
                            setConnectionSyncRevision((revision) => revision + 1);
                            return;
                        }

                        setGoogleSecondaryResourceSelection({
                            kind: 'choosing',
                            tool,
                            provider: googleSecondaryProvider,
                            connectionId: selectionConnectionId,
                            connectionKeys: syncDecision.connectionKeys,
                            resources,
                        });
                    } catch (error) {
                        clearSyncingTool(tool);
                        clearConnectionSuccessTracking(tool);
                        completedConnectToolsRef.current.delete(tool);
                        showToast(
                            t('website.integrationsSettings.businessGroupAssignmentFailed'),
                            'error',
                        );
                        console.warn('[IntegrationsSettings] Failed to load Google secondary resources', error);
                    } finally {
                        googleSecondaryResourceSelectionOperationKeysRef.current.delete(resourceSelectionOperationKey);
                    }
                    return;
                }
            }

            const promptAlreadyOpen = connectionBrandAssignmentPromptRef.current.kind === 'choosing'
                && connectionBrandAssignmentPromptRef.current.tool === tool;
            const assignmentAction = resolveConnectionBrandAssignmentAction({
                mode: brandAssignmentMode,
                syncDecision,
                selectedBrandIds: assignmentBrandIds,
                promptAlreadyOpen,
                businessGroupAvailability: businessGroupAvailabilityForTool,
                autoAssignBusinessGroupId: skipBusinessGroupAssignmentForTool
                    ? null
                    : onlyDefaultBusinessGroupId,
            });

            if (assignmentAction.kind === 'prompt') {
                const assignmentDraft = createDefaultBusinessGroupAssignmentDraft(
                    connectionBrandOptions,
                    selectedBrandIds,
                    pendingConnectionBrandAccessLevelsRef.current.get(tool) || {},
                );
                setConnectionBrandAssignmentPrompt({
                    kind: 'choosing',
                    tool,
                    connectionKeys: assignmentAction.connectionKeys,
                    selectedBrandIds: assignmentDraft.selectedBrandIds,
                    accessLevels: assignmentDraft.accessLevels,
                });
                return;
            }

            if (assignmentAction.kind === 'assign') {
                const googleSecondaryResourceKeys = pendingGoogleSecondaryResourceKeysRef.current.get(tool) ?? [];
                void persistBusinessGroupAssignments({
                    connectionKeys: assignmentAction.connectionKeys,
                    brandIds: assignmentAction.brandIds,
                    accessLevels: assignmentAccessLevels,
                    selectedResourceKeys: googleSecondaryResourceKeys,
                    requireResources: googleSecondaryResourceKeys.length > 0,
                    attempts: googleSecondaryResourceKeys.length > 0 ? 20 : undefined,
                    waitMs: googleSecondaryResourceKeys.length > 0 ? 500 : undefined,
                    writeAttempts: googleSecondaryResourceKeys.length > 0 ? 3 : undefined,
                    writeWaitMs: googleSecondaryResourceKeys.length > 0 ? 500 : undefined,
                })
                    .then(() => {
                        assignmentAction.connectionKeys.forEach((connectionKey) => {
                            assignExternalConnectionToBrand(
                                connectionKey,
                                assignmentAction.brandIds,
                                '',
                            );
                        });
                        completeConnectionSuccess(tool, {
                            brandAssignmentSynchronized: true,
                            businessGroupAssignmentsUpdated: assignmentAction.brandIds.length > 0,
                        });
                    })
                    .catch((error) => {
                        clearSyncingTool(tool);
                        clearConnectionSuccessTracking(tool);
                        showToast(
                            t('website.integrationsSettings.businessGroupAssignmentFailed'),
                            'error',
                        );
                        console.warn('[IntegrationsSettings] Failed to save Business group assignment', error);
                    });
                return;
            }

            if (assignmentAction.kind === 'complete') {
                completeConnectionSuccess(tool, { brandAssignmentSynchronized: true });
            }
        });
    }, [
        allIntegrations,
        assignExternalConnectionToBrand,
        assignExternalGoogleAdsCustomerToBrand,
        businessGroupAvailability,
        completeConnectionSuccess,
        clearConnectionSuccessTracking,
        clearSyncingTool,
        connectionBrandOptions,
        connectionSyncRevision,
        currentWorkspace?.id,
        disconnectingTools,
        getWorkspaceConnectionCount,
        getWorkspaceConnectionIdentityKeys,
        getWorkspaceConnectionStatus,
        onlyDefaultBusinessGroupId,
        persistBusinessGroupAssignments,
        showToast,
        shouldSkipBusinessGroupAssignment,
        syncingTools,
        t,
    ]);

    const finishPostAuthorizationBrandAssignment = useCallback(async (brandIds: string[], accessLevels: Record<string, 'READ' | 'WRITE'>) => {
        const prompt = connectionBrandAssignmentPromptRef.current;
        if (prompt.kind !== 'choosing' || postAuthorizationBrandAssignmentSavingRef.current) {
            return;
        }

        postAuthorizationBrandAssignmentSavingRef.current = true;
        setIsSavingPostAuthorizationBrandAssignment(true);
        try {
            const googleSecondaryResourceKeys = pendingGoogleSecondaryResourceKeysRef.current.get(prompt.tool) ?? [];
            await persistBusinessGroupAssignments({
                connectionKeys: prompt.connectionKeys,
                brandIds,
                accessLevels,
                selectedResourceKeys: googleSecondaryResourceKeys,
                requireResources: googleSecondaryResourceKeys.length > 0,
                attempts: googleSecondaryResourceKeys.length > 0 ? 20 : undefined,
                waitMs: googleSecondaryResourceKeys.length > 0 ? 500 : undefined,
                writeAttempts: googleSecondaryResourceKeys.length > 0 ? 3 : undefined,
                writeWaitMs: googleSecondaryResourceKeys.length > 0 ? 500 : undefined,
            });
            prompt.connectionKeys.forEach((connectionKey) => {
                assignExternalConnectionToBrand(connectionKey, brandIds, '');
            });
            completeConnectionSuccess(prompt.tool, {
                brandAssignmentSynchronized: true,
                businessGroupAssignmentsUpdated: brandIds.length > 0,
            });
            setConnectionBrandAssignmentPrompt({ kind: 'idle' });
        } catch (error) {
            showToast(t('website.integrationsSettings.businessGroupAssignmentFailed'), 'error');
            console.warn('[IntegrationsSettings] Failed to save post-authorization Business group assignment', error);
        } finally {
            postAuthorizationBrandAssignmentSavingRef.current = false;
            setIsSavingPostAuthorizationBrandAssignment(false);
        }
    }, [assignExternalConnectionToBrand, completeConnectionSuccess, persistBusinessGroupAssignments, showToast, t]);

    const keepDefaultBusinessGroupAssignment = useCallback(() => {
        if (postAuthorizationBrandAssignmentSavingRef.current) return;
        const prompt = connectionBrandAssignmentPromptRef.current;
        if (prompt.kind !== 'choosing') return;

        const assignmentDraft = createDefaultBusinessGroupAssignmentDraft(
            connectionBrandOptions,
        );
        void finishPostAuthorizationBrandAssignment(
            assignmentDraft.selectedBrandIds,
            assignmentDraft.accessLevels,
        );
    }, [connectionBrandOptions, finishPostAuthorizationBrandAssignment]);

    useEffect(() => {
        if (!connectProvider) {
            connectAttemptedRef.current = null;
            return;
        }
        if (isLoading) {
            return;
        }
        if (permissions.status === 'idle' || permissions.status === 'loading') {
            return;
        }

        const tool = getToolFromConnectProvider(connectProvider);
        if (!tool) {
            showToast(
                t('website.integrationsSettings.unsupportedIntegration', {
                    tool: connectProvider,
                }),
                'error',
            );
            return;
        }

        const connectRequestKey = `${connectProvider}:${connectRequestId || ''}:${permissions.status}`;
        if (connectAttemptedRef.current === connectRequestKey) {
            return;
        }
        connectAttemptedRef.current = connectRequestKey;

        if (applyIntegrationConnectPreflight(tool)) {
            return;
        }

        const normalizedTool = tool.toLowerCase().replace(/\s+/g, '_');
        if (!isSupported(normalizedTool)) {
            showToast(
                t('website.integrationsSettings.unsupportedIntegration', {
                    tool,
                }),
                'error',
            );
            return;
        }

        const connectEntryPoint = resolveIntegrationConnectEntryPoint(tool, {
            hasLegacyStoreUrlInput: Boolean(URL_INPUT_CONFIG[tool]),
            hasLegacyCredentialInput: isIntegrationCredentialTool(tool),
        });

        if (connectEntryPoint.kind === 'legacy-store-url') {
            setConnectingTool(tool);
            setStoreUrl('');
            setBigCommerceStoreHash('');
            setBigCommerceAccountUuid('');
            return;
        }

        if (
            connectEntryPoint.kind === 'legacy-credential' &&
            isIntegrationCredentialTool(tool)
        ) {
            setCredentialTool(tool);
            setCredentialValues(createIntegrationCredentialValues(tool));
            return;
        }

        if (requiresManualCredentialInput(tool)) {
            setApiKeyTool(tool);
            setApiKeyValue('');
            setPrestaShopStoreUrl('');
            return;
        }

        if (tool === 'Feishu') {
            setFeishuCustomAppTool(tool);
            setFeishuCustomAppRegion('feishu');
            setFeishuCustomAppId('');
            setFeishuCustomAppSecret('');
            return;
        }

        if (tool === 'DingTalk') {
            setDingTalkCustomAppTool(tool);
            setDingTalkCustomAppRegion('dingtalk');
            setDingTalkCustomAppId('');
            setDingTalkCustomAppSecret('');
            setDingTalkConnectionMethod('internal_app');
            setMcpConnectSurface(null);
            setMcpModalMode(null);
            return;
        }

        resetConnectionForm();
        void handleConnect(tool);
    }, [applyIntegrationConnectPreflight, connectProvider, connectRequestId, isLoading, permissions.status]);

    /**
     * 处理应用连接
     * 根据 app 类型选择不同的连接方式：
     * - 仍由 WWW 收集 URL 的应用（例如 Shopify）：打开 URL 输入弹窗
     * - 凭证应用（Semrush, WooCommerce, Magento）：打开 Bustly 凭证表单
     * - OAuth 应用：直接打开第三方授权页
     */
    const handleConnect = async (tool: string) => {
        if (applyIntegrationConnectPreflight(tool)) {
            return;
        }

        if (isMcpTool(tool) && !isMcpUiEnabled()) {
            return;
        }

        if (isMcpTool(tool)) {
            setSelectedTool(MCP_TOOL_NAME);
            openMcpConnectorModal('url');
            return;
        }

        if (!isSupported(tool)) return;

        const connectEntryPoint = resolveIntegrationConnectEntryPoint(tool, {
            hasLegacyStoreUrlInput: Boolean(URL_INPUT_CONFIG[tool]),
            hasLegacyCredentialInput: isIntegrationCredentialTool(tool),
        });

        if (connectEntryPoint.kind === 'legacy-store-url') {
            setConnectingTool(tool);
            setStoreUrl('');
            setBigCommerceStoreHash('');
            setBigCommerceAccountUuid('');
            return;
        }

        if (
            connectEntryPoint.kind === 'legacy-credential' &&
            isIntegrationCredentialTool(tool)
        ) {
            setCredentialTool(tool);
            setCredentialValues(createIntegrationCredentialValues(tool));
            return;
        }

        if (requiresManualCredentialInput(tool)) {
            setApiKeyTool(tool);
            setApiKeyValue('');
            setPrestaShopStoreUrl('');
            return;
        }

        if (tool === 'Feishu') {
            setFeishuCustomAppTool(tool);
            setFeishuCustomAppRegion('feishu');
            setFeishuCustomAppId('');
            setFeishuCustomAppSecret('');
            return;
        }

        if (tool === 'DingTalk') {
            setDingTalkCustomAppTool(tool);
            setDingTalkCustomAppRegion('dingtalk');
            setDingTalkCustomAppId('');
            setDingTalkCustomAppSecret('');
            setDingTalkConnectionMethod('internal_app');
            setMcpConnectSurface(null);
            setMcpModalMode(null);
            return;
        }

        if (connectOperationKeysRef.current.has(tool)) {
            return;
        }
        connectOperationKeysRef.current.add(tool);

        try {
            startSyncingTool(tool);

            const providerKey = getIntegrationProviderForTool(tool) || tool;
            const result = await withZernioConnectTimeout(
                tool,
                connectPlatform(
                    providerKey,
                    { connection_scene: mode },
                    {
                        onSuccess: async () => {
                            if (tool === 'Google Ads') {
                                return;
                            }
                            if (isGoogleSecondaryResourceTool(tool)) {
                                completedConnectToolsRef.current.add(tool);
                                setConnectionSyncRevision((revision) => revision + 1);
                                refreshIntegrationsAfterConnectionSuccess(tool);
                                return;
                            }
                            completeConnectionSuccess(tool);
                            refreshIntegrationsAfterConnectionSuccess(tool);
                        },
                        onError: (errorMsg: string) => {
                            showToast(`${t('website.integrationsSettings.connectionFailed')}: ${normalizeConnectionUserMessage(errorMsg)}`, 'error');
                            clearSyncingTool(tool);
                            clearConnectionSuccessTracking(tool);
                        },
                        onRestriction: async (restriction) => {
                            await handleConnectRestriction(restriction, tool);
                            clearSyncingTool(tool);
                            clearConnectionSuccessTracking(tool);
                        },
                    },
                ),
            );

            if (await handleConnectRestriction(result, tool)) {
                clearSyncingTool(tool);
                clearConnectionSuccessTracking(tool);
                return;
            }

            if (result?.requiresAccountSelection) {
                openGoogleAdsAccountSelection(result.connectionId);
                clearSyncingTool(tool);
                return;
            }

            if (isGoogleSecondaryResourceTool(tool)) {
                completedConnectToolsRef.current.add(tool);
                setConnectionSyncRevision((revision) => revision + 1);
                refreshIntegrationsAfterConnectionSuccess(tool);
                return;
            }

            if (!result?.uiOpened) {
                completeConnectionSuccess(tool);
                refreshIntegrationsAfterConnectionSuccess(tool);
            }
        } catch (error: any) {
            showToast(
                normalizeConnectionUserMessage(
                    error.message,
                    t('website.integrationsSettings.failedToConnectToTool', {
                        tool,
                    }),
                ),
                'error',
            );
            clearSyncingTool(tool);
            clearConnectionSuccessTracking(tool);
        } finally {
            connectOperationKeysRef.current.delete(tool);
        }
    };

    const handleApiKeyFallbackConnection = (tool: string) => {
        if (!canConnect) {
            showToast(t('website.integrationsSettings.connectPermissionDenied'), 'error');
            return;
        }
        if (applyPluginAdminConnectPreflight(tool)) {
            return;
        }

        setApiKeyTool(tool);
        setApiKeyValue('');
    };

    const handleFeishuCustomAppConnection = async () => {
        const tool = feishuCustomAppTool;
        const appId = feishuCustomAppId.trim();
        const appSecret = feishuCustomAppSecret.trim();
        const scope = FEISHU_REQUIRED_BASE_SCOPE;
        const redirectUri = getFeishuOAuthRedirectUri();
        if (!tool || !appId || !appSecret || !redirectUri) {
            return;
        }
        if (applyIntegrationConnectPreflight(tool)) {
            resetConnectionForm();
            return;
        }

        try {
            startSyncingTool(tool);
            const result = await connectPlatform('feishu', {
                region: feishuCustomAppRegion,
                provider: feishuCustomAppRegion,
                app_id: appId,
                app_secret: appSecret,
                scope,
                redirect_uri: redirectUri,
                connection_scene: mode,
            });

            if (await handleConnectRestriction(result, tool)) {
                clearConnectionSuccessTracking(tool);
                return;
            }

            completeConnectionSuccess(tool);
            resetConnectionForm();
            refreshIntegrationsAfterConnectionSuccess(tool);
        } catch (error: any) {
            showToast(`Connection Failed: ${normalizeConnectionUserMessage(error.message, `Failed to connect to ${tool}`)}`, 'error');
            clearConnectionSuccessTracking(tool);
        } finally {
            clearSyncingTool(tool);
        }
    };

    const handleDingTalkCustomAppConnection = async () => {
        const tool = dingtalkCustomAppTool;
        const appId = dingtalkCustomAppId.trim();
        const appSecret = dingtalkCustomAppSecret.trim();
        const redirectUri = getDingTalkOAuthRedirectUri();
        if (!tool || !appId || !appSecret || !redirectUri) {
            return;
        }
        if (applyIntegrationConnectPreflight(tool)) {
            resetConnectionForm();
            return;
        }

        try {
            startSyncingTool(tool);
            const result = await connectPlatform('dingtalk', {
                region: dingtalkCustomAppRegion,
                provider: dingtalkCustomAppRegion,
                app_id: appId,
                app_secret: appSecret,
                scope: DINGTALK_REQUIRED_BASE_SCOPE,
                redirect_uri: redirectUri,
                connection_scene: mode,
            });

            if (await handleConnectRestriction(result, tool)) {
                clearConnectionSuccessTracking(tool);
                return;
            }

            completeConnectionSuccess(tool);
            resetConnectionForm();
            refreshIntegrationsAfterConnectionSuccess(tool);
        } catch (error: any) {
            showToast(`Connection Failed: ${normalizeConnectionUserMessage(error.message, `Failed to connect to ${tool}`)}`, 'error');
            clearConnectionSuccessTracking(tool);
        } finally {
            clearSyncingTool(tool);
        }
    };

    const handleApiKeyConnection = async () => {
        const tool = apiKeyTool;
        const isPrestaShop = tool === 'PrestaShop';
        const apiKey = apiKeyValue.trim();
        const storeUrlValue = prestaShopStoreUrl.trim();
        if (!tool || (!isPrestaShop && !apiKey)) {
            return;
        }
        if (isPrestaShop && (!storeUrlValue || !apiKey)) {
            return;
        }
        if (applyPluginAdminConnectPreflight(tool)) {
            resetConnectionForm();
            return;
        }

        try {
            startSyncingTool(tool);
            const result = await connectPlatform(
                API_KEY_CONNECT_PROVIDER[tool] || tool,
                isPrestaShop
                    ? {
                          storeUrl: storeUrlValue,
                          apiKey,
                          connection_scene: mode,
                      }
                    : {
                          apiKey,
                          connection_scene: mode,
                      },
            );

            if (await handleConnectRestriction(result, tool)) {
                resetConnectionForm();
                clearConnectionSuccessTracking(tool);
                return;
            }

            completeConnectionSuccess(tool);
            resetConnectionForm();
            refreshIntegrationsAfterConnectionSuccess(tool);
        } catch (error: any) {
            showToast(
                `${t('website.integrationsSettings.connectionFailed')}: ${normalizeConnectionUserMessage(error.message, t('website.integrationsSettings.failedToConnectToTool', { tool }))}`,
                'error',
            );
            clearConnectionSuccessTracking(tool);
        } finally {
            clearSyncingTool(tool);
        }
    };

    const handleIntegrationCredentialConnection = async () => {
        const tool = credentialTool;
        if (!tool) {
            return;
        }
        if (!canConnect) {
            showToast(t('website.integrationsSettings.connectPermissionDenied'), 'error');
            return;
        }
        if (!canSubmitIntegrationCredentialForm(tool, credentialValues)) {
            return;
        }
        if (applyPluginAdminConnectPreflight(tool)) {
            resetConnectionForm();
            return;
        }
        if (connectOperationKeysRef.current.has(tool)) {
            return;
        }

        const { provider, connectionConfig } = buildIntegrationCredentialConnection(tool, credentialValues);
        connectOperationKeysRef.current.add(tool);

        try {
            startSyncingTool(tool);
            const result = await connectPlatform(provider, {
                ...connectionConfig,
                connection_scene: mode,
            });

            if (await handleConnectRestriction(result, tool)) {
                resetConnectionForm();
                clearConnectionSuccessTracking(tool);
                return;
            }

            completeConnectionSuccess(tool);
            resetConnectionForm();
            refreshIntegrationsAfterConnectionSuccess(tool);
        } catch (error: any) {
            showToast(
                `${t('website.integrationsSettings.connectionFailed')}: ${normalizeConnectionUserMessage(
                    error.message,
                    t('website.integrationsSettings.failedToConnectToTool', {
                        tool,
                    }),
                )}`,
                'error',
            );
            clearConnectionSuccessTracking(tool);
        } finally {
            connectOperationKeysRef.current.delete(tool);
            clearSyncingTool(tool);
        }
    };

    const handleRegister = (tool: string) => {
        const url = REGISTRATION_URLS[tool] || `https://www.google.com/search?q=${encodeURIComponent(tool)}+signup`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleConnectRestriction = async (
        payload:
            | {
                  restrictionState?: StoreTrialEligibilityState | null;
                  eligibilityState?: StoreTrialEligibilityState | null;
                  reasonCode?: string | null;
                  blockConnect?: boolean | null;
                  upgradeRequired?: boolean | null;
                  message?: string | null;
                  eligibilityMessage?: string | null;
                  allowsFreshTrial?: boolean | null;
                  trialRemainingDays?: number | null;
                  currentStoreCount?: number | null;
                  storeLimit?: number | null;
                  upgradeTarget?: 'TEAM' | 'SALES' | null;
              }
            | undefined,
        tool: string,
    ) => {
        const action = getConnectRestrictionAction(payload, t, connectRestrictionCopyContext);

        if (action.shouldShowStoreLimitPrompt && action.storeLimitPrompt) {
            setStoreLimitPrompt(action.storeLimitPrompt);
            return true;
        }

        if (action.shouldShowSubscriptionPrompt && action.subscriptionPrompt) {
            const isPluginFreeTrialUsedPrompt = shouldShowPluginFreeTrialUsedPrompt({
                isPluginAdmin,
                isTrialWorkspace: isPluginTrialWorkspace,
                restrictionState: action.restrictionState,
            });
            setSubscriptionRestrictionPrompt(
                isPluginFreeTrialUsedPrompt
                    ? {
                          title: t('website.integrationsSettings.pluginFreeTrialUsedTitle'),
                          description: t('website.integrationsSettings.pluginFreeTrialUsedDescription', { tool }),
                          detail: null,
                          primaryActionLabel: t('website.pricing.startSubscription'),
                          secondaryActionLabel: null,
                          primaryActionTarget: 'plans',
                      }
                    : action.subscriptionPrompt,
            );
            return action.shouldBlockConnect || isPluginFreeTrialUsedPrompt;
        }

        if (!action.shouldBlockConnect) {
            return false;
        }

        if (isDuplicateCurrentWorkspaceConnection(action.message)) {
            showToast(
                t('website.integrationsSettings.duplicateConnectionInCurrentWorkspace', { tool }),
                'warning',
            );
            return true;
        }

        showToast(action.message || t('website.integrationsSettings.storeBoundToAnotherWorkspace'), 'error');
        return true;
    };

    /**
     * 处理需要输入 URL 的应用连接
     * 包括 Shopify 和 BigCommerce
     */
    const handleUrlConnection = async () => {
        if (!connectingTool || !storeUrl.trim()) return;
        if (connectingTool === 'BigCommerce' && (!bigCommerceStoreHash.trim() || !bigCommerceAccountUuid.trim())) {
            return;
        }
        if (urlConnectionInFlightRef.current) {
            return;
        }

        const activeConnectingTool = connectingTool;
        if (applyPluginAdminConnectPreflight(activeConnectingTool)) {
            resetConnectionForm();
            return;
        }
        let keepSyncingUntilCallback = false;
        urlConnectionInFlightRef.current = true;

        try {
            startSyncingTool(activeConnectingTool);
            setUrlConnectionError(null);

            if (activeConnectingTool === 'Shopify') {
                // Shopify connection logic
                const shopDomain = normalizeShopifyStoreDomainInput(storeUrl);
                const url = new URL(window.location.href);
                url.searchParams.set('connect_success', 'shopify');
                const result = await connectPlatform(
                    'shopify',
                    {
                        shop_domain: shopDomain,
                        redirect_url: url.toString(),
                        connection_scene: mode,
                        connection_name: shopDomain,
                    },
                    {
                        onAuthorizationStarted: () => {
                            resetConnectionForm();
                        },
                    },
                );
                if (await handleConnectRestriction(result, activeConnectingTool)) {
                    resetConnectionForm();
                    clearConnectionSuccessTracking(activeConnectingTool);
                    return;
                }
                if (result?.action === 'REDIRECT' && result?.url) {
                    window.location.href = result.url;
                    return;
                }
                completeConnectionSuccess(activeConnectingTool);
                refreshIntegrationsAfterConnectionSuccess(activeConnectingTool);
            } else if (activeConnectingTool === 'BigCommerce') {
                // BigCommerce connection logic
                const bigCommerceStoreDomain = `store-${storeUrl}.mybigcommerce.com`;
                const result = await connectPlatform(
                    'bigcommerce',
                    {
                        store_domain: `https://${bigCommerceStoreDomain}/`,
                        store_hash: bigCommerceStoreHash.trim(),
                        accountUuid: bigCommerceAccountUuid.trim(),
                        connection_scene: mode,
                        connection_name: bigCommerceStoreDomain,
                    },
                    {
                        onAuthorizationStarted: () => {
                            resetConnectionForm();
                        },
                    },
                );
                if (await handleConnectRestriction(result, activeConnectingTool)) {
                    resetConnectionForm();
                    clearConnectionSuccessTracking(activeConnectingTool);
                    return;
                }
                if (result?.action === 'REDIRECT' && result?.url) {
                    window.location.href = result.url;
                    return;
                }
                completeConnectionSuccess(activeConnectingTool);
            } else {
                // Non-URL tools (OAuth based, etc.)
                const result = await connectPlatform(
                    activeConnectingTool,
                    {
                        connection_scene: mode,
                    },
                    {
                        onSuccess: async () => {
                            completeConnectionSuccess(activeConnectingTool);
                            resetConnectionForm();
                            refreshIntegrationsAfterConnectionSuccess(activeConnectingTool);
                        },
                        onError: (errorMsg: string) => {
                            const { message, isDuplicate } = showConnectionFailureToast(
                                errorMsg,
                                activeConnectingTool,
                                t('website.integrationsSettings.failedToConnectToTool', {
                                    tool: activeConnectingTool,
                                }),
                                t('website.integrationsSettings.connectionFailed'),
                            );
                            if (!isDuplicate) {
                                setUrlConnectionError(message);
                            }
                            clearConnectionSuccessTracking(activeConnectingTool);
                        },
                        onRestriction: async (restriction) => {
                            await handleConnectRestriction(restriction, activeConnectingTool);
                            clearConnectionSuccessTracking(activeConnectingTool);
                        },
                    },
                );

                if (await handleConnectRestriction(result, activeConnectingTool)) {
                    resetConnectionForm();
                    clearConnectionSuccessTracking(activeConnectingTool);
                    return;
                }

                if (result?.uiOpened) {
                    keepSyncingUntilCallback = true;
                } else if (!result?.uiOpened && !result?.action) {
                    completeConnectionSuccess(activeConnectingTool);
                    resetConnectionForm();
                    refreshIntegrationsAfterConnectionSuccess(activeConnectingTool);
                }
            }

            if (!keepSyncingUntilCallback) {
                resetConnectionForm();
            }
        } catch (error: any) {
            const { message, isDuplicate } = showConnectionFailureToast(
                error.message,
                activeConnectingTool,
                t('website.integrationsSettings.failedToConnectToTool', {
                    tool: activeConnectingTool,
                }),
                t('website.integrationsSettings.connectionFailed'),
            );
            if (!isDuplicate) {
                setUrlConnectionError(message);
            }
            clearConnectionSuccessTracking(activeConnectingTool);
        } finally {
            urlConnectionInFlightRef.current = false;
            if (keepSyncingUntilCallback) {
                return;
            }
            clearSyncingTool(activeConnectingTool);
        }
    };

    const getConnectionDisconnectTrackingId = (tool: string, connection?: ConnectionData) =>
        tool === 'Feishu' || tool === 'DingTalk' ? connection?.slugId || connection?.connectionId || connection?.id : connection?.connectionId || connection?.id;

    const getDisconnectOperationKey = (tool: string, connection?: ConnectionData) => `${tool}:${getConnectionDisconnectTrackingId(tool, connection) || 'tool'}`;

    const startDisconnectFeedback = (tool: string, connection?: ConnectionData) => {
        const connectionId = getConnectionDisconnectTrackingId(tool, connection);
        setDisconnectingTools((prev) => new Set(prev).add(tool));
        if (connectionId) {
            setDisconnectingConnectionIds((prev) => new Set(prev).add(connectionId));
        }
        return connectionId;
    };

    const clearDisconnectFeedback = (tool: string, connectionId?: string) => {
        setDisconnectingTools((prev) => {
            const next = new Set(prev);
            next.delete(tool);
            return next;
        });
        if (connectionId) {
            setDisconnectingConnectionIds((prev) => {
                const next = new Set(prev);
                next.delete(connectionId);
                return next;
            });
        }
    };

    /**
     * 处理断开集成连接 - 使用 showDialog 显示确认弹窗
     */
    const handleDisconnect = async (tool: string, connection?: ConnectionData) => {
        if (!canDisconnect) {
            showToast('Permission Denied: You do not have permission to disconnect integrations', 'error');
            return;
        }

        const disconnectOperationKey = getDisconnectOperationKey(tool, connection);
        if (disconnectOperationKeysRef.current.has(disconnectOperationKey)) {
            return;
        }
        disconnectOperationKeysRef.current.add(disconnectOperationKey);
        const confirmed = await showDialog({
            title: t('website.integrationsSettings.disconnectTitle', {
                target: tool,
            }),
            subtitle: t('website.integrationsSettings.disconnectSubtitle'),
            confirmText: t('website.integrationsSettings.disconnect'),
            cancelText: t('common.cancel'),
            destructive: true,
        });

        if (confirmed) {
            await performDisconnect(tool, connection, disconnectOperationKey);
            return;
        }

        disconnectOperationKeysRef.current.delete(disconnectOperationKey);
    };

    const handleConnectionDisconnect = async (tool: string, connection: ConnectionData) => {
        if (!canDisconnect) {
            showToast(t('website.integrationsSettings.disconnectPermissionDenied'), 'error');
            return;
        }

        const disconnectOperationKey = getDisconnectOperationKey(tool, connection);
        if (disconnectOperationKeysRef.current.has(disconnectOperationKey)) {
            return;
        }
        disconnectOperationKeysRef.current.add(disconnectOperationKey);
        const connectionName = connection.accountId || connection.name || tool;
        const confirmed = await showDialog({
            title: t('website.integrationsSettings.disconnectTitle', {
                target: connectionName,
            }),
            subtitle: t('website.integrationsSettings.disconnectSubtitle'),
            confirmText: t('website.integrationsSettings.disconnect'),
            cancelText: t('common.cancel'),
            destructive: true,
        });

        if (confirmed) {
            await performDisconnect(tool, connection, disconnectOperationKey);
            return;
        }

        disconnectOperationKeysRef.current.delete(disconnectOperationKey);
    };

    /**
     * 执行断开集成连接操作
     * 使用统一的 disconnect API
     */
    const performDisconnect = async (tool: string, connection?: ConnectionData, disconnectOperationKey?: string) => {
        clearConnectionSuccessTracking(tool);
        setSyncingTools((prev) => new Set(prev).add(tool));
        setDisconnectingTools((prev) => new Set(prev).add(tool));
        const connectionId = getConnectionDisconnectTrackingId(tool, connection);
        const tracking = mode === 'admin'
            ? startAdminWrite({
                area: 'integration',
                action: 'disconnect',
                workspace_id: currentWorkspace?.id,
                entity_id: connection?.workspaceIntegrationId || connection?.id,
                integration_tool: tool,
            })
            : null;
        let mutationSucceeded = false;
        if (connectionId) {
            setDisconnectingConnectionIds((prev) => new Set(prev).add(connectionId));
        }

        try {
            if (connection?.connectionMode === 'MCP_SERVER' && connection.mcpConnectorId != null) {
                const workspaceId = currentWorkspace?.id;
                if (!workspaceId) {
                    throw new Error(t('website.integrationsSettings.mcpSaveFailed'));
                }
                await mcpConnectorService.disconnect(workspaceId, connection.mcpConnectorId);
                mutationSucceeded = true;
                await loadMcpConnectors();
                try {
                    await refreshWorkspaceIntegrations(false, {
                        forceRefresh: true,
                        throwOnError: true,
                    });
                } catch (refreshError) {
                    tracking?.postWriteFailed(refreshError, { failure_stage: 'refresh' });
                }
                tracking?.succeeded();
                showToast(
                    t('website.integrationsSettings.disconnectedSuccess', {
                        tool,
                    }),
                    'success',
                );
                return;
            }

            const normalizedTool = tool.toLowerCase().replace(/\s+/g, '_');
            const workspaceIntegrationId = connection?.workspaceIntegrationId || connection?.id;
            const shopDomain = tool === 'Shopify' ? connection?.accountId || connection?.name : undefined;

            // 使用统一的 disconnect API
            await disconnectPlatform(normalizedTool, {
                connectionId,
                workspaceIntegrationId,
                shopDomain,
                authType: connection?.authType,
                native: connection?.native,
                connectionBackend: connection?.connectionBackend,
                onMutationSucceeded: () => {
                    mutationSucceeded = true;
                },
                onPostWriteFailed: (refreshError) => {
                    tracking?.postWriteFailed(refreshError, { failure_stage: 'refresh' });
                },
            });

            // Update onboarding data
            if (updateOnboardingData) {
                updateOnboardingData({
                    connectedIntegrations: (onboardingData.connectedIntegrations || []).filter((i) => i !== tool),
                });
            }

            tracking?.succeeded();
            showToast(t('website.integrationsSettings.disconnectedSuccess', { tool }), 'success');
        } catch (error: any) {
            if (mutationSucceeded) {
                tracking?.postWriteFailed(error, { failure_stage: 'post_write_follow_up' });
            } else {
                tracking?.failed(error);
            }
            showToast(
                `${t('website.integrationsSettings.disconnectFailed')}: ${normalizeConnectionUserMessage(error.message, t('website.integrationsSettings.failedToDisconnectFromTool', { tool }))}`,
                'error',
            );
        } finally {
            if (disconnectOperationKey) {
                disconnectOperationKeysRef.current.delete(disconnectOperationKey);
            }
            setSyncingTools((prev) => {
                const next = new Set(prev);
                next.delete(tool);
                return next;
            });
            setDisconnectingTools((prev) => {
                const next = new Set(prev);
                next.delete(tool);
                return next;
            });
            if (connectionId) {
                setDisconnectingConnectionIds((prev) => {
                    const next = new Set(prev);
                    next.delete(connectionId);
                    return next;
                });
            }
        }
    };

    const getMcpConnectorConnections = useCallback(
        (): ConnectionData[] =>
            customMcpConnectors.map((connector): ConnectionData => {
                const connectorId = String(connector.connectorId);
                const toolCount = connector.tools?.length || 0;
                return {
                    id: connectorId,
                    connectionId: connectorId,
                    workspaceIntegrationId: connector.integrationId == null ? undefined : String(connector.integrationId),
                    connectionMode: 'MCP_SERVER',
                    mcpConnectorId: connector.connectorId,
                    integrationType: MCP_TOOL_NAME,
                    name: connector.displayName,
                    alias: connector.displayName,
                    accountId: connector.serverUrl,
                    status: mapMcpConnectorStatusToConnectionStatus(connector.status),
                    statusLabel: connector.status,
                    statusMessage: connector.reasonCode || undefined,
                    connectedBy: '',
                    connectedAt: 'Recent',
                    permissionSummary:
                        toolCount > 0
                            ? t('website.integrationsSettings.mcpToolCount', {
                                  count: toolCount,
                                  plural: toolCount === 1 ? '' : 's',
                              })
                            : '',
                    permissions: {
                        read: toolCount > 0 ? t('website.integrationsSettings.mcpToolsSynced') : '',
                        write: '',
                    },
                };
            }),
        [customMcpConnectors, t],
    );

    const getToolConnections = useCallback(
        (tool: string): ConnectionData[] => {
            if (isMcpTool(tool)) {
                return getMcpConnectorConnections();
            }

            const providerKey = normalizeIntegrationProvider(getIntegrationProviderForTool(tool) || tool);
            const realIntegrationGroups = allIntegrations.filter((integration) => normalizeIntegrationProvider(integration.provider) === providerKey);
            let connections = realIntegrationGroups.flatMap((integration) => {
                const profile = integration.profile_data || {};
                if (!integration.mappings?.length && !integration.connected) {
                    return [];
                }
                const mappings = integration.mappings?.length ? integration.mappings : [undefined];
                const availability = tool === 'Klaviyo' ? resolveKlaviyoIntegrationAvailability(integration) : AVAILABLE_INTEGRATION_STATUS;

                return mappings.map((mapping, idx): ConnectionData => {
                    const connId = String(mapping?.id || `${integration.provider}-${idx}`);
                    const workspaceConnectionStatus: ConnectionData['status'] = integration.connected ? 'connected' : 'disconnected';
                    const normalizedConnectionStatus = normalizeConnectionStatusValue(mapping?.status, workspaceConnectionStatus);
                    const displayStatus = availability.isUnavailable
                        ? availability.status
                        : integration.connected && normalizedConnectionStatus === 'disconnected'
                          ? 'connected'
                          : normalizedConnectionStatus;
                    const isShopifyConnection = tool === 'Shopify';
                    const isGoogleAdsConnection = tool === 'Google Ads';
                    const isGoogleOAuthConnection = tool === 'Google Analytics' || tool === 'Google Search Console';
                    const isBigCommerceConnection = tool === 'BigCommerce';
                    const isFeishuConnection = tool === 'Feishu';
                    const isDingTalkConnection = tool === 'DingTalk';
                    const googleAdsAuthorizedAccountValue = isGoogleAdsConnection ? resolveGoogleAdsAuthorizedAccountValue(profile, integration, mapping) : '';
                    const googleOAuthAccountDisplayName = isGoogleOAuthConnection
                        ? getGoogleOAuthAccountDisplayName(integration, mapping, profile)
                        : null;
                    const feishuAccountDisplayValue = isFeishuConnection ? resolveFeishuConnectionDisplayName(profile, integration, mapping) : '';
                    const dingtalkAccountDisplayValue = isDingTalkConnection ? resolveDingTalkConnectionDisplayName(profile, integration, mapping) : '';
                    const accountValue = isShopifyConnection
                        ? resolveShopifyConnectionDomain({ integration, mapping }) || mapping?.account_name || mapping?.connection_id || ''
                        : isGoogleAdsConnection
                          ? googleAdsAuthorizedAccountValue
                          : isGoogleOAuthConnection
                            ? googleOAuthAccountDisplayName || `${tool} account`
                          : isFeishuConnection
                            ? feishuAccountDisplayValue
                            : isDingTalkConnection
                              ? dingtalkAccountDisplayValue
                              : isBigCommerceConnection
                                ? mapping?.name || mapping?.account_name || mapping?.external_account_name || mapping?.externalAccountName || mapping?.account_id || mapping?.external_account_id || mapping?.externalAccountId || profile.accountName || profile.name || mapping?.connection_id || ''
                                : profile.accountId || mapping?.account_name || mapping?.external_account_name || mapping?.externalAccountName || mapping?.account_id || mapping?.external_account_id || mapping?.externalAccountId || profile.username || mapping?.connection_id || '';
                    const connectionLabel = mapping?.label || '';
                    const connectedByName = resolveConnectionUserDisplayName(mapping, profile);
                    const googleAdsSubAccounts = isGoogleAdsConnection
                        ? (mapping?.sub_accounts || [])
                              .map((subAccount) => ({
                                  accountName: String(subAccount.account_name || subAccount.customer_id || subAccount.account_id || 'Google Ads sub account'),
                                  customerId: String(subAccount.customer_id || subAccount.account_id || ''),
                                  label: typeof subAccount.label === 'string' ? subAccount.label : null,
                              }))
                              .filter((subAccount) => subAccount.customerId)
                        : undefined;
                    const connectionId =
                        mapping?.external_connection_id ||
                        mapping?.externalConnectionId ||
                        mapping?.connection_id ||
                        mapping?.nango_connection_id ||
                        undefined;
                    const connectionBackend = mapping?.connection_backend || mapping?.connectionBackend || undefined;
                    const workspaceIntegrationId = mapping?.id == null ? undefined : String(mapping.id);
                    const dingtalkMcpConnector = isDingTalkConnection
                        ? mcpConnectors.find(
                              (connector) => connector.platform === 'DINGTALK' && connector.integrationId != null && String(connector.integrationId) === workspaceIntegrationId,
                          )
                        : undefined;
                    const needsGoogleAdsAccountSelection =
                        isGoogleAdsConnection && Boolean(connectionId) && (displayStatus === 'connected' || displayStatus === 'active') && !googleAdsSubAccounts?.length;

                    return {
                        id: connId,
                        connectionId,
                        connectionBackend,
                        workspaceIntegrationId,
                        connectionMode: dingtalkMcpConnector ? 'MCP_SERVER' : 'INTERNAL_APP',
                        mcpConnectorId: dingtalkMcpConnector?.connectorId,
                        slugId: mapping?.slugId || profile.slugId,
                        authType: String(mapping?.authType || integration.authType || ''),
                        native: Boolean(mapping?.native ?? integration.native),
                        integrationType: tool,
                        name: dingtalkMcpConnector
                            ? dingtalkMcpConnector.displayName
                            : isGoogleAdsConnection
                              ? googleAdsAuthorizedAccountValue
                              : isGoogleOAuthConnection
                                ? googleOAuthAccountDisplayName || `${tool} account`
                              : isFeishuConnection
                                ? feishuAccountDisplayValue
                                : isDingTalkConnection
                                  ? dingtalkAccountDisplayValue
                                  : profile.connection_name || profile.accountName || mapping?.name || mapping?.account_name || mapping?.external_account_name || mapping?.externalAccountName || `${tool} Workspace`,
                        alias: connectionLabel,
                        accountId: dingtalkMcpConnector?.serverUrl || String(accountValue),
                        status: dingtalkMcpConnector ? mapMcpConnectorStatusToConnectionStatus(dingtalkMcpConnector.status) : displayStatus,
                        statusLabel: availability.label,
                        statusMessage: availability.message,
                        isUnavailable: availability.isUnavailable,
                        connectedBy: connectedByName,
                        connectedAt: mapping?.created_at || integration.created_at || 'Recent',
                        permissionSummary: dingtalkMcpConnector
                            ? t('website.integrationsSettings.mcpToolCount', {
                                  count: dingtalkMcpConnector.tools?.length || 0,
                                  plural: dingtalkMcpConnector.tools?.length === 1 ? '' : 's',
                              })
                            : '',
                        needsAccountSelection: needsGoogleAdsAccountSelection,
                        subAccounts: googleAdsSubAccounts,
                        permissions: {
                            read: dingtalkMcpConnector ? t('website.integrationsSettings.mcpToolsSynced') : '',
                            write: '',
                        },
                    };
                });
            });

            if (tool === 'DingTalk') {
                const existingIntegrationIds = new Set(connections.map((connection) => connection.workspaceIntegrationId).filter((value): value is string => Boolean(value)));
                const mcpOnlyConnections = mcpConnectors
                    .filter((connector) => connector.platform === 'DINGTALK' && (connector.integrationId == null || !existingIntegrationIds.has(String(connector.integrationId))))
                    .map((connector): ConnectionData => {
                        const toolCount = connector.tools?.length || 0;
                        return {
                            id: `dingtalk-mcp-${connector.connectorId}`,
                            connectionId: String(connector.connectorId),
                            workspaceIntegrationId: connector.integrationId == null ? undefined : String(connector.integrationId),
                            connectionMode: 'MCP_SERVER',
                            mcpConnectorId: connector.connectorId,
                            integrationType: 'DingTalk',
                            name: connector.displayName,
                            alias: connector.displayName,
                            accountId: connector.serverUrl,
                            status: mapMcpConnectorStatusToConnectionStatus(connector.status),
                            statusLabel: connector.status,
                            statusMessage: connector.reasonCode || undefined,
                            connectedBy: '',
                            connectedAt: 'Recent',
                            permissionSummary: t('website.integrationsSettings.mcpToolCount', {
                                count: toolCount,
                                plural: toolCount === 1 ? '' : 's',
                            }),
                            permissions: {
                                read: toolCount > 0 ? t('website.integrationsSettings.mcpToolsSynced') : '',
                                write: '',
                            },
                        };
                    });
                connections = [...connections, ...mcpOnlyConnections];
            }

            if (brandId) {
                if (Array.isArray(brandConnectionIds)) {
                    const assignedConnectionIds = new Set(brandConnectionIds.map(String));
                    const assignedProjectResources = (brandResources || []).filter((resource) => resource.brandIds?.includes(brandId));
                    connections = connections.flatMap((connection) => {
                        const connectionAssigned = [
                            connection.workspaceIntegrationId,
                            connection.connectionId,
                            connection.id,
                        ].some((identity) => identity != null && assignedConnectionIds.has(String(identity)));
                        const providerResources = assignedProjectResources.filter(
                            (resource) => normalizeIntegrationProvider(resource.provider) === providerKey,
                        );
                        const resourceMatchesConnection = (resource: IntegrationResource) =>
                            (resource.connectionId != null &&
                                connection.connectionId != null &&
                                String(resource.connectionId) === String(connection.connectionId)) ||
                            (resource.connectionId == null && resource.resourceKey === connection.accountId);

                        if (!connectionAssigned && providerResources.length === 0) return [];
                        if (connection.subAccounts?.length) {
                            if (connectionAssigned && providerResources.length === 0) return [connection];
                            const scopedSubAccounts = connection.subAccounts.filter((subAccount) =>
                                providerResources.some(
                                    (resource) =>
                                        resource.resourceKey === subAccount.customerId &&
                                        resourceMatchesConnection(resource),
                                ),
                            );
                            return scopedSubAccounts.length
                                ? [{ ...connection, subAccounts: scopedSubAccounts }]
                                : [];
                        }
                        return [connection];
                    });
                } else if (Array.isArray(brandResources)) {
                    const assignedProjectResources = brandResources.filter((resource) => resource.brandIds?.includes(brandId));
                    connections = connections.flatMap<ConnectionData>((connection) => {
                        const providerResources = assignedProjectResources.filter(
                            (resource) => normalizeIntegrationProvider(resource.provider) === providerKey,
                        );
                        const resourceMatchesConnection = (resource: IntegrationResource) =>
                            (resource.connectionId != null &&
                                connection.connectionId != null &&
                                String(resource.connectionId) === String(connection.connectionId)) ||
                            (resource.connectionId == null && resource.resourceKey === connection.accountId);

                        if (connection.subAccounts?.length) {
                            const scopedSubAccounts = connection.subAccounts.filter((subAccount) =>
                                providerResources.some(
                                    (resource) =>
                                        resource.resourceKey === subAccount.customerId &&
                                        resourceMatchesConnection(resource),
                                ),
                            );

                            return [{ ...connection, subAccounts: scopedSubAccounts }];
                        }

                        return [connection];
                    });
                } else {
                    connections = connections.flatMap<ConnectionData>((connection) => {
                        if (connection.subAccounts?.length) {
                            const scopedSubAccounts = connection.subAccounts.filter((subAccount) =>
                                brandPrototypeData.externalGoogleAdsCustomerBrandIds[`${connection.id}:${subAccount.customerId}`]?.includes(brandId),
                            );

                            return scopedSubAccounts.length
                                ? [
                                      {
                                          ...connection,
                                          subAccounts: scopedSubAccounts,
                                      },
                                  ]
                                : [];
                        }

                        const connectionKey = String(connection.workspaceIntegrationId || connection.id);
                        if (!brandPrototypeData.externalConnectionBrandIds[connectionKey]?.includes(brandId)) {
                            return [];
                        }
                        return [{
                            ...connection,
                            alias: brandPrototypeData.externalConnectionBrandLabels?.[connectionKey]?.[brandId] || '',
                        }];
                    });
                }
            }

            return connections;
        },
        [
            allIntegrations,
            brandId,
            brandConnectionIds,
            brandResources,
            brandPrototypeData.externalConnectionBrandIds,
            brandPrototypeData.externalConnectionBrandLabels,
            brandPrototypeData.externalGoogleAdsCustomerBrandIds,
            getMcpConnectorConnections,
            isSoloWorkspace,
            mcpConnectors,
            t,
        ],
    );

    const openIntegrationDetail = useCallback((tool: string) => {
        const scrollContainer = getScrollContainer();

        if (scrollContainer) {
            listScrollTopRef.current = scrollContainer.scrollTop;
            pendingScrollTargetRef.current = 'detail';
        }

        setSelectedDetailTab(resolveDefaultIntegrationDetailTab(getToolConnections(tool).some(isIntegrationConnectionActive)));
        setSelectedTool(tool);
    }, [getScrollContainer, getToolConnections]);

    useEffect(() => {
        if (!detailProvider || isLoading) {
            return;
        }

        const requestKey = `${detailProvider}:${detailRequestId || 'initial'}`;
        if (handledDetailRequestRef.current === requestKey) {
            return;
        }
        handledDetailRequestRef.current = requestKey;

        const tool = getToolFromConnectProvider(detailProvider);
        if (!tool) {
            showToast(
                t('website.integrationsSettings.unsupportedIntegration', {
                    tool: detailProvider,
                }),
                'error',
            );
            return;
        }

        const normalizedTool = tool.toLowerCase().replace(/\s+/g, '_');
        if (!isSupported(normalizedTool)) {
            showToast(
                t('website.integrationsSettings.unsupportedIntegration', {
                    tool,
                }),
                'error',
            );
            return;
        }

        openIntegrationDetail(tool);
    }, [detailProvider, detailRequestId, isLoading, openIntegrationDetail, showToast, t]);

    const getToolCapabilityState = (tool: string, connection?: ConnectionData) => {
        const copy = resolveIntegrationContent(content.rows, tool, locale);
        const catalog: CapabilityCatalog = {
            read: buildContentCapabilitySections(tool, 'read', copy?.readCapabilities || [], t, shouldUsePluginWorkspaceUi),
            write: buildContentCapabilitySections(tool, 'write', copy?.writeCapabilities || [], t, shouldUsePluginWorkspaceUi),
        };
        const availableReadCount = countCapabilityItems(catalog.read);
        const availableWriteCount = countCapabilityItems(catalog.write);
        const activeReadItemIds = getActiveCapabilityItemIds(tool, connection, 'read', catalog);
        const activeWriteItemIds = getActiveCapabilityItemIds(tool, connection, 'write', catalog);

        return {
            catalog,
            availableReadCount,
            availableWriteCount,
            activeReadItemIds,
            activeWriteItemIds,
            activeReadCount: activeReadItemIds.size,
            activeWriteCount: activeWriteItemIds.size,
            accessLabel: getCapabilityAccessLabel(t, activeReadItemIds.size, activeWriteItemIds.size),
        };
    };

    const getToolSortPriority = useCallback(
        (tool: string) => {
            if (getToolConnections(tool).some(isIntegrationConnectionActive)) {
                return 0;
            }

            if (isSupported(tool)) {
                return 1;
            }

            return 2;
        },
        [getToolConnections],
    );

    const sortToolsByPriority = useCallback(
        (tools: string[]) =>
            [...tools].sort((toolA, toolB) => {
                const priorityDelta = getToolSortPriority(toolA) - getToolSortPriority(toolB);

                if (priorityDelta !== 0) {
                    return priorityDelta;
                }

                return (ALL_INTEGRATION_TOOL_ORDER.get(toolA) ?? Number.MAX_SAFE_INTEGER) - (ALL_INTEGRATION_TOOL_ORDER.get(toolB) ?? Number.MAX_SAFE_INTEGER);
            }),
        [getToolSortPriority],
    );

    const connectedIntegrationTools = ALL_INTEGRATION_TOOLS.filter((tool) => getToolConnections(tool).some(isIntegrationConnectionActive));

    useEffect(() => {
        const workspaceId = currentWorkspace?.id;

        if (
            viewMode !== 'full'
            || isLoading
            || !workspaceId
            || catalogLandingWorkspaceIdRef.current === workspaceId
        ) {
            return;
        }

        catalogLandingWorkspaceIdRef.current = workspaceId;

        if (connectedIntegrationTools.length === 0) {
            setSearchQuery('');
            setSelectedCategory('Store Ops');
        }
    }, [connectedIntegrationTools.length, currentWorkspace?.id, isLoading, viewMode]);

    const visibleScenarioFilters = SCENARIO_FILTERS;
    const connectionScopeFilters: Array<{
        scope: ConnectionAccessFilter;
        labelKey: string;
        count: number | null;
    }> = [{
        scope: 'owned_by_me',
        labelKey: 'website.integrationsSettings.ownedByMe',
        count: connectionScopeCounts?.owned_by_me ?? null,
    }];
    connectionScopeFilters.push({
        scope: 'all',
        labelKey: brandId ? 'website.integrationsSettings.allBrandConnections' : 'website.integrationsSettings.allConnections',
        count: connectionScopeCounts?.all ?? null,
    });

    const scenarioSections = SCENARIO_FILTERS.filter((cat) => cat.id !== 'Connected').map((cat) => ({
        category: cat.id,
        tools: [] as string[],
    }));

    const scenarioSectionMap = new Map(scenarioSections.map((section) => [section.category, section]));

    ALL_INTEGRATIONS.forEach((group) => {
        group.tools.forEach((tool) => {
            const sourceScenarioCategory = INTEGRATION_SOURCE_CATEGORY_TO_SCENARIO[group.category];

            if (!sourceScenarioCategory) {
                return;
            }

            const scenarioCategory = getIntegrationBrowseDisplayScenario(sourceScenarioCategory);
            const targetSection = scenarioSectionMap.get(scenarioCategory);

            if (targetSection && !targetSection.tools.includes(tool)) {
                targetSection.tools.push(tool);
            }
        });
    });

    scenarioSections.forEach((section) => {
        section.tools = sortToolsByPriority(section.tools);
    });

    const hasSearchQuery = searchQuery.trim().length > 0;
    const selectedScenarioSection = scenarioSections.find((section) => section.category === selectedCategory);
    const visibleConnectedTools = connectedIntegrationTools.filter((tool) => matchesIntegrationSearch(tool, searchQuery));
    const visibleScenarioTools = (selectedScenarioSection?.tools || []).filter((tool) => matchesIntegrationSearch(tool, searchQuery));
    const visibleSearchTools = hasSearchQuery ? sortToolsByPriority(ALL_INTEGRATION_TOOLS.filter((tool) => matchesIntegrationSearch(tool, searchQuery))) : [];
    const selectedListTools = hasSearchQuery
        ? visibleSearchTools
        : selectedCategory === 'Connected'
          ? visibleConnectedTools
          : visibleScenarioTools;
    const selectCatalogCategory = (category: ScenarioFilter) => {
        setSelectedCategory(category);
        catalogScrollAreaRef.current?.scrollTo({ top: 0 });
    };
    const selectedToolConnections = selectedTool ? getToolConnections(selectedTool) : [];
    const lastStableConnections = selectedTool ? lastStableConnectionsByToolRef.current[selectedTool] || [] : [];
    const isDisconnectingSelectedTool = selectedTool ? disconnectingTools.has(selectedTool) : false;
    const selectedToolDisplayConnections =
        selectedTool && isDisconnectingSelectedTool && lastStableConnections.length > selectedToolConnections.length ? lastStableConnections : selectedToolConnections;
    const selectedToolPrimaryConnection = selectedToolDisplayConnections[0];
    const selectedToolCapabilityState = selectedTool ? getToolCapabilityState(selectedTool, selectedToolPrimaryConnection) : null;
    useEffect(() => {
        if (!selectedTool || selectedToolConnections.length === 0 || syncingTools.has(selectedTool)) {
            return;
        }

        lastStableConnectionsByToolRef.current[selectedTool] = selectedToolConnections;
    }, [selectedTool, selectedToolConnections, syncingTools]);

    useEffect(() => {
        if (!pendingScrollTargetRef.current || typeof window === 'undefined') {
            return undefined;
        }

        const scrollContainer = getScrollContainer();
        if (!scrollContainer) {
            pendingScrollTargetRef.current = null;
            return undefined;
        }

        const nextScrollTarget = pendingScrollTargetRef.current;
        pendingScrollTargetRef.current = null;
        const targetTop = nextScrollTarget === 'detail' ? 0 : listScrollTopRef.current;
        const frameId = window.requestAnimationFrame(() => {
            scrollContainer.scrollTo({ top: targetTop });
        });

        return () => {
            window.cancelAnimationFrame(frameId);
        };
    }, [getScrollContainer, selectedTool]);

    useEffect(() => {
        if (!selectedTool) {
            return;
        }

        setSelectedDetailTab(resolveDefaultIntegrationDetailTab(selectedToolDisplayConnections.length > 0));
    }, [selectedTool, selectedToolDisplayConnections.length]);

    useEffect(() => {
        setIsAddConnectionUpgradeModalOpen(false);
    }, [selectedTool]);

    useEffect(() => {
        if (selectedCategory === 'Connected' && isLoading) {
            return;
        }

        if (viewMode === 'catalog' && selectedCategory === 'Connected') {
            setSelectedCategory('Store Ops');
            return;
        }

        if (visibleScenarioFilters.some((category) => category.id === selectedCategory)) {
            return;
        }

        setSelectedCategory(visibleScenarioFilters[0]?.id ?? 'Store Ops');
    }, [isLoading, selectedCategory, viewMode, visibleScenarioFilters]);

    const renderPermissionSection = () => {
        if (!selectedTool || !selectedToolCapabilityState) {
            return null;
        }

        return <IntegrationDisclosure
            tool={selectedTool}
            content={content}
            isPlugin={shouldUsePluginWorkspaceUi}
        />;
    };

    const renderIntegrationCard = (tool: string) => {
        const connections = getToolConnections(tool);
        const activeConnections = connections.filter(isIntegrationConnectionActive);
        const connectionCount = activeConnections.length;
        const isToolSupported = isSupported(tool.toLowerCase().replace(/\s+/g, '_'));
        const isConnected = connectionCount > 0;

        // Get capabilities for preview
        const catalog = getCapabilityCatalog(tool, activeConnections[0], integrationPermissionCatalog, t);
        const readCapabilities = catalog.read
            .flatMap((section) => section.items)
            .map((item) => item.id)
            .filter(Boolean);
        const writeCapabilities = catalog.write
            .flatMap((section) => section.items)
            .map((item) => item.id)
            .filter(Boolean);
        const hasCapabilityPreview = readCapabilities.length > 0 || writeCapabilities.length > 0;
        const isCatalogCard = viewMode === 'catalog';
        const isConnectingTool = (syncingTools.has(tool) || (tool === "Shopify" && (shopifyInstallState === "checking" || shopifyEntryState === "checking"))) && !disconnectingTools.has(tool);
        const isInteractiveToolCard = isToolSupported;
        const isCustomMcpTool = isMcpTool(tool);
        const openToolDetails = () => openIntegrationDetail(tool);

        return (
            <div
                key={tool}
                {...(isInteractiveToolCard
                    ? {
                          role: 'button',
                          tabIndex: 0,
                          onKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  openToolDetails();
                              }
                          },
                          onClick: openToolDetails,
                      }
                    : {})}
                className={clsx(
                    'relative flex w-full flex-col rounded-2xl border bg-white text-left transition-[border-color,box-shadow] group',
                    isToolSupported
                        ? 'cursor-pointer border-[#E8EBF3] hover:border-[#B5BDD6] hover:shadow-[0_4px_16px_rgba(26,22,47,0.04)]'
                        : 'border-[#E8EBF3]',
                )}
            >
                <div className="flex w-full items-center justify-between gap-4 px-5 py-3">
                    <div className="flex items-center gap-3">
                        <div
                            className={clsx(
                                'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                                isCustomMcpTool ? 'bg-[#F4F5F8] text-[#1A162F]' : getBrandIconFrameClassName(tool),
                            )}
                        >
                            {isCustomMcpTool ? (
                                <Database size={20} weight="regular" />
                            ) : (
                                <img
                                    src={getBrandIcon(tool)}
                                    alt={tool}
                                    className={getBrandIconImageClassName(tool, 'h-5 w-5')}
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src = 'https://cdn.brandfetch.io/google.com/icon';
                                    }}
                                />
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <h3 className="text-sm font-medium leading-none text-[#1A162F]">{tool}</h3>
                            {isConnected ? (
                                <span className="inline-flex items-center rounded-full border border-[#CFE7D8] bg-[#EEF9F2] px-2 py-0.5 text-[10px] font-semibold leading-none text-[#1D7A45]">
                                    {t('website.integrationsSettings.connected')}
                                </span>
                            ) : null}
                        </div>
                    </div>
                    {isToolSupported && isCatalogCard ? (
                        <button
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                void handleConnect(tool);
                            }}
                            disabled={!canConnect || isConnectingTool}
                            title={!canConnect ? t('website.integrationsSettings.connectPermissionDenied') : undefined}
                            aria-label={t('website.integrationsSettings.connectApplication', { tool })}
                            aria-busy={isConnectingTool || undefined}
                            className={clsx(
                                'inline-flex min-h-9 shrink-0 items-center justify-center gap-1.5 rounded-[10px] bg-[#1A162F] px-3.5 py-2 text-xs font-medium text-white outline-none transition-colors hover:bg-[#2A2446] focus-visible:ring-2 focus-visible:ring-[#1A162F]/20 disabled:bg-[#D1D5DB]',
                                isConnectingTool ? 'cursor-wait' : 'disabled:cursor-not-allowed',
                            )}
                        >
                            {isConnectingTool ? <CircleNotch size={14} weight="regular" className="animate-spin" /> : null}
                            {t(
                                isConnectingTool
                                    ? 'website.integrationsSettings.connecting'
                                    : 'website.integrationsSettings.connect',
                            )}
                        </button>
                    ) : isToolSupported ? (
                        <div className="shrink-0">
                            <CaretRight size={16} weight="regular" className="text-[#A0A8C0] group-hover:text-[#1A162F]" />
                        </div>
                    ) : (
                        <span className="inline-flex shrink-0 rounded-full bg-[#F4F5F8] px-2.5 py-1 text-[11px] font-medium text-[#8A93B2]">
                            {t('website.integrationsSettings.soon')}
                        </span>
                    )}
                </div>

                {tool === "Shopify" && <div data-shopify-connection-status="card" ref={setShopifyStatusContainer} onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()} />}

                {/* Bottom Section: How it works Preview & Connected Accounts */}
                {isToolSupported && (
                    <div className="bg-white px-5 pb-4 pt-1 rounded-b-2xl flex flex-col gap-2.5">
                        <div className="flex flex-col gap-1">
                            <span className="text-[11px] font-medium text-[#8A93B2]">{t('website.integrationsSettings.howItWorks')}</span>
                            <p
                                className="overflow-hidden text-[13px] font-normal leading-5 text-[#666F8D]"
                                style={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                }}
                            >
                                {getToolDescription(t, tool, shouldUsePluginWorkspaceUi)}
                            </p>
                        </div>
                        {viewMode !== 'catalog' && connectionCount > 0 && (
                            <div className="pt-0.5 flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center gap-1.5">
                                    <span className="text-[11px] font-medium text-[#8A93B2]">{t('website.integrationsSettings.connectionsTab')}</span>
                                    <span className="inline-flex h-5 items-center justify-center rounded-full bg-[#F4F5F8] px-2 text-[10px] font-semibold text-[#666F8D]">
                                        <span className="leading-none">{connectionCount}</span>
                                    </span>
                                </div>
                                <div className="grid grid-cols-3 gap-2.5 w-full">
                                    {activeConnections.slice(0, 3).map((conn) => (
                                        <div key={conn.id} className="flex items-center justify-between gap-3 rounded-lg border border-[#E8EBF3] bg-white px-3 py-2">
                                            <div className="flex flex-col min-w-0">
                                                <span className="truncate text-xs font-medium text-[#1A162F]" title={conn.accountId}>
                                                    {conn.accountId}
                                                </span>
                                                {conn.alias && (
                                                    <span className="text-[10px] text-[#8A93B2] truncate mt-px" title={conn.alias}>
                                                        {conn.alias}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderMcpIntegrationDetail = () => {
        const activeMcpConnectors = customMcpConnectors.filter((connector) => mapMcpConnectorStatusToConnectionStatus(connector.status) === 'connected');

        return (
            <div className="space-y-6">
                <section className="rounded-2xl border border-[#E8EBF3] bg-white p-5 shadow-[0_10px_24px_rgba(26,22,47,0.06)]">
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex min-w-0 items-center gap-4">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#F4F5F8] text-[#1A162F]">
                                <Database size={24} weight="regular" />
                            </div>
                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <h2 className="text-xl font-bold text-text-main">{MCP_TOOL_NAME}</h2>
                                    {activeMcpConnectors.length > 0 ? (
                                        <span className="inline-flex items-center rounded-full border border-[#CFE7D8] bg-[#EEF9F2] px-2 py-0.5 text-[10px] font-semibold leading-none text-[#1D7A45]">
                                            {t('website.integrationsSettings.connected')}
                                        </span>
                                    ) : null}
                                </div>
                                <div className="mt-2 max-w-3xl space-y-1">
                                    <p className="text-[11px] font-medium text-[#8A93B2]">{t('website.integrationsSettings.howItWorks')}</p>
                                    <p className="text-sm leading-6 text-[#666F8D]">{getToolDescription(t, MCP_TOOL_NAME, shouldUsePluginWorkspaceUi)}</p>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={() => openMcpConnectorModal('url')}
                            disabled={!canConnect || isSavingMcpConnector || isMcpOAuthLoading}
                            title={!canConnect ? t('website.integrationsSettings.mcpPermissionRequired') : undefined}
                            className="flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {isMcpOAuthLoading ? <CircleNotch size={16} weight="regular" className="animate-spin" /> : <Plus size={16} weight="regular" />}
                            {t('website.integrationsSettings.addConnection')}
                        </button>
                    </div>
                </section>
                <section className="rounded-2xl border border-[#E8EBF3] bg-white p-6">
                    <IntegrationDisclosure tool={MCP_TOOL_NAME} content={content} isPlugin={shouldUsePluginWorkspaceUi} />
                </section>

                <section className="space-y-4 rounded-2xl border border-[#E8EBF3] bg-white p-6">
                    <div className="flex items-center justify-between gap-4">
                        <h3 className="text-sm font-medium text-[#1A162F]">{t('website.integrationsSettings.connectionsTab')}</h3>
                        {isLoadingMcpConnectors ? (
                            <span className="inline-flex items-center gap-2 text-xs font-medium text-[#666F8D]">
                                <CircleNotch size={14} className="animate-spin" />
                                {t('website.integrationsSettings.loadingConnectionAccess')}
                            </span>
                        ) : null}
                    </div>

                    {mcpConnectorError && !mcpModalMode ? (
                        <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm leading-5 text-red-700">{mcpConnectorError}</div>
                    ) : null}

                    {customMcpConnectors.length > 0 ? (
                        <div className="space-y-3">
                            {customMcpConnectors.map((connector) => {
                                const status = mapMcpConnectorStatusToConnectionStatus(connector.status);
                                const tools = connector.tools || [];
                                const toolCount = tools.length;
                                const isDisconnectingMcpConnector = disconnectingMcpConnectorIds.has(connector.connectorId);
                                const isRecoveringMcpConnector = recoveringMcpConnectorIds.has(connector.connectorId);
                                const canManageMcpConnector = canConnect && connector.canManage !== false;
                                const requiresOAuthRecovery = connector.credentialStatus === 'AUTH_REQUIRED' || connector.reasonCode === 'MCP_AUTH_REQUIRED';
                                const requiresLegacyQueryRepair = connector.reasonCode === 'MCP_LEGACY_QUERY_CREDENTIAL_REPAIR_REQUIRED';
                                const isTestingMcpConnector = testingMcpConnectorIds.has(connector.connectorId);
                                const isSyncingMcpConnector = syncingMcpConnectorIds.has(connector.connectorId);
                                const resourceContextUpdateKey = currentWorkspace?.id ? `${currentWorkspace.id}:${connector.connectorId}` : null;
                                const isSavingMcpResourceContext = Boolean(resourceContextUpdateKey && savingMcpResourceContextConnectorIds.has(resourceContextUpdateKey));
                                const supabaseProjectRef = mcpResourceContextDrafts[connector.connectorId] ?? connector.resourceContext?.project_id ?? '';
                                const isColumnUpdatingMcpTools = columnUpdatingMcpConnectorIds.has(connector.connectorId);
                                const isAnyMcpToolUpdating = tools.some((tool) => {
                                    const updateKey = getMcpToolUpdateKey(connector.connectorId, tool);
                                    return updatingMcpToolKeys.has(updateKey);
                                });
                                const areAllMcpToolsEnabled = tools.length > 0 && tools.every((tool) => Boolean(tool.enabled));
                                const areAllMcpToolsRequiringConfirmation = tools.length > 0 && tools.every((tool) => Boolean(tool.requireConfirmation));
                                return (
                                    <div key={connector.connectorId} className="space-y-4 rounded-xl border border-[#E8EBF3] bg-[#FCFCFC] p-4">
                                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                            <div className="min-w-0 space-y-1">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className="text-sm font-semibold text-[#1A162F]">{connector.displayName}</span>
                                                    <span
                                                        className={clsx(
                                                            'inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold leading-none',
                                                            status === 'connected'
                                                                ? 'bg-[#EEF9F2] text-[#1D7A45] ring-1 ring-[#CFE7D8]'
                                                                : status === 'error'
                                                                  ? 'bg-red-50 text-red-700 ring-1 ring-red-200'
                                                                  : 'bg-[#F4F5F8] text-[#666F8D] ring-1 ring-[#E8EBF3]',
                                                        )}
                                                    >
                                                        {connector.status}
                                                    </span>
                                                </div>
                                                <p className="truncate text-xs text-[#666F8D]" title={connector.serverUrl}>
                                                    {connector.serverUrl}
                                                </p>
                                            </div>
                                            <div className="flex shrink-0 flex-wrap items-center gap-2 text-xs font-medium text-[#666F8D]">
                                                <span>
                                                    {t('website.integrationsSettings.mcpToolCount', {
                                                        count: toolCount,
                                                        plural: toolCount === 1 ? '' : 's',
                                                    })}
                                                </span>
                                                {requiresOAuthRecovery || requiresLegacyQueryRepair ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => void handleMcpConnectorCredentialRecovery(connector)}
                                                        disabled={!canManageMcpConnector || isRecoveringMcpConnector || isMcpOAuthLoading}
                                                        title={!canManageMcpConnector ? t('website.integrationsSettings.mcpPermissionRequired') : undefined}
                                                        className="inline-flex items-center gap-1.5 rounded-lg border border-[#D9DDE8] bg-white px-2.5 py-1.5 text-xs font-medium text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-60"
                                                    >
                                                        {isRecoveringMcpConnector || isMcpOAuthLoading ? <CircleNotch size={14} className="animate-spin" /> : null}
                                                        {requiresOAuthRecovery
                                                            ? t('website.integrationsSettings.mcpReauthorize')
                                                            : t('website.integrationsSettings.mcpRepairUrlCredential')}
                                                    </button>
                                                ) : null}
                                                <button
                                                    type="button"
                                                    onClick={() => void handleMcpConnectorTest(connector)}
                                                    disabled={!canManageMcpConnector || isTestingMcpConnector}
                                                    title={!canManageMcpConnector ? t('website.integrationsSettings.mcpPermissionRequired') : undefined}
                                                    className="inline-flex items-center gap-1.5 rounded-lg border border-[#D9DDE8] bg-white px-2.5 py-1.5 text-xs font-medium text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-60"
                                                >
                                                    {isTestingMcpConnector ? <CircleNotch size={14} className="animate-spin" /> : null}
                                                    {t('website.integrationsSettings.mcpTest')}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => void handleMcpConnectorSync(connector)}
                                                    disabled={!canManageMcpConnector || isSyncingMcpConnector}
                                                    title={!canManageMcpConnector ? t('website.integrationsSettings.mcpPermissionRequired') : undefined}
                                                    className="inline-flex items-center gap-1.5 rounded-lg border border-[#D9DDE8] bg-white px-2.5 py-1.5 text-xs font-medium text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-60"
                                                >
                                                    {isSyncingMcpConnector ? <CircleNotch size={14} className="animate-spin" /> : null}
                                                    {t('website.integrationsSettings.mcpSync')}
                                                </button>
                                                {tools.length > 0 ? (
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setMcpToolManagerConnectorId((previous) => (previous === connector.connectorId ? null : connector.connectorId))
                                                        }
                                                        disabled={!canManageMcpConnector}
                                                        title={!canManageMcpConnector ? t('website.integrationsSettings.mcpPermissionRequired') : undefined}
                                                        className="inline-flex items-center gap-1.5 rounded-lg border border-[#D9DDE8] bg-white px-2.5 py-1.5 text-xs font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC] disabled:cursor-not-allowed disabled:opacity-60"
                                                    >
                                                        <SlidersHorizontal size={14} weight="regular" />
                                                        {mcpToolManagerConnectorId === connector.connectorId
                                                            ? t('website.integrationsSettings.mcpHideTools')
                                                            : t('website.integrationsSettings.mcpManageTools')}
                                                    </button>
                                                ) : null}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        void handleMcpConnectorDisconnect(connector);
                                                    }}
                                                    disabled={!canDisconnect || !canManageMcpConnector || isDisconnectingMcpConnector}
                                                    title={!canDisconnect || !canManageMcpConnector ? t('website.integrationsSettings.disconnectPermissionDenied') : undefined}
                                                    className="inline-flex items-center gap-1.5 rounded-lg border border-red-100 bg-white px-2.5 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                                                >
                                                    {isDisconnectingMcpConnector ? (
                                                        <CircleNotch size={14} weight="regular" className="animate-spin" />
                                                    ) : (
                                                        <Trash size={14} weight="regular" />
                                                    )}
                                                    {t('website.integrationsSettings.disconnect')}
                                                </button>
                                            </div>
                                        </div>
                                        {isSupabaseMcpServerUrl(connector.serverUrl) ? (
                                            <div className="rounded-lg border border-[#E8EBF3] bg-white p-3">
                                                <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
                                                    <div className="min-w-0 flex-1 space-y-1.5">
                                                        <label htmlFor={`mcp-supabase-project-ref-${connector.connectorId}`} className="block text-xs font-semibold text-[#1A162F]">
                                                            {t('website.integrationsSettings.mcpSupabaseProjectRef')}
                                                        </label>
                                                        <p className="text-[11px] leading-4 text-[#666F8D]">{t('website.integrationsSettings.mcpSupabaseProjectRefDescription')}</p>
                                                        <input
                                                            id={`mcp-supabase-project-ref-${connector.connectorId}`}
                                                            type="text"
                                                            value={supabaseProjectRef}
                                                            onChange={(event) =>
                                                                setMcpResourceContextDrafts((previous) => ({
                                                                    ...previous,
                                                                    [connector.connectorId]: event.target.value,
                                                                }))
                                                            }
                                                            placeholder="abcdefghijklmnop"
                                                            disabled={!canManageMcpConnector || isSavingMcpResourceContext}
                                                            aria-label={t('website.integrationsSettings.mcpSupabaseProjectRef')}
                                                            className="w-full rounded-lg border border-[#D9DDE8] bg-white px-3 py-2 text-sm text-[#1A162F] outline-none transition-colors placeholder:text-[#A0A8C0] focus:border-[#1A162F] disabled:cursor-not-allowed disabled:bg-[#F8F9FC]"
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => void handleMcpResourceContextUpdate(connector, supabaseProjectRef)}
                                                        disabled={!canManageMcpConnector || isSavingMcpResourceContext}
                                                        title={!canManageMcpConnector ? t('website.integrationsSettings.mcpPermissionRequired') : undefined}
                                                        className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-[#D9DDE8] bg-white px-3 text-xs font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC] disabled:cursor-not-allowed disabled:opacity-60"
                                                    >
                                                        {isSavingMcpResourceContext ? <CircleNotch size={14} className="animate-spin" /> : null}
                                                        {t('website.integrationsSettings.mcpSaveResourceContext')}
                                                    </button>
                                                </div>
                                            </div>
                                        ) : null}
                                        {mcpToolManagerConnectorId === connector.connectorId && tools.length > 0 ? (
                                            <div className="overflow-x-auto rounded-lg border border-[#E8EBF3] bg-white">
                                                <table className="min-w-full divide-y divide-[#E8EBF3] text-left text-xs">
                                                    <thead className="bg-[#F8F9FC] text-[#666F8D]">
                                                        <tr>
                                                            <th className="px-3 py-2 font-semibold">{t('website.integrationsSettings.mcpToolName')}</th>
                                                            <th className="px-3 py-2 font-semibold">
                                                                <div className="inline-flex items-center gap-2">
                                                                    <Checkbox
                                                                        checked={areAllMcpToolsEnabled}
                                                                        disabled={!canManageMcpConnector || isAnyMcpToolUpdating || isColumnUpdatingMcpTools}
                                                                        onCheckedChange={() => {
                                                                            void handleMcpToolBooleanColumnToggle(connector, 'enabled');
                                                                        }}
                                                                        aria-label={
                                                                            areAllMcpToolsEnabled
                                                                                ? t('website.integrationsSettings.mcpClearEnabledTools')
                                                                                : t('website.integrationsSettings.mcpSelectAllEnabledTools')
                                                                        }
                                                                    />
                                                                    <span>{t('website.integrationsSettings.mcpToolEnabled')}</span>
                                                                </div>
                                                            </th>
                                                            <th className="px-3 py-2 font-semibold">{t('website.integrationsSettings.mcpToolAccess')}</th>
                                                            <th className="px-3 py-2 font-semibold">{t('website.integrationsSettings.mcpToolRisk')}</th>
                                                            <th className="px-3 py-2 font-semibold">
                                                                <div className="inline-flex items-center gap-2">
                                                                    <Checkbox
                                                                        checked={areAllMcpToolsRequiringConfirmation}
                                                                        disabled={!canManageMcpConnector || isAnyMcpToolUpdating || isColumnUpdatingMcpTools}
                                                                        onCheckedChange={() => {
                                                                            void handleMcpToolBooleanColumnToggle(connector, 'requireConfirmation');
                                                                        }}
                                                                        aria-label={
                                                                            areAllMcpToolsRequiringConfirmation
                                                                                ? t('website.integrationsSettings.mcpClearAllConfirmations')
                                                                                : t('website.integrationsSettings.mcpSelectAllConfirmations')
                                                                        }
                                                                    />
                                                                    <span>{t('website.integrationsSettings.mcpToolRequireConfirmation')}</span>
                                                                </div>
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-[#E8EBF3]">
                                                        {tools.map((tool) => {
                                                            const updateKey = getMcpToolUpdateKey(connector.connectorId, tool);
                                                            const isUpdatingTool = isColumnUpdatingMcpTools || updatingMcpToolKeys.has(updateKey);
                                                            const toolDisplayName = getMcpToolDisplayName(tool);
                                                            const shouldShowInternalName = tool.name && tool.name !== toolDisplayName;
                                                            return (
                                                                <tr key={updateKey} className="align-middle">
                                                                    <td className="max-w-[280px] px-3 py-2">
                                                                        <div className="truncate font-medium text-[#1A162F]" title={toolDisplayName}>
                                                                            {toolDisplayName}
                                                                        </div>
                                                                        {shouldShowInternalName ? (
                                                                            <div className="mt-0.5 truncate font-mono text-[10px] leading-4 text-[#8A93B2]" title={tool.name}>
                                                                                {tool.name}
                                                                            </div>
                                                                        ) : null}
                                                                        {tool.description ? (
                                                                            <div className="mt-0.5 line-clamp-2 text-[11px] leading-4 text-[#666F8D]" title={tool.description}>
                                                                                {tool.description}
                                                                            </div>
                                                                        ) : null}
                                                                    </td>
                                                                    <td className="px-3 py-2">
                                                                        <Checkbox
                                                                            checked={Boolean(tool.enabled)}
                                                                            disabled={!canManageMcpConnector || isUpdatingTool}
                                                                            onCheckedChange={(checked) =>
                                                                                handleMcpToolUpdate(connector, tool, {
                                                                                    enabled: checked === true,
                                                                                })
                                                                            }
                                                                            aria-label={t('website.integrationsSettings.mcpToolEnabled')}
                                                                        />
                                                                    </td>
                                                                    <td className="px-3 py-2">
                                                                        <select
                                                                            value={tool.accessLevelRequired || 'WRITE'}
                                                                            disabled={!canManageMcpConnector || isUpdatingTool}
                                                                            onChange={(event) =>
                                                                                handleMcpToolUpdate(connector, tool, {
                                                                                    accessLevelRequired: event.target.value as NonNullable<
                                                                                        McpConnectorToolUpdateRequest['accessLevelRequired']
                                                                                    >,
                                                                                })
                                                                            }
                                                                            className="h-8 rounded-lg border border-[#D9DDE8] bg-white px-2 text-xs font-medium text-[#1A162F] outline-none focus:border-[#1A162F]"
                                                                        >
                                                                            {mcpToolAccessOptions.map((option) => (
                                                                                <option key={option} value={option}>
                                                                                    {option}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    </td>
                                                                    <td className="px-3 py-2">
                                                                        <select
                                                                            value={tool.riskLevel || 'MEDIUM'}
                                                                            disabled={!canManageMcpConnector || isUpdatingTool}
                                                                            onChange={(event) =>
                                                                                handleMcpToolUpdate(connector, tool, {
                                                                                    riskLevel: event.target.value as NonNullable<McpConnectorToolUpdateRequest['riskLevel']>,
                                                                                })
                                                                            }
                                                                            className="h-8 rounded-lg border border-[#D9DDE8] bg-white px-2 text-xs font-medium text-[#1A162F] outline-none focus:border-[#1A162F]"
                                                                        >
                                                                            {mcpToolRiskOptions.map((option) => (
                                                                                <option key={option} value={option}>
                                                                                    {option}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    </td>
                                                                    <td className="px-3 py-2">
                                                                        <Checkbox
                                                                            checked={Boolean(tool.requireConfirmation)}
                                                                            disabled={!canManageMcpConnector || isUpdatingTool}
                                                                            onCheckedChange={(checked) =>
                                                                                handleMcpToolUpdate(connector, tool, {
                                                                                    requireConfirmation: checked === true,
                                                                                })
                                                                            }
                                                                            aria-label={t('website.integrationsSettings.mcpToolRequireConfirmation')}
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="rounded-xl border border-dashed border-[#E8EBF3] bg-[#FCFCFC] px-4 py-8 text-center text-sm text-[#666F8D]">
                            {t('website.integrationsSettings.mcpNoConnectors')}
                        </div>
                    )}
                </section>

                {customMcpConnectors.length > 0 ? (
                    <section className="space-y-4 rounded-2xl border border-[#E8EBF3] bg-white p-6">
                        <h3 className="text-sm font-medium text-[#1A162F]">{t('website.integrationsSettings.mcpMemberAccess')}</h3>
                        <ConnectionAccessSettings
                            key={`${MCP_TOOL_NAME}-mcp-connection-access`}
                            brandId={brandId}
                            brandConnectionIds={brandConnectionIds}
                            canManageBrand={canManageBrand}
                            canRemoveConnectionsFromBrand={canRemoveConnectionsFromBrand}
                            brandResources={brandResources}
                            projectBootstrap={projectBootstrap}
                            showHeader={false}
                            providerFilter={MCP_TOOL_NAME}
                            usePluginWorkspaceContext={shouldUsePluginWorkspaceContext}
                            connectionScene={mode}
                            onConnectionDisconnected={() => loadMcpConnectors().then(() => undefined)}
                            accessRefreshKey={connectionAccessRefreshKey}
                            pendingBusinessGroupAssignmentResourceKeys={pendingBusinessGroupAssignmentResourceKeys}
                            onSetMemberAccess={onSetMemberAccess}
                            canSetMemberAccess={canSetMemberAccess}
                            getSetMemberAccessDisabledReason={getSetMemberAccessDisabledReason}
                        />
                    </section>
                ) : null}
            </div>
        );
    };

    const renderMcpAuthorizationDetail = () => (
        <section className="rounded-2xl border border-[#E8EBF3] bg-white p-5 shadow-[0_10px_24px_rgba(26,22,47,0.06)]">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex min-w-0 items-center gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#F4F5F8] text-[#1A162F]">
                        <Database size={24} weight="regular" />
                    </div>
                    <div className="min-w-0">
                        <h2 className="text-xl font-medium text-[#1A162F]">{MCP_TOOL_NAME}</h2>
                        <p className="mt-2 max-w-3xl text-sm font-normal leading-6 text-[#666F8D]">{getToolDescription(t, MCP_TOOL_NAME, shouldUsePluginWorkspaceUi)}</p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => openMcpConnectorModal('url')}
                    disabled={!canConnect || isSavingMcpConnector || isMcpOAuthLoading}
                    title={!canConnect ? t('website.integrationsSettings.mcpPermissionRequired') : undefined}
                    className="flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-[#D1D5DB]"
                >
                    {isMcpOAuthLoading ? <CircleNotch size={16} weight="regular" className="animate-spin" /> : <Plus size={16} weight="regular" />}
                    {t('website.integrationsSettings.addConnection')}
                </button>
            </div>
            <div className="mt-6 border-t border-[#E8EBF3] pt-6">
                <IntegrationDisclosure tool={MCP_TOOL_NAME} content={content} isPlugin={shouldUsePluginWorkspaceUi} />
            </div>
        </section>
    );

    const renderIntegrationDetail = () => {
        if (!selectedTool || !selectedToolCapabilityState) {
            return null;
        }

        if (isMcpTool(selectedTool)) {
            return viewMode === 'catalog' && customMcpConnectors.length === 0
                ? renderMcpAuthorizationDetail()
                : renderMcpIntegrationDetail();
        }

        const normalizedTool = selectedTool.toLowerCase().replace(/\s+/g, '_');
        const isToolSupported = isSupported(normalizedTool);
        const hasConnections = selectedToolDisplayConnections.length > 0;
        const unavailableKlaviyoConnection = selectedTool === 'Klaviyo' ? selectedToolDisplayConnections.find((connection) => connection.isUnavailable) : null;
        const isSyncing = syncingTools.has(selectedTool) || (selectedTool === "Shopify" && (shopifyInstallState === "checking" || shopifyEntryState === "checking"));
        const isDisconnectingSelectedTool = disconnectingTools.has(selectedTool);
        const startedConnectionCount = connectStartConnectionCountsRef.current.get(selectedTool);
        const hasConnectionCreatedDuringSync = startedConnectionCount !== undefined && selectedToolDisplayConnections.length > startedConnectionCount;
        const canAddConnections = canConnect;
        const showFirstConnectionAction = isToolSupported && !hasConnections;
        const showPrimaryAction = showFirstConnectionAction || (canConnect && canAddConnections);
        const showApiKeyFallbackAction = API_KEY_FALLBACK_TOOLS.has(selectedTool) && isToolSupported && canConnect && (!hasConnections || canAddConnections);
        const isPrimaryActionBusy = isSyncing && !isDisconnectingSelectedTool && !hasConnectionCreatedDuringSync;
        const primaryActionLabel = isPrimaryActionBusy
            ? t('website.integrationsSettings.connecting')
            : hasConnections
              ? t('website.integrationsSettings.addConnection')
              : isToolSupported
                ? t('website.integrationsSettings.connect')
                : t('website.integrationsSettings.soon');
        const handlePrimaryActionClick = () => {
            void handleConnect(selectedTool);
        };

        return (
            <div className="space-y-6">
                <section className="rounded-2xl border border-[#E8EBF3] bg-white p-5 shadow-[0_10px_24px_rgba(26,22,47,0.06)]">
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex min-w-0 items-center gap-4">
                            <div className={clsx('flex h-12 w-12 shrink-0 items-center justify-center rounded-xl', getBrandIconFrameClassName(selectedTool))}>
                                <img
                                    src={getBrandIcon(selectedTool)}
                                    alt={selectedTool}
                                    className={getBrandIconImageClassName(selectedTool, 'h-6 w-6')}
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src = 'https://cdn.brandfetch.io/google.com/icon';
                                    }}
                                />
                            </div>

                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <h2 className="text-xl font-bold text-text-main">{selectedTool}</h2>
                                    {unavailableKlaviyoConnection ? (
                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-medium tracking-[0.02em] text-amber-800 ring-1 ring-amber-500/25">
                                            {t('website.integrationsSettings.attentionNeeded')}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>

                        <div className="flex shrink-0 items-center gap-4">
                            {!hasConnections && isToolSupported && canConnect ? (
                                <button
                                    onClick={() => handleRegister(selectedTool)}
                                    className="text-xs font-medium text-[#667085] transition-colors hover:text-[#1A162F] hover:underline"
                                >
                                    {t('website.integrationsSettings.dontHaveAccountSignUp')}
                                </button>
                            ) : null}
                            {showPrimaryAction ? (
                                <button
                                    onClick={handlePrimaryActionClick}
                                    disabled={!isToolSupported || isPrimaryActionBusy}
                                    className={clsx(
                                        'flex items-center justify-center gap-2 rounded-[10px] px-4 py-2.5 text-sm font-medium shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors',
                                        isToolSupported
                                            ? 'bg-[#1A162F] text-white hover:bg-primary/90 disabled:cursor-wait disabled:opacity-70'
                                            : 'cursor-not-allowed bg-[#F4F5F8] text-[#8A93B2]',
                                    )}
                                >
                                    {isPrimaryActionBusy ? (
                                        <CircleNotch size={16} weight="regular" className="animate-spin" />
                                    ) : hasConnections ? (
                                        <Plus size={16} weight="regular" />
                                    ) : null}
                                    {primaryActionLabel}
                                </button>
                            ) : null}
                            {showApiKeyFallbackAction ? (
                                <button
                                    type="button"
                                    onClick={() => handleApiKeyFallbackConnection(selectedTool)}
                                    disabled={isPrimaryActionBusy}
                                    className="text-xs font-medium text-[#667085] transition-colors hover:text-[#1A162F] hover:underline disabled:cursor-wait disabled:opacity-60"
                                >
                                    {t('website.integrationsSettings.connectWithApiKey')}
                                </button>
                            ) : null}
                        </div>
                    </div>
                    {selectedTool === "Shopify" && <div data-shopify-connection-status="detail" ref={setShopifyStatusContainer} />}
                </section>

                {unavailableKlaviyoConnection ? (
                    <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
                        <h3 className="text-sm font-semibold text-amber-900">{t('website.integrationsSettings.klaviyoUnavailable')}</h3>
                        <p className="mt-2 text-sm leading-6 text-amber-800">
                            {unavailableKlaviyoConnection.statusMessage || t('website.integrationsSettings.klaviyoUnavailableDescription')}
                        </p>
                    </section>
                ) : null}

                <section className="space-y-5 rounded-2xl border border-[#E8EBF3] bg-white p-6">
                    <div className="flex min-h-[42px] items-end justify-between gap-4 border-b border-[#ECEFF5]">
                        <div className="flex h-full items-end gap-6">
                            <button
                                onClick={() => setSelectedDetailTab('permissions')}
                                className={clsx(
                                    'relative pb-3 text-sm font-medium transition-colors',
                                    selectedDetailTab === 'permissions' ? 'text-[#1A162F]' : 'text-[#666F8D] hover:text-[#1A162F]',
                                )}
                            >
                                {t('website.integrationsSettings.descriptionTab')}
                                {selectedDetailTab === 'permissions' ? <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-[#1A162F]" /> : null}
                            </button>
                            {viewMode !== 'catalog' && hasConnections ? (
                                <button
                                    onClick={() => setSelectedDetailTab('connections')}
                                    className={clsx(
                                        'relative flex items-center gap-1.5 pb-3 text-sm font-medium transition-colors',
                                        selectedDetailTab === 'connections' ? 'text-[#1A162F]' : 'text-[#666F8D] hover:text-[#1A162F]',
                                    )}
                                >
                                    <span>{t('website.integrationsSettings.connectionsTab')}</span>
                                    <span
                                        className={clsx(
                                            'inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1.5 text-[10px] font-medium leading-none transition-colors',
                                            selectedDetailTab === 'connections' ? 'bg-[#1A162F]/10 text-[#1A162F]' : 'bg-[#F4F5F8] text-[#8A93B2]',
                                        )}
                                    >
                                        {selectedToolConnections.length}
                                    </span>
                                    {selectedDetailTab === 'connections' ? <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-[#1A162F]" /> : null}
                                </button>
                            ) : null}
                        </div>
                    </div>

                    <div className="min-h-[420px]">
                        {viewMode !== 'catalog' && selectedDetailTab === 'connections' && hasConnections ? (
                            <div className="space-y-4">
                                <ConnectionAccessSettings
                                    key={`${selectedTool}-detail-connections`}
                                    brandId={brandId}
                                    brandConnectionIds={brandConnectionIds}
                                    canManageBrand={canManageBrand}
                                    canRemoveConnectionsFromBrand={canRemoveConnectionsFromBrand}
                                    brandResources={brandResources}
                                    projectBootstrap={projectBootstrap}
                                    showHeader={false}
                                    providerFilter={selectedTool}
                                    usePluginWorkspaceContext={shouldUsePluginWorkspaceContext}
                                    connectionScene={mode}
                                    accessRefreshKey={connectionAccessRefreshKey}
                                    pendingBusinessGroupAssignmentResourceKeys={pendingBusinessGroupAssignmentResourceKeys}
                                    onConnectionDisconnected={selectedTool === 'DingTalk' ? refreshMcpConnectorsAfterDisconnect : undefined}
                                    onSetMemberAccess={onSetMemberAccess}
                                    canSetMemberAccess={canSetMemberAccess}
                                    getSetMemberAccessDisabledReason={getSetMemberAccessDisabledReason}
                                />
                            </div>
                        ) : (
                            renderPermissionSection()
                        )}
                    </div>
                </section>
            </div>
        );
    };

    if (viewMode === 'connected') {
        return (
            <div ref={rootRef}>
                <ConnectionAccessSettings
                    key="brand-connected-connection-cards"
                    brandId={brandId}
                    brandConnectionIds={brandConnectionIds}
                    canManageBrand={canManageBrand}
                    canRemoveConnectionsFromBrand={canRemoveConnectionsFromBrand}
                    brandResources={brandResources}
                    projectBootstrap={projectBootstrap}
                    showHeader={false}
                    showScopeSwitcher={Boolean(brandId) && !hideConnectionScopeFilter}
                    connectionFilter={connectionFilter}
                    onConnectionFilterChange={setConnectionFilter}
                    onBrowseIntegrations={onBrowseIntegrations}
                    usePluginWorkspaceContext={shouldUsePluginWorkspaceContext}
                    connectionScene={mode}
                    accessRefreshKey={connectionAccessRefreshKey}
                    pendingBusinessGroupAssignmentResourceKeys={pendingBusinessGroupAssignmentResourceKeys}
                    onSetMemberAccess={onSetMemberAccess}
                    canSetMemberAccess={canSetMemberAccess}
                    getSetMemberAccessDisabledReason={getSetMemberAccessDisabledReason}
                />
            </div>
        );
    }

    return (
        <div ref={rootRef} className={clsx('space-y-6', scrollableCatalog && 'flex h-full min-h-0 flex-col space-y-0')}>
            {content.status === 'error' && !selectedTool ? (
                <IntegrationContentNotice status="error" zh={locale === 'zh-CN'} onRetry={content.retry} />
            ) : null}
            {/* Unified Compact Header */}
            {!hideHeader ? (
                <div className="flex flex-col gap-5 pb-2">
                    {selectedTool ? (
                        <button
                            type="button"
                            onClick={closeIntegrationDetail}
                            className="inline-flex items-center gap-3 self-start text-left text-[#1A162F] transition-colors hover:text-[#1A162F]/80"
                        >
                            <CaretLeft size={24} weight="regular" />
                            <span className="text-[20px] font-medium leading-none tracking-[-0.01em]">{selectedTool}</span>
                        </button>
                    ) : (
                        <>
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                                <div>
                                    <h2 className="text-[20px] font-medium tracking-[-0.01em] text-[#1A162F]">{t('website.integrationsSettings.integrations')}</h2>
                                </div>
                                {mode === 'admin' && (
                                    <div className="flex w-full flex-wrap items-center justify-end gap-3 lg:w-auto">
                                        {!scrollableCatalog && !brandId ? (
                                            <div
                                                id={WORKSPACE_CONNECTED_APP_ASSIGNMENT_ACTION_TARGET_ID}
                                                className="flex shrink-0 items-center"
                                            />
                                        ) : null}
                                        <div className="relative min-w-[220px] flex-1 sm:flex-none sm:w-[260px] lg:w-[300px]">
                                            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A93B2]" size={16} weight="regular" />
                                            <input
                                                ref={searchInputRef}
                                                type="text"
                                                placeholder={t('website.integrationsSettings.searchIntegrations')}
                                                className="w-full rounded-[10px] border border-[#E8EBF3] bg-white py-2 pl-9 pr-10 text-sm font-normal text-[#1A162F] focus:outline-none transition-all placeholder:text-[#8A93B2] focus:border-[#1A162F] focus:ring-1 focus:ring-[#1A162F]"
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                            />
                                            {hasSearchQuery ? (
                                                <button
                                                    type="button"
                                                    onClick={clearSearch}
                                                    aria-label={t('website.integrationsSettings.clearSearch')}
                                                    title={t('website.integrationsSettings.clearSearch')}
                                                    className="absolute right-3 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-[#A0A8C0] transition-colors hover:text-[#1A162F]"
                                                >
                                                    <XCircle size={16} weight="fill" />
                                                </button>
                                            ) : null}
                                        </div>
                                    </div>
                                )}
                            </div>
                            {mode === 'admin' && viewMode === 'full' && !brandId ? (
                                <div data-connection-trust-section="true" className="mt-3">
                                    <ConnectionTrustTags
                                        isPlugin={shouldUsePluginWorkspaceUi}
                                        showPrivacyLink={false}
                                        badges={[
                                            {
                                                id: 'infrastructure',
                                                label: t('website.onboarding.connect.trustBadge2Tag'),
                                                description: t('website.onboarding.connect.trustBadge2Desc'),
                                                icon: <CloudCheck size={15} weight="regular" />,
                                            },
                                            {
                                                id: 'platform-partner',
                                                label: t('website.onboarding.connect.trustBadge4Tag'),
                                                description: t(isPluginAdmin ? 'website.onboarding.connect.pluginTrustBadge4Desc' : 'website.onboarding.connect.trustBadge4Desc'),
                                                icon: <SealCheck size={15} weight="regular" />,
                                            },
                                            {
                                                id: 'local-task-information',
                                                label: t(isPluginAdmin
                                                    ? 'website.onboarding.connect.pluginTrustBadge3Tag'
                                                    : 'website.onboarding.connect.trustBadge3Tag'),
                                                description: t(isPluginAdmin ? 'website.onboarding.connect.pluginTrustBadge3Desc' : 'website.onboarding.connect.trustBadge3Desc'),
                                                icon: <HardDrive size={15} weight="regular" />,
                                            },
                                        ]}
                                    />
                                </div>
                            ) : null}
                        </>
                    )}
                </div>
            ) : selectedTool ? (
                scrollableCatalog ? null : (
                    <button
                        type="button"
                        onClick={closeIntegrationDetail}
                        className="inline-flex items-center gap-3 self-start text-left text-[#1A162F] transition-colors hover:text-[#1A162F]/80"
                    >
                        <CaretLeft size={20} weight="regular" />
                        <span className="text-[18px] font-medium leading-none tracking-[-0.01em]">{selectedTool}</span>
                    </button>
                )
            ) : scrollableCatalog ? null : (
                <div className={clsx('flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between', scrollableCatalog && 'mb-5 shrink-0')}>
                    {showBrandCatalogConnectionUsage && brandCatalogConnectionUsage ? (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-normal text-[#8A93B2]">
                            {connectRestrictionCopyContext.storeLimit !== null ? (
                                <span>
                                    {t('website.integrationsSettings.brandStoreConnectionUsage', {
                                        current: brandCatalogConnectionUsage.storeConnections,
                                        limit: connectRestrictionCopyContext.storeLimit,
                                    })}
                                </span>
                            ) : null}
                            {connectRestrictionCopyContext.totalIntegrationLimit !== null ? (
                                <span className="border-l border-[#E8EBF3] pl-4">
                                    {t('website.integrationsSettings.brandTotalConnectionUsage', {
                                        current: brandCatalogConnectionUsage.totalConnections,
                                        limit: connectRestrictionCopyContext.totalIntegrationLimit,
                                    })}
                                </span>
                            ) : null}
                        </div>
                    ) : null}
                    <div className="relative w-full sm:w-[380px] lg:w-[420px]">
                        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A93B2]" size={16} weight="regular" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder={t('website.integrationsSettings.searchIntegrations')}
                            className="w-full rounded-[10px] border border-[#E8EBF3] bg-white py-2 pl-9 pr-10 text-sm font-normal text-[#1A162F] transition-all placeholder:text-[#8A93B2] focus:border-[#1A162F] focus:outline-none focus:ring-1 focus:ring-[#1A162F]"
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                        />
                        {hasSearchQuery ? (
                            <button
                                type="button"
                                onClick={clearSearch}
                                aria-label={t('website.integrationsSettings.clearSearch')}
                                title={t('website.integrationsSettings.clearSearch')}
                                className="absolute right-3 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-[#A0A8C0] transition-colors hover:text-[#1A162F]"
                            >
                                <XCircle size={16} weight="fill" />
                            </button>
                        ) : null}
                    </div>
                    {!brandId ? (
                        <div
                            id="workspace-connected-app-assignment-action"
                            className="flex shrink-0 items-center"
                        />
                    ) : null}
                </div>
            )}

            {selectedTool ? (
                <div
                    className={clsx(
                        !scrollableCatalog && 'animate-in fade-in slide-in-from-bottom-2 duration-300',
                        scrollableCatalog
                            ? 'flex h-full min-h-0 flex-col overflow-hidden bg-white'
                            : '',
                    )}
                >
                    {scrollableCatalog ? (
                        <div className="flex shrink-0 items-center justify-between border-b border-[#E8EBF3] px-6 py-4 bg-white">
                            <button
                                type="button"
                                onClick={closeIntegrationDetail}
                                className="inline-flex items-center gap-2 text-sm font-medium text-[#1A162F] transition-colors hover:text-[#666F8D]"
                            >
                                <CaretLeft size={18} weight="regular" />
                                <span>{selectedTool}</span>
                            </button>
                            {onCloseModal ? (
                                <button
                                    type="button"
                                    onClick={onCloseModal}
                                    aria-label={t('common.close')}
                                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[#7B849D] transition-colors hover:bg-[#F3F5F8] hover:text-[#1A162F]"
                                >
                                    <X size={20} weight="regular" />
                                </button>
                            ) : null}
                        </div>
                    ) : null}
                    <div
                        className={clsx(
                            scrollableCatalog &&
                                'min-h-0 flex-1 overflow-y-scroll [scrollbar-gutter:stable] [overflow-anchor:none] integrations-apps-scroll-area',
                        )}
                    >
                        <div className={clsx(scrollableCatalog && 'p-6 pr-4')}>{renderIntegrationDetail()}</div>
                    </div>
                </div>
            ) : (
                <div
                    className={clsx(
                        'flex flex-col md:flex-row',
                        !scrollableCatalog && 'animate-in fade-in slide-in-from-bottom-2 duration-300',
                        scrollableCatalog
                            ? 'min-h-0 flex-1 overflow-hidden bg-white md:gap-0'
                            : 'gap-6',
                        scrollableCatalog ? 'md:items-stretch' : 'md:items-start',
                    )}
                >
                    <aside
                        aria-label={t('website.integrationsSettings.integrations')}
                        className={clsx(
                            'w-full shrink-0 md:w-[224px]',
                            scrollableCatalog ? 'flex min-h-0 flex-col bg-[#FCFCFD] md:w-[252px]' : 'md:sticky md:top-6',
                        )}
                    >
                        {scrollableCatalog ? (
                            <div className="shrink-0 border-b border-[#E8EBF3] px-5 py-5">
                                <h3 className="text-base font-medium text-[#1A162F]">{t('website.integrationsSettings.integrations')}</h3>
                                {showBrandCatalogConnectionUsage && brandCatalogConnectionUsage ? (
                                    <div className="mt-3 space-y-1 text-xs font-normal leading-5 text-[#8A93B2]">
                                        <p>{t('website.integrationsSettings.brandWorkspaceConnectionUsageLabel')}</p>
                                        {connectRestrictionCopyContext.storeLimit !== null ? (
                                            <p>
                                                {t('website.integrationsSettings.brandStoreConnectionUsage', {
                                                    current: brandCatalogConnectionUsage.storeConnections,
                                                    limit: connectRestrictionCopyContext.storeLimit,
                                                })}
                                            </p>
                                        ) : null}
                                        {connectRestrictionCopyContext.totalIntegrationLimit !== null ? (
                                            <p>
                                                {t('website.integrationsSettings.brandTotalConnectionUsage', {
                                                    current: brandCatalogConnectionUsage.totalConnections,
                                                    limit: connectRestrictionCopyContext.totalIntegrationLimit,
                                                })}
                                            </p>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                        <nav
                            aria-label={t('website.integrationsSettings.integrations')}
                            className={clsx(
                                'flex gap-1',
                                scrollableCatalog ? 'min-h-0 flex-1 flex-col overflow-y-auto px-3 py-3' : 'overflow-x-auto pb-1 md:flex-col md:overflow-visible',
                            )}
                        >
                            {viewMode !== 'catalog' ? (
                                <div className="flex shrink-0 items-center gap-1 md:block">
                                    <span className="shrink-0 px-3 text-[11px] font-medium text-[#8A93B2] md:mb-1 md:block">
                                        {t('website.integrationsSettings.connectedFilter')}
                                    </span>
                                    <div className="flex gap-1 md:flex-col">
                                        {connectionScopeFilters.map(({ scope, labelKey, count }) => {
                                            const isActive = selectedCategory === 'Connected' && connectionFilter === scope && !hasSearchQuery;

                                            return (
                                                <button
                                                    key={scope}
                                                    type="button"
                                                    aria-current={isActive ? 'page' : undefined}
                                                    onClick={() => {
                                                        setSearchQuery('');
                                                        setSelectedCategory('Connected');
                                                        setConnectionFilter(scope);
                                                    }}
                                                    className={clsx(
                                                        'flex min-h-10 shrink-0 items-center rounded-[10px] px-3 py-2 text-left text-sm font-normal transition-colors md:w-full',
                                                        isActive ? 'bg-[#E6E7EA] text-[#1A162F]' : 'text-[#666F8D] hover:bg-[#F4F5F8] hover:text-[#1A162F]',
                                                    )}
                                                >
                                                    <span className="min-w-0 flex-1 truncate">{t(labelKey)}</span>
                                                    <span className={clsx(
                                                        'ml-2 inline-flex min-w-6 justify-end text-xs tabular-nums',
                                                        isActive ? 'text-[#666F8D]' : 'text-[#8A93B2]',
                                                    )}>
                                                        {count ?? null}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : null}
                            {viewMode === 'full' ? (
                                <span className="mt-3 shrink-0 px-3 text-[11px] font-medium text-[#8A93B2] md:mb-1 md:block">
                                    {t('website.integrationsSettings.addIntegrations')}
                                </span>
                            ) : null}
                            {visibleScenarioFilters
                                .filter((category) => category.id !== 'Connected')
                                .map((category) => {
                                    const isActive = selectedCategory === category.id && !hasSearchQuery;
                                    const categoryToolCount = scenarioSections.find((section) => section.category === category.id)?.tools.length || 0;

                                    return (
                                        <button
                                            key={category.id}
                                            type="button"
                                            aria-current={isActive ? 'page' : undefined}
                                            onClick={() => selectCatalogCategory(category.id)}
                                            className={clsx(
                                                'flex min-h-10 shrink-0 items-center gap-2 rounded-[10px] px-3 py-2 text-left text-sm font-normal transition-colors md:w-full',
                                                isActive ? 'bg-[#E6E7EA] text-[#1A162F]' : 'text-[#666F8D] hover:bg-[#F4F5F8] hover:text-[#1A162F]',
                                            )}
                                        >
                                            <span className="min-w-0 flex-1 truncate">{localizedScenarioLabels[category.id]}</span>
                                            <span
                                                className={clsx(
                                                    'flex min-h-4 min-w-4 items-center justify-center text-xs font-normal tabular-nums',
                                                    isActive ? 'text-[#666F8D]' : 'text-[#8A93B2]',
                                                )}
                                            >
                                                {categoryToolCount}
                                            </span>
                                        </button>
                                    );
                                })}
                        </nav>
                    </aside>

                    <section
                        id="integrations-list"
                        className={clsx('min-w-0 flex-1', scrollableCatalog && 'flex min-h-0 flex-col border-t border-[#E8EBF3] md:border-l md:border-t-0')}
                    >
                        {scrollableCatalog ? (
                            <div className="shrink-0 border-b border-[#E8EBF3] px-5 py-4">
                                <div className="flex items-center justify-between gap-4">
                                    <div className="relative flex-1">
                                        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A93B2]" size={16} weight="regular" />
                                        <input
                                            ref={searchInputRef}
                                            type="text"
                                            placeholder={t('website.integrationsSettings.searchIntegrations')}
                                            className="w-full rounded-[10px] border border-[#E8EBF3] bg-white py-2 pl-9 pr-10 text-sm font-normal text-[#1A162F] transition-all placeholder:text-[#8A93B2] focus:border-[#1A162F] focus:outline-none focus:ring-1 focus:ring-[#1A162F]"
                                            value={searchQuery}
                                            onChange={(event) => setSearchQuery(event.target.value)}
                                        />
                                        {hasSearchQuery ? (
                                            <button
                                                type="button"
                                                onClick={clearSearch}
                                                aria-label={t('website.integrationsSettings.clearSearch')}
                                                title={t('website.integrationsSettings.clearSearch')}
                                                className="absolute right-3 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-[#A0A8C0] transition-colors hover:text-[#1A162F]"
                                            >
                                                <XCircle size={16} weight="fill" />
                                            </button>
                                        ) : null}
                                    </div>
                                    {!brandId ? (
                                        <div
                                            id={WORKSPACE_CONNECTED_APP_ASSIGNMENT_ACTION_TARGET_ID}
                                            className="flex shrink-0 items-center"
                                        />
                                    ) : null}
                                    {onCloseModal ? (
                                        <button
                                            type="button"
                                            onClick={onCloseModal}
                                            aria-label={t('common.close')}
                                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[#7B849D] transition-colors hover:bg-[#F3F5F8] hover:text-[#1A162F]"
                                        >
                                            <X size={20} weight="regular" />
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        ) : null}
                        <div
                            ref={scrollableCatalog ? catalogScrollAreaRef : undefined}
                            className={clsx(
                                scrollableCatalog &&
                                    'min-h-0 flex-1 overflow-y-scroll [scrollbar-gutter:stable] [overflow-anchor:none] integrations-apps-scroll-area',
                            )}
                        >
                            <div className={clsx(scrollableCatalog && 'p-5 pr-4')}>
                            {selectedCategory === 'Connected' && !hasSearchQuery ? (
                                <ConnectionAccessSettings
                                    key="connected-connection-cards"
                                    brandId={brandId}
                                    brandConnectionIds={brandConnectionIds}
                                    canManageBrand={canManageBrand}
                                    canRemoveConnectionsFromBrand={canRemoveConnectionsFromBrand}
                                    brandResources={brandResources}
                                    projectBootstrap={projectBootstrap}
                                    showHeader={false}
                                    connectionFilter={connectionFilter}
                                    onConnectionScopeCountsChange={
                                        brandId ? undefined : setConnectionScopeCounts
                                    }
                                    onBrowseIntegrations={() => {
                                        setSearchQuery('');
                                        selectCatalogCategory('Store Ops');
                                    }}
                                    searchQuery={searchQuery}
                                    brandAssignmentActionTargetId={brandId ? undefined : WORKSPACE_CONNECTED_APP_ASSIGNMENT_ACTION_TARGET_ID}
                                    usePluginWorkspaceContext={shouldUsePluginWorkspaceContext}
                                    connectionScene={mode}
                                    pendingBusinessGroupAssignmentResourceKeys={pendingBusinessGroupAssignmentResourceKeys}
                                    accessRefreshKey={connectionAccessRefreshKey}
                                    onSetMemberAccess={onSetMemberAccess}
                                    canSetMemberAccess={canSetMemberAccess}
                                    getSetMemberAccessDisabledReason={getSetMemberAccessDisabledReason}
                                />
                            ) : (
                                <>
                                    {hasSearchQuery ? (
                                        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1">
                                            <h3 className="text-sm font-medium text-[#1A162F]">{t('website.integrationsSettings.searchResults')}</h3>
                                            <span className="text-sm font-normal text-[#666F8D]">
                                                {t('website.integrationsSettings.searchResultsSummary', {
                                                    count: visibleSearchTools.length,
                                                    plural: visibleSearchTools.length === 1 ? '' : 'es',
                                                })}
                                            </span>
                                        </div>
                                    ) : null}

                                    {selectedListTools.length > 0 ? (
                                        <div className="flex flex-col gap-4">{selectedListTools.map((tool) => renderIntegrationCard(tool))}</div>
                                    ) : (
                                        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-[#E8EBF3] bg-[#FCFCFC] py-20">
                                            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#F4F5F8] text-[#A0A8C0]">
                                                <SquaresFour size={24} weight="regular" />
                                            </div>
                                            <p className="text-sm font-normal text-[#666F8D]">
                                                {searchQuery
                                                    ? t('website.integrationsSettings.noIntegrationsFound', { query: searchQuery })
                                                    : t('website.integrationsSettings.noIntegrationsInCategory', {
                                                          category: localizedScenarioLabels[selectedCategory],
                                                      })}
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}
                            </div>
                        </div>
                    </section>
                </div>
            )}

            <GoogleAdsAccountSelectionModal
                isOpen={Boolean(googleAdsAccountSelectionConnectionId)}
                connectionId={googleAdsAccountSelectionConnectionId}
                brandOptions={googleAdsBusinessGroupOptions}
                autoAssignBusinessGroupId={
                    shouldSkipBusinessGroupAssignment || isLoadingBusinessGroups
                        ? undefined
                        : onlyDefaultGoogleAdsBusinessGroupId
                }
                showBusinessGroupAssignment={!shouldSkipBusinessGroupAssignment}
                assignedBrandIdsByCustomerId={
                    googleAdsAssignedBrandIdsByCustomerId
                }
                assignedBrandAccessLevelsByCustomerId={
                    googleAdsAssignedBrandAccessLevelsByCustomerId
                }
                onClose={closeGoogleAdsAccountSelection}
                onSaved={handleGoogleAdsAccountSelectionSaved}
                onCancel={() => completeConnectionSuccess('Google Ads', { brandAssignmentSynchronized: true })}
            />

            <OnboardingResourceSelectionModal
                isOpen={googleSecondaryResourceSelection.kind === 'choosing'}
                provider={googleSecondaryResourceSelection.kind === 'choosing' ? googleSecondaryResourceSelection.tool : ''}
                title={googleSecondaryResourceSelection.kind === 'choosing' ? `选择 ${googleSecondaryResourceSelection.tool} 账号` : undefined}
                description="选择要保存到工作区的二级账号。保存后可继续分配业务组；不选择则仅保留父连接，不授予具体资源权限。"
                resources={googleSecondaryResourceSelection.kind === 'choosing' ? googleSecondaryResourceSelection.resources : []}
                isSaving={isSavingGoogleSecondaryResources}
                onCancel={dismissGoogleSecondaryResourceSelection}
                onSave={finishGoogleSecondaryResourceSelection}
            />

            <Modal
                isOpen={isAddConnectionUpgradeModalOpen}
                onClose={() => setIsAddConnectionUpgradeModalOpen(false)}
                title={t('website.integrationsSettings.upgradeAddConnections')}
                width="max-w-[520px]"
                bodyClassName="px-6 pb-6 pt-2"
            >
                <div className="space-y-5">
                    <p className="text-sm leading-6 text-text-sub">
                        {t('website.integrationsSettings.upgradeAddConnectionsDescription', {
                            tool: selectedTool || t('website.integrationsSettings.thisIntegration'),
                            plan: t('website.integrationsSettings.teamPlan'),
                        })}
                    </p>
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={() => setIsAddConnectionUpgradeModalOpen(false)}
                            className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#666F8D] transition-colors hover:bg-[#F8F9FC] hover:text-[#1A162F]"
                        >
                            {t('website.integrationsSettings.notNow')}
                        </button>
                        <button
                            onClick={handleOpenAddConnectionUpgradePricing}
                            className="rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-primary/90"
                        >
                            {t('website.integrationsSettings.upgradeToTeam')}
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={!!blockedToolName}
                onClose={() => setBlockedToolName(null)}
                title={t(isPluginAdmin ? 'website.integrationsSettings.pluginConnectRestrictedToolTitle' : 'website.integrationsSettings.connectRestrictedToolTitle', {
                    tool: blockedToolName,
                })}
                width="max-w-[520px]"
                bodyClassName="px-6 pb-6 pt-2"
            >
                <div className="space-y-5">
                    <p className="text-sm leading-6 text-text-sub">
                        {t(
                            isPluginAdmin ? 'website.integrationsSettings.pluginConnectRestrictedToolDescription' : 'website.integrationsSettings.connectRestrictedToolDescription',
                            { tool: blockedToolName },
                        )}
                    </p>
                    <div className="flex justify-end gap-3">
                        {isPluginAdmin ? (
                            <button
                                onClick={() => setBlockedToolName(null)}
                                className="rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-primary/90"
                            >
                                {t('website.integrationsSettings.pluginRestrictionAcknowledge')}
                            </button>
                        ) : (
                            <>
                                <button
                                    onClick={() => setBlockedToolName(null)}
                                    className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#666F8D] transition-colors hover:bg-[#F8F9FC] hover:text-[#1A162F]"
                                >
                                    {t('website.integrationsSettings.notNow')}
                                </button>
                                <button
                                    onClick={() => {
                                        setBlockedToolName(null);
                                        modal.openModal('pricing');
                                    }}
                                    className="rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-primary/90"
                                >
                                    {t('website.integrationsSettings.upgradeToTeam')}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={!!storeLimitPrompt}
                onClose={() => setStoreLimitPrompt(null)}
                title={storeLimitPrompt?.title || t('website.integrationsSettings.storeConnectionLimitReached')}
                width="max-w-[520px]"
                bodyClassName="px-6 pb-6 pt-2"
            >
                <div className="space-y-5">
                    <p className="text-sm leading-6 text-text-sub">{storeLimitPrompt?.description}</p>
                    <div className="flex justify-end gap-3">
                        {storeLimitPrompt?.primaryActionTarget !== 'none' ? (
                            <button
                                onClick={() => setStoreLimitPrompt(null)}
                                className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#666F8D] transition-colors hover:bg-[#F8F9FC] hover:text-[#1A162F]"
                            >
                                {storeLimitPrompt?.secondaryActionLabel || t('website.integrationsSettings.notNow')}
                            </button>
                        ) : null}
                        <button
                            onClick={handleStoreLimitPrimaryAction}
                            className="rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-primary/90"
                        >
                            {storeLimitPrompt?.primaryActionLabel || t('website.integrationsSettings.upgrade')}
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={subscriptionRestrictionPrompt !== null}
                onClose={() => setSubscriptionRestrictionPrompt(null)}
                title={subscriptionRestrictionPrompt?.title || t('website.integrationsSettings.currentTrialCantBeUsedHere')}
                width="max-w-[520px]"
                bodyClassName="px-6 pb-6 pt-2"
            >
                <div className="space-y-5">
                    {subscriptionRestrictionPrompt?.detail ? <p className="text-sm leading-6 text-[#1A162F]">{subscriptionRestrictionPrompt.detail}</p> : null}
                    <p className="text-sm leading-6 text-text-sub">
                        {subscriptionRestrictionPrompt?.description || t('website.integrationsSettings.currentTrialCantBeUsedHereDescription')}
                    </p>
                    <div className="flex justify-end gap-3">
                        {subscriptionRestrictionPrompt?.secondaryActionLabel ? (
                            <button
                                onClick={() => setSubscriptionRestrictionPrompt(null)}
                                className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#666F8D] transition-colors hover:bg-[#F8F9FC] hover:text-[#1A162F]"
                            >
                                {subscriptionRestrictionPrompt.secondaryActionLabel}
                            </button>
                        ) : null}
                        <button
                            onClick={handleSubscriptionRestrictionPrimaryAction}
                            className="rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-primary/90"
                        >
                            {subscriptionRestrictionPrompt?.primaryActionLabel || t('website.workspaceSwitcher.startSubscription')}
                        </button>
                    </div>
                </div>
            </Modal>

            <ContactSalesModal isOpen={isStoreLimitContactSalesOpen} onClose={() => setIsStoreLimitContactSalesOpen(false)} />

            <BusinessGroupAssignmentModal
                isOpen={connectionBrandAssignmentPrompt.kind === 'choosing'}
                onClose={keepDefaultBusinessGroupAssignment}
                account={connectionBrandAssignmentPrompt.kind === 'choosing' ? connectionBrandAssignmentPrompt.tool : ''}
                brands={connectionBrandOptions}
                selectedBrandIds={connectionBrandAssignmentPrompt.kind === 'choosing' ? connectionBrandAssignmentPrompt.selectedBrandIds : []}
                assignedBrandIds={[]}
                accessLevels={connectionBrandAssignmentPrompt.kind === 'choosing' ? connectionBrandAssignmentPrompt.accessLevels : {}}
                requiredBrandIds={defaultBusinessGroupId ? [defaultBusinessGroupId] : []}
                onToggle={(brandId) => setConnectionBrandAssignmentPrompt((current) => {
                    if (current.kind !== 'choosing') return current;
                    if (brandId === defaultBusinessGroupId) return current;
                    const isSelected = current.selectedBrandIds.includes(brandId);
                    const selectedBrandIds = isSelected
                        ? current.selectedBrandIds.filter((id) => id !== brandId)
                        : [...current.selectedBrandIds, brandId];
                    return {
                        ...current,
                        selectedBrandIds,
                        accessLevels: Object.fromEntries(selectedBrandIds.map((id) => [id, current.accessLevels[id] || 'WRITE'])),
                    };
                })}
                onAccessLevelChange={(brandId, level) => setConnectionBrandAssignmentPrompt((current) => current.kind === 'choosing'
                    ? { ...current, accessLevels: { ...current.accessLevels, [brandId]: level } }
                    : current)}
                onSave={() => {
                    if (connectionBrandAssignmentPrompt.kind === 'choosing') {
                        void finishPostAuthorizationBrandAssignment(
                            connectionBrandAssignmentPrompt.selectedBrandIds,
                            connectionBrandAssignmentPrompt.accessLevels,
                        );
                    }
                }}
                isSaveDisabled={connectionBrandAssignmentPrompt.kind !== 'choosing' || connectionBrandAssignmentPrompt.selectedBrandIds.length === 0}
                isSaving={isSavingPostAuthorizationBrandAssignment}
                secondaryActionLabel={t('website.integrationsSettings.keepDefaultBusinessGroupOnly')}
            />

            <IntegrationApiKeyModal
                isOpen={!!apiKeyTool}
                onClose={() => {
                    if (!apiKeyTool || !syncingTools.has(apiKeyTool)) {
                        resetConnectionForm();
                    }
                }}
                toolName={apiKeyTool || ''}
                apiKey={apiKeyValue}
                onApiKeyChange={setApiKeyValue}
                storeUrl={prestaShopStoreUrl}
                onStoreUrlChange={setPrestaShopStoreUrl}
                onConfirm={handleApiKeyConnection}
                isLoading={apiKeyTool ? syncingTools.has(apiKeyTool) : false}
            />

            <IntegrationCredentialsModal
                isOpen={!!credentialTool}
                onClose={resetConnectionForm}
                tool={credentialTool}
                values={credentialValues}
                onValuesChange={setCredentialValues}
                onConfirm={handleIntegrationCredentialConnection}
                isLoading={credentialTool ? syncingTools.has(credentialTool) : false}
            />

            <OnboardingShopifyInstall
                scene="admin"
                workspaceId={currentWorkspace?.id || ""}
                onProgress={setShopifyInstallState}
                renderStatus={renderShopifyStatus}
                onConnected={async () => {
                    await refreshWorkspaceIntegrationsAndAccess(false, { forceRefresh: true });
                    onConnectionSuccess?.();
                }}
                onRestart={() => setConnectingTool("Shopify")}
            />
            <ShopifyConnectionEntry
                scene="admin"
                workspaceId={currentWorkspace?.id}
                onProgress={setShopifyEntryState}
                renderStatus={renderShopifyStatus}
                isOpen={!!connectingTool}
                platform={connectingTool}
                onClose={resetConnectionForm}
            >
                <IntegrationUrlModal
                    isOpen={!!connectingTool && Boolean(URL_INPUT_CONFIG[connectingTool])}
                    onClose={resetConnectionForm}
                    toolName={connectingTool || ''}
                    urlConfig={connectingTool && URL_INPUT_CONFIG[connectingTool] ? URL_INPUT_CONFIG[connectingTool] : undefined}
                    storeUrl={storeUrl}
                    onStoreUrlChange={(value) => {
                        setStoreUrl(value);
                        setUrlConnectionError(null);
                    }}
                    onConfirm={handleUrlConnection}
                    isLoading={connectingTool ? syncingTools.has(connectingTool) : false}
                    errorText={urlConnectionError}
                    extraFields={
                        connectingTool === 'BigCommerce'
                            ? [
                                  {
                                      key: 'store_hash',
                                      label: t('website.integrationsSettings.storeHash'),
                                      placeholder: 'abc123xyz',
                                      value: bigCommerceStoreHash,
                                      onChange: setBigCommerceStoreHash,
                                  },
                                  {
                                      key: 'account_uuid',
                                      label: t('website.integrationsSettings.accountUuid'),
                                      placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
                                      value: bigCommerceAccountUuid,
                                      onChange: setBigCommerceAccountUuid,
                                  },
                              ]
                            : []
                    }
                />
            </ShopifyConnectionEntry>
            {isMcpUiEnabled() ? (
                <McpConnectorModal
                    isOpen={mcpConnectSurface === 'custom' && Boolean(mcpModalMode)}
                    mode={mcpModalMode}
                    showManualSetup={mcpFlow.state.kind === 'manual-setup'}
                    onClose={closeMcpConnectorModal}
                    onStartOAuth={handleMcpOAuthStart}
                    onConnectDirect={handleMcpDirectConnect}
                    onSubmitManual={handleMcpManualSubmit}
                    isLoading={isSavingMcpConnector || isMcpOAuthLoading}
                    errorText={mcpConnectorError}
                />
            ) : null}
            <Modal
                isOpen={!!feishuCustomAppTool}
                onClose={() => {
                    if (!feishuCustomAppTool || !syncingTools.has(feishuCustomAppTool)) {
                        resetConnectionForm();
                    }
                }}
                title={t('website.integrationsSettings.connectFeishuTitle')}
                width={getIntegrationConnectGuideModalWidth(feishuCustomAppTool || 'Feishu', 'max-w-[560px]')}
                bodyClassName="px-6 pb-6 pt-1"
                titleClassName="text-[#1A162F]"
            >
                <div className="space-y-5">
                    <IntegrationConnectGuide toolName={feishuCustomAppTool || ''} />

                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-1.5">
                            <label className="block text-sm font-medium text-text-main">{t('website.integrationsSettings.feishuAppId')}</label>
                            <input
                                value={feishuCustomAppId}
                                onChange={(event) => setFeishuCustomAppId(event.target.value)}
                                placeholder="cli_a..."
                                className="w-full rounded-[10px] border border-[#E8EBF3] px-3 py-2 text-sm text-text-main outline-none transition-colors placeholder:text-[#A0A8C0] focus:border-[#1A162F]"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className="block text-sm font-medium text-text-main">{t('website.integrationsSettings.feishuAppSecret')}</label>
                            <input
                                type="password"
                                value={feishuCustomAppSecret}
                                onChange={(event) => setFeishuCustomAppSecret(event.target.value)}
                                placeholder={t('website.integrationsSettings.feishuAppSecretPlaceholder')}
                                className="w-full rounded-[10px] border border-[#E8EBF3] px-3 py-2 text-sm text-text-main outline-none transition-colors placeholder:text-[#A0A8C0] focus:border-[#1A162F]"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end gap-3">
                        <button
                            onClick={resetConnectionForm}
                            disabled={feishuCustomAppTool ? syncingTools.has(feishuCustomAppTool) : false}
                            className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#666F8D] transition-colors hover:bg-[#F8F9FC] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            onClick={handleFeishuCustomAppConnection}
                            disabled={
                                !feishuCustomAppId.trim() ||
                                !feishuCustomAppSecret.trim() ||
                                !getFeishuOAuthRedirectUri() ||
                                (feishuCustomAppTool ? syncingTools.has(feishuCustomAppTool) : false)
                            }
                            className="inline-flex items-center justify-center gap-2 rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {feishuCustomAppTool && syncingTools.has(feishuCustomAppTool) ? <CircleNotch size={15} weight="regular" className="animate-spin" /> : null}
                            {t('website.integrationsSettings.connectFeishuAction')}
                        </button>
                    </div>
                </div>
            </Modal>
            <Modal
                isOpen={!!dingtalkCustomAppTool}
                onClose={() => {
                    if ((!dingtalkCustomAppTool || !syncingTools.has(dingtalkCustomAppTool)) && !isSavingMcpConnector && !isMcpOAuthLoading) {
                        cancelMcpOAuthTracking('user_closed');
                        mcpFlow.close();
                        resetConnectionForm();
                    }
                }}
                title={t('website.integrationsSettings.connectDingTalkTitle')}
                width={getIntegrationConnectGuideModalWidth(dingtalkCustomAppTool || 'DingTalk', 'max-w-[560px]')}
                bodyClassName="px-6 pb-6 pt-1"
                titleClassName="text-[#1A162F]"
            >
                <div className="space-y-5">
                    <div role="tablist" aria-label={t('website.integrationsSettings.dingtalkConnectionMethod')} className="grid grid-cols-2 gap-1 rounded-xl bg-[#F3F5F9] p-1">
                        {(['internal_app', 'mcp_server'] as const).map((method) => {
                            const selected = dingtalkConnectionMethod === method;
                            return (
                                <button
                                    key={method}
                                    type="button"
                                    role="tab"
                                    aria-selected={selected}
                                    disabled={isSavingMcpConnector || isMcpOAuthLoading}
                                    onClick={() => {
                                        setDingTalkConnectionMethod(method);
                                        setMcpConnectorError(null);
                                        if (method === 'mcp_server') {
                                            setMcpConnectSurface('dingtalk');
                                            setMcpModalMode('url');
                                        } else {
                                            setMcpConnectSurface(null);
                                            setMcpModalMode(null);
                                            cancelMcpOAuthTracking('connection_method_changed');
                                            mcpFlow.close();
                                        }
                                    }}
                                    className={`rounded-lg px-3 py-2.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                                        selected ? 'bg-white text-[#1A162F] shadow-sm' : 'text-[#68708A] hover:text-[#1A162F]'
                                    }`}
                                >
                                    {t(method === 'internal_app' ? 'website.integrationsSettings.dingtalkInternalAppMethod' : 'website.integrationsSettings.dingtalkMcpMethod')}
                                </button>
                            );
                        })}
                    </div>

                    {dingtalkConnectionMethod === 'internal_app' ? (
                        <div className="space-y-5">
                            <IntegrationConnectGuide toolName={dingtalkCustomAppTool || ''} />

                            <div className="rounded-[10px] border border-[#E8EBF3] bg-[#F8F9FC] px-3 py-2 text-xs leading-5 text-[#666F8D]">
                                {t('website.integrationsSettings.dingtalkAdminNotice')}
                            </div>

                            <div className="space-y-1.5">
                                <label className="block text-sm font-medium text-text-main">{t('website.integrationsSettings.dingtalkCallbackUrl')}</label>
                                <input
                                    readOnly
                                    value={getDingTalkOAuthRedirectUri()}
                                    aria-label={t('website.integrationsSettings.dingtalkCallbackUrl')}
                                    className="w-full rounded-[10px] border border-[#E8EBF3] bg-[#F8F9FC] px-3 py-2 font-mono text-xs text-[#666F8D] outline-none"
                                />
                            </div>

                            <div className="grid gap-4 sm:grid-cols-2">
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-medium text-text-main">{t('website.integrationsSettings.dingtalkAppId')}</label>
                                    <input
                                        value={dingtalkCustomAppId}
                                        onChange={(event) => setDingTalkCustomAppId(event.target.value)}
                                        placeholder="ding..."
                                        className="w-full rounded-[10px] border border-[#E8EBF3] px-3 py-2 text-sm text-text-main outline-none transition-colors placeholder:text-[#A0A8C0] focus:border-[#1A162F]"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-medium text-text-main">{t('website.integrationsSettings.dingtalkAppSecret')}</label>
                                    <input
                                        type="password"
                                        value={dingtalkCustomAppSecret}
                                        onChange={(event) => setDingTalkCustomAppSecret(event.target.value)}
                                        placeholder={t('website.integrationsSettings.dingtalkAppSecretPlaceholder')}
                                        className="w-full rounded-[10px] border border-[#E8EBF3] px-3 py-2 text-sm text-text-main outline-none transition-colors placeholder:text-[#A0A8C0] focus:border-[#1A162F]"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={resetConnectionForm}
                                    disabled={dingtalkCustomAppTool ? syncingTools.has(dingtalkCustomAppTool) : false}
                                    className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#666F8D] transition-colors hover:bg-[#F8F9FC] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {t('common.cancel')}
                                </button>
                                <button
                                    onClick={handleDingTalkCustomAppConnection}
                                    disabled={
                                        !dingtalkCustomAppId.trim() ||
                                        !dingtalkCustomAppSecret.trim() ||
                                        !getDingTalkOAuthRedirectUri() ||
                                        (dingtalkCustomAppTool ? syncingTools.has(dingtalkCustomAppTool) : false)
                                    }
                                    className="inline-flex items-center justify-center gap-2 rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {dingtalkCustomAppTool && syncingTools.has(dingtalkCustomAppTool) ? <CircleNotch size={15} weight="regular" className="animate-spin" /> : null}
                                    {t('website.integrationsSettings.connectDingTalkAction')}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <McpConnectorModal
                                embedded
                                platform="DINGTALK"
                                isOpen={mcpConnectSurface === 'dingtalk' && Boolean(mcpModalMode)}
                                mode={mcpModalMode}
                                showManualSetup={mcpFlow.state.kind === 'manual-setup'}
                                onClose={() => {
                                    cancelMcpOAuthTracking('user_closed');
                                    mcpFlow.close();
                                    resetConnectionForm();
                                }}
                                onStartOAuth={handleMcpOAuthStart}
                                onConnectDirect={handleMcpDirectConnect}
                                onConnectBatch={handleDingTalkMcpBatchConnect}
                                onSubmitManual={handleMcpManualSubmit}
                                isLoading={isSavingMcpConnector || isMcpOAuthLoading}
                                errorText={mcpConnectorError}
                            />
                        </div>
                    )}
                </div>
            </Modal>
            <Modal
                isOpen={!!viewingDetailsConnection}
                onClose={() => setViewingDetailsConnection(null)}
                title={t('website.integrationsSettings.connectionDetailsTitle', {
                    tool: viewingDetailsConnection?.integrationType || '',
                })}
                variant="reference"
                width="max-w-md"
                bodyClassName="px-6 pb-6 pt-2"
            >
                <div className="divide-y divide-gray-100">
                    {viewingDetailsConnection?.subAccounts?.map((sub) => (
                        <div key={`${sub.accountName}-${sub.customerId}`} className="py-4 first:pt-2 last:pb-2 flex items-start gap-3">
                            <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#3B82F6] shadow-[0_0_8px_rgba(59,130,246,0.5)]" />
                            <div className="space-y-1.5">
                                <div className="text-sm font-normal text-text-main flex items-baseline gap-1">
                                    <span className="text-gray-400 font-medium text-xs">{t('website.integrationsSettings.idLabel')}:</span>
                                    <span className="text-[#1a162f]">{sub.customerId}</span>
                                </div>
                                <div className="text-sm font-normal text-text-main flex items-baseline gap-1">
                                    <span className="text-gray-400 font-medium text-xs">{t('website.integrationsSettings.nameLabel')}:</span>
                                    <span className="text-[#1a162f]">{sub.accountName}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </Modal>
        </div>
    );
};

export default IntegrationsSettings;
